import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowUpRight, Check, LockKeyhole, Play, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import reachSeanOriginal from "@/assets/sean-stephens-instagram-cropped.jpg";
import reachSeanVideoOriginal from "@/assets/kaylee-bell-stats.avif";
import reachKayleeOriginal from "@/assets/kaylee-bell-stats.png";
import logo from "@/assets/damian-keyes-logo.png";
import brianPhoto from "@/assets/brian-michael-video-stats.jpg";
import kayleePhoto from "@/assets/kaylee-bell-wrapped.png";
import spotifyDashboard from "@/assets/spotify-screenshot.png";
import { videoTestimonials } from "@/data/videoTestimonialsData";
import { AudioWaveform } from "@/components/AudioWaveform";
import { Footer } from "@/components/Footer";
import {
  BLACK_FRIDAY_NOINDEX,
  CAMPAIGN_NAME,
  CONTACT_EMAIL,
  DURATIONS,
  DurationId,
  LOWEST_PRICE,
  formatUsd,
  getCheckoutUrl,
  getDuration,
  getSavings,
  getSavingsPercent,
  readStoredSelection,
  storeSelection,
} from "@/features/black-friday/config";
import { ARTIST_MESSAGES, ArtistMessage } from "@/features/black-friday/messages";

const campaignStyles = `
/* A compact campaign masthead, scoped to this page. */
.bf-page .bf-campaign-banner{position:relative;isolation:isolate;overflow:hidden;background:#151e2b;color:#fff;border-bottom:1px solid #819db342;box-shadow:inset 0 1px 0 #d5e8ff21}
.bf-campaign-banner .bf-banner-surface{position:absolute;inset:0;z-index:-1;pointer-events:none;overflow:hidden;background:radial-gradient(ellipse at 17% 100%,#138bff52,transparent 49%),linear-gradient(108deg,#082b52 0%,#102743 27%,#162331 47%,#1a2029 73%,#11151c 100%)}
.bf-banner-surface:before{content:'';position:absolute;inset:0;background:linear-gradient(114deg,transparent 15%,#d2e6ff0c 23%,#bcd9ff23 23.2%,#d4e9ff08 23.45%,transparent 34%,#7ab5ed09 57%,transparent 57.15%)}
.bf-banner-surface:after{content:'';position:absolute;inset:auto 0 0;height:1px;background:linear-gradient(90deg,transparent,#77bbffa3 23%,#d2e9ff33 40%,transparent 70%)}
.bf-banner-surface span{position:absolute;width:36%;height:180%;left:2%;top:-58%;border-radius:50%;border-bottom:1px solid #6db9ff21;transform:rotate(-12deg)}
.bf-banner-surface span:nth-child(2){width:42%;left:3%;top:-63%;border-color:#b9dfff14}
.bf-banner-surface span:nth-child(3){width:48%;left:4%;top:-69%;border-color:#b9dfff0d}
.bf-page .bf-campaign-banner .bf-banner-inner{min-height:64px;display:flex;align-items:center;justify-content:flex-start;gap:30px;padding:10px 0}
.bf-banner-lockup{position:relative;flex:none;font-size:26px;line-height:1;letter-spacing:-.045em;font-weight:650;color:#f6faff;text-shadow:0 2px 12px #0071e326;padding-right:32px}
.bf-banner-cut{position:absolute;right:0;top:-5px;width:1px;height:36px;transform:rotate(20deg);background:linear-gradient(180deg,transparent,#a9c9e662,transparent)}
.bf-campaign-banner .bf-banner-description{font-size:14px;line-height:1.35;letter-spacing:-.01em;color:#f3f6fb;font-weight:500}
.bf-banner-description>span{display:block;font-size:12px;letter-spacing:0;font-weight:400;color:#aebfd1;margin-top:3px}
.bf-banner-link{margin-left:auto;display:inline-flex;align-items:center;gap:14px;flex:none;min-height:44px;font-size:14px;font-weight:550;letter-spacing:-.01em;color:#edf5ff;white-space:nowrap}
.bf-banner-arrow{width:32px;height:32px;display:grid;place-items:center;border-radius:50%;border:1px solid #adc6df45;background:linear-gradient(140deg,#cbe2fb19,#d5e8ff04);box-shadow:inset 0 1px 0 #fff2;transition:background .4s,border-color .4s,transform .4s cubic-bezier(.2,.7,.3,1)}
.bf-banner-arrow svg{width:16px;height:16px;stroke-width:1.7}
@media(hover:hover){.bf-banner-link:hover .bf-banner-arrow{background:#0071e3;border-color:#5baeff;transform:translate(2px,-1px)}.bf-banner-link:hover{color:#fff}}
.bf-page .bf-banner-link:focus-visible{outline:2px solid #e2f0ff;outline-offset:4px;border-radius:4px}
@media(max-width:760px){.bf-page .bf-campaign-banner .bf-banner-inner{min-height:52px;padding:4px 0;gap:16px}.bf-banner-lockup{font-size:22px;padding-right:0}.bf-banner-cut,.bf-banner-description{display:none}.bf-banner-link{font-size:13px;gap:10px}.bf-banner-arrow{width:28px;height:28px}.bf-banner-arrow svg{width:15px;height:15px}.bf-banner-surface:before{background:linear-gradient(114deg,transparent 26%,#d2e6ff0c 45%,#bcd9ff23 45.3%,#d4e9ff08 45.6%,transparent 69%)}.bf-banner-surface span{width:75%}.bf-banner-surface span:nth-child(2){width:85%}.bf-banner-surface span:nth-child(3){width:95%}}
@media(prefers-reduced-motion:reduce){.bf-banner-arrow{transition:none}.bf-banner-link:hover .bf-banner-arrow{transform:none}}

.bf-price-bonus-link{display:inline-flex;align-items:center;gap:7px;color:#0065cc;font-size:13px;font-weight:500;margin-top:14px;text-decoration:underline;text-underline-offset:3px}.bf-price-bonus-link svg{width:13px;height:13px;transform:rotate(90deg)}
.bf-purchase-bonuses{padding-top:54px;scroll-margin-top:25px}.bf-purchase-bonuses>.bf-heading{font-size:clamp(29px,3.2vw,40px);max-width:850px}.bf-bonuses-intro{color:#aab6c8;font-size:16px;margin-top:15px!important}.bf-bonus-grid{display:grid;grid-template-columns:1fr 1fr;gap:22px;margin-top:28px}.bf-bonus-grid article{padding:32px 34px;border-radius:12px;display:flex;flex-direction:column;min-width:0}.bf-bonus-academy{background:#f5f7fb;color:#112039}.bf-bonus-damian{background:#0071e3;color:#fff}.bf-bonus-eligibility{font-size:13px;font-weight:500;color:#0065cc;margin-bottom:17px!important}.bf-bonus-damian .bf-bonus-eligibility{color:#d7eaff}.bf-bonus-grid h4{font-size:30px;line-height:1.15;letter-spacing:-.035em;font-weight:550;margin:0}.bf-bonus-value{display:flex;align-items:baseline;gap:18px;margin:22px 0 18px;color:#62718a;font-size:14px}.bf-bonus-value s{font-size:26px;letter-spacing:-.03em;text-decoration-thickness:2px}.bf-bonus-value strong{color:#0067cf;font-weight:600;font-size:39px;letter-spacing:-.045em;line-height:1.1}.bf-bonus-grid article>p:not(.bf-bonus-eligibility){font-size:16px;line-height:1.65;color:#53677f;max-width:430px}.bf-bonus-damian>p:not(.bf-bonus-eligibility){color:#e4f0ff!important}.bf-bonus-grid .bf-bonus-call-intro{font-size:21px!important;font-weight:500;letter-spacing:-.025em;margin:25px 0 15px!important;color:white!important}.bf-bonus-confirmation{display:flex;align-items:flex-start;gap:9px;margin-top:auto;padding-top:25px;font-size:13px;font-weight:500;line-height:1.6;color:#0065cc;min-height:49px}.bf-bonus-confirmation svg{width:16px;height:16px;flex-shrink:0;margin-top:2px}.bf-bonus-damian .bf-bonus-confirmation{color:#fff}
@media(max-width:760px){.bf-purchase-bonuses{padding-top:35px;padding-bottom:16px}.bf-purchase-bonuses>.bf-heading{font-size:31px;max-width:400px}.bf-bonuses-intro{font-size:14px;margin-top:13px!important}.bf-bonus-grid{display:block;margin-top:23px}.bf-bonus-grid article{padding:26px 24px}.bf-bonus-grid article+article{margin-top:16px}.bf-bonus-grid h4{font-size:28px}.bf-bonus-grid article>p:not(.bf-bonus-eligibility){font-size:15px}.bf-bonus-eligibility{margin-bottom:14px!important}.bf-bonus-value{margin-top:20px}.bf-bonus-confirmation{padding-top:23px}}

.bf-audio-badge{display:flex;flex-direction:column;align-items:center;width:max-content;margin-bottom:20px}.bf-occasion-badge{display:inline-flex;align-items:center;background:#ffffff0d;border:1px solid #ffffff1a;border-radius:6px;padding:6px 10px;font-family:var(--font-mono),monospace;font-size:12px;color:#ffffff99;letter-spacing:.15em;line-height:1.4}@media(max-width:760px){.bf-occasion-badge{font-size:10px;padding:4px 8px}.bf-audio-badge{margin-bottom:18px}}.bf-kickstart-journey{margin:25px 0 0;padding:0;max-width:650px}.bf-kickstart-journey figcaption{display:flex;align-items:center;gap:12px;font-size:14px;font-weight:600;color:#0071e3;margin-bottom:7px}.bf-kickstart-journey figcaption:before{content:'';width:23px;height:2px;background:#0071e3;border-radius:2px}.bf-kickstart-journey figcaption span{color:#7f90a7;font-size:12px;font-weight:400}.bf-kickstart-journey svg{display:block;width:100%;height:auto;overflow:visible;font-family:Geist,Inter,system-ui,sans-serif}.bf-kickstart-journey .bf-journey-stage{fill:#516278;font-size:22px;font-weight:600}.bf-kickstart-journey .bf-journey-label{fill:#5e6e82;font-size:24px;font-weight:400;letter-spacing:-.4px}.bf-kickstart-journey .bf-journey-momentum{fill:#0071e3;font-weight:600}.bf-kickstart-journey .bf-journey-future{fill:#8495ac}.bf-recognition-offer .bf-kickstart-journey+.bf-recognition-promise{margin-top:15px!important;padding-top:21px}@media(max-width:760px){.bf-kickstart-journey{margin-top:24px}.bf-kickstart-journey figcaption{font-size:14px;margin-bottom:11px}.bf-kickstart-journey figcaption span{font-size:12px}.bf-recognition-offer .bf-kickstart-journey+.bf-recognition-promise{margin-top:17px!important;padding-top:19px}}

@media(prefers-reduced-motion:reduce){.bf-audio-badge .wave-bar{animation:none!important}}

.bf-page{--bf-blue:#0071e3;--bf-ink:#101114;--bf-muted:#62656e;--bf-line:#dfe1e6;background:#fff;color:var(--bf-ink);font-family:Geist,Inter,system-ui,sans-serif;font-size:16px;line-height:1.55;overflow:hidden}.bf-page *{box-sizing:border-box}.bf-page h1,.bf-page h2,.bf-page h3,.bf-page p{margin:0}.bf-page a,.bf-page button{-webkit-tap-highlight-color:transparent}.bf-page a:focus-visible,.bf-page button:focus-visible{outline:3px solid var(--bf-blue);outline-offset:5px}.bf-wrap{width:min(1120px,calc(100% - 112px));margin:0 auto;position:relative;z-index:3}.bf-main{position:relative;isolation:isolate}.bf-heading{font-size:clamp(32px,3.45vw,46px);font-weight:600;line-height:1.09;letter-spacing:-.045em}.bf-center-heading{text-align:center;max-width:780px;margin:0 auto}.bf-center-heading>p{max-width:640px;margin:19px auto 0;color:var(--bf-muted);font-size:17px;line-height:1.65}.bf-blue{color:var(--bf-blue)}.bf-section{position:relative}.bf-dark{background:var(--bf-ink);color:#fff}.bf-light{background:#fff;color:var(--bf-ink)}.bf-soft{background:#f4f6f9;color:var(--bf-ink)}
.bf-occasion{background:var(--bf-blue);color:#fff;font-size:13px;font-weight:500}.bf-occasion .bf-wrap{display:flex;justify-content:space-between;padding:9px 0}.bf-header{background:var(--bf-ink);color:white}.bf-nav{height:81px;display:flex;align-items:center;justify-content:space-between;gap:24px;border-bottom:1px solid #ffffff24}.bf-logo{width:166px;height:auto;filter:invert(1)}.bf-nav-links{display:flex;align-items:center;gap:29px;font-size:13px;color:#c2c5cc}.bf-nav-links a:hover{color:#fff}.bf-nav-buy{padding:9px 13px;border:1px solid #ffffff40;border-radius:6px;color:white}.bf-hero{padding:42px 0 0}.bf-hero-grid{display:grid;grid-template-columns:.98fr 1.02fr;gap:58px;align-items:center}.bf-hero-occasion{display:flex;align-items:center;gap:11px;font-size:16px;font-weight:500;color:#b5c7e0;margin-bottom:18px!important}.bf-hero-occasion:before{content:'';height:2px;width:29px;background:var(--bf-blue)}.bf-hero-title{font-size:clamp(40px,4.35vw,59px);line-height:1.04;letter-spacing:-.052em;font-weight:650}.bf-hero-title>span{display:block}.bf-title-result{color:var(--bf-blue)}.bf-hero-copy{font-size:17px;line-height:1.65;color:#c3c6cf;max-width:495px;margin-top:21px!important}.bf-hero-action{margin-top:25px}.bf-cta{display:inline-flex;align-items:center;justify-content:center;gap:24px;background:var(--bf-blue);color:#fff;padding:16px 21px;border-radius:7px;font-size:16px;font-weight:600;line-height:1.4;transition:background .2s,transform .2s}.bf-cta:hover{background:#0062c4;transform:translateY(-1px)}.bf-cta svg{width:18px;height:18px}.bf-hero-price{font-size:13px;color:#bcc0ca;margin-top:13px!important}.bf-hero-price strong{color:#fff;font-weight:600}.bf-hero-start{font-size:12px;color:#9298a5;margin-top:4px!important}.bf-intro-media{position:relative;align-self:center;margin-top:20px}.bf-video-frame{position:relative;aspect-ratio:16/9;background:#07090d;border-radius:11px;overflow:hidden}.bf-video-frame iframe{position:absolute;inset:0;width:100%;height:100%}.bf-intro-media .bf-video-frame{border:1px solid #ffffff38;box-shadow:0 0 0 6px #ffffff04,0 22px 70px #0007;backdrop-filter:blur(14px)}.bf-video-placeholder{display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;padding:28px;background:radial-gradient(ellipse at 75% 100%,#0071e330,transparent 62%),linear-gradient(125deg,#1c2230ee,#11151ee8);isolation:isolate}.bf-placeholder-logo{width:160px;height:auto;filter:invert(1);opacity:.84;margin-bottom:24px}.bf-video-placeholder h2{font-size:25px;letter-spacing:-.035em;font-weight:500;line-height:1.2}.bf-video-placeholder p{font-size:13px;color:#919caf;margin-top:10px}.bf-video-placeholder:after{content:'';position:absolute;width:75%;height:180%;right:-20%;top:-45%;border:1px solid #ffffff0b;border-radius:50%;transform:rotate(40deg);z-index:-1}.bf-intro-caption{display:flex;align-items:center;justify-content:space-between;gap:20px;font-size:12px;color:#a7adbb;margin:17px 2px 0}.bf-intro-caption span:last-child{color:#6f7b8f}.bf-proof-strip{display:grid;grid-template-columns:.67fr 1fr 1fr;gap:26px;align-items:center;border-top:1px solid #ffffff26;margin-top:42px;padding:24px 0}.bf-proof-strip>p{font-size:14px;color:#a9afba;max-width:150px}.bf-proof-tile{display:flex;align-items:center;gap:15px;text-align:left;width:100%;color:#fff;padding:0;min-width:0}.bf-proof-tile+.bf-proof-tile{border-left:1px solid #ffffff26;padding-left:26px}.bf-proof-face{position:relative;width:67px;height:70px;flex-shrink:0;overflow:hidden;border-radius:7px;background-repeat:no-repeat}.bf-face-brian{background-image:url('${brianPhoto}');background-size:760% auto;background-position:50% 8.2%}.bf-face-kaylee{background-image:url('${kayleePhoto}');background-size:235% auto;background-position:50% 10%}.bf-proof-name{display:block;font-size:12px;color:#aeb5c3;margin-bottom:3px}.bf-proof-result{display:block;font-size:18px;font-weight:500;line-height:1.3;letter-spacing:-.025em}.bf-proof-watch{display:flex;align-items:center;gap:7px;font-size:11px;margin-top:6px;color:#849bbd}.bf-proof-watch svg{width:10px;height:10px}
.bf-energy{position:absolute;inset:0;z-index:2;pointer-events:none;overflow:visible}.bf-energy-svg{display:block;width:100%;overflow:visible}.bf-ribbon-turn{height:76px;position:relative;background:#fff}.bf-recognition{padding:65px 0 4px}.bf-recognition .bf-center-heading{max-width:770px}.bf-recognition-copy{max-width:880px;margin:31px auto 0;display:grid;grid-template-columns:1fr 1fr;gap:38px;padding-top:29px;border-top:1px solid var(--bf-line)}.bf-recognition-copy p{font-size:16px;line-height:1.7;color:#62656e}.bf-recognition-copy p:first-child{font-size:20px;line-height:1.55;letter-spacing:-.02em;color:#292d35}.bf-recognition-copy strong{font-weight:500;color:#151920}.bf-journey{padding:67px 0 58px}.bf-journey .bf-center-heading>p{color:#a9b0be;max-width:670px}.bf-journey-steps{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:28px;margin-top:40px;position:relative}.bf-journey-step{position:relative;min-width:0;padding-top:23px;border-top:1px solid #ffffff2b}.bf-step-number{display:block;font-size:41px;font-weight:500;letter-spacing:-.055em;color:var(--bf-blue);line-height:1;margin-bottom:20px}.bf-journey-step h3{font-size:20px;line-height:1.25;letter-spacing:-.03em;font-weight:500;max-width:230px}.bf-journey-step p{font-size:14px;color:#a5adbc;line-height:1.7;margin-top:14px}.bf-journey-foot{display:flex;align-items:center;justify-content:space-between;gap:24px;margin-top:32px;padding-top:24px;border-top:1px solid #ffffff20}.bf-journey-foot p{font-size:12px;color:#818c9e;max-width:570px;line-height:1.65}.bf-text-link{display:inline-flex;align-items:center;gap:9px;font-size:14px;font-weight:500;padding-bottom:4px;border-bottom:1px solid #969da9}.bf-text-link svg{width:14px;height:14px}.bf-journey-foot .bf-text-link{flex-shrink:0;color:#d2dae7;border-color:#5b6e8c}
.bf-comparison{padding:65px 0 0}.bf-comparison .bf-center-heading{max-width:790px}.bf-compare-table{width:100%;border-collapse:separate;border-spacing:0;margin-top:33px;table-layout:fixed;border:1px solid #dde1e8;border-radius:10px;overflow:hidden;font-size:14px;text-align:left;line-height:1.6}.bf-compare-table th{font-weight:500;font-size:17px;letter-spacing:-.015em;padding:20px 22px;border-bottom:1px solid #dce0e7}.bf-compare-table th:first-child{width:19%;font-size:13px;color:#69717e}.bf-compare-table th:nth-child(2){width:38%}.bf-compare-table th:last-child{background:var(--bf-blue);color:white}.bf-compare-table td{padding:20px 22px;vertical-align:top;border-bottom:1px solid #e5e8ee;color:#6a727e}.bf-compare-table td:first-child{font-size:13px;font-weight:500;color:#252c36}.bf-compare-table tr:last-child td{border-bottom:0}.bf-compare-table td:last-child{background:#f0f6fd;color:#28394e}.bf-compare-cell{display:flex;gap:12px;align-items:flex-start}.bf-compare-cell svg{flex-shrink:0;width:16px;height:16px;color:var(--bf-blue);margin-top:4px}.bf-fit{margin-top:52px;display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid var(--bf-line);border-radius:10px;overflow:hidden}.bf-fit-column{padding:28px 32px;background:#f7faff}.bf-fit-column+.bf-fit-column{border-left:1px solid var(--bf-line);background:white}.bf-fit h3{font-size:22px;font-weight:500;letter-spacing:-.035em}.bf-fit ul{list-style:none;margin:20px 0 0;padding:0}.bf-fit li{display:flex;gap:11px;align-items:flex-start;margin-top:15px;font-size:14px;line-height:1.65;color:#4e5969}.bf-fit li svg{width:17px;height:17px;flex-shrink:0;margin-top:4px;color:var(--bf-blue)}.bf-fit-column+.bf-fit-column li svg{color:#888f9a}.bf-fit-note{font-size:12px;color:#7b8290;margin-top:16px!important}
.bf-stories{padding:61px 0 57px}.bf-stories .bf-center-heading>p{color:#9ca7b8;max-width:590px}.bf-video-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:19px;margin-top:35px}.bf-story-button{display:block;text-align:left;width:100%;color:#fff}.bf-story-image{display:block;position:relative;aspect-ratio:16/9;background:#1c222c;overflow:hidden;border:1px solid #ffffff21;border-radius:8px}.bf-story-image>img{display:block;width:100%;height:100%;object-fit:cover;transition:transform .25s}.bf-story-button:hover .bf-story-image>img{transform:scale(1.035)}.bf-play-circle{position:absolute;bottom:10px;left:10px;display:flex;align-items:center;justify-content:center;background:var(--bf-blue);border:1px solid #ffffff50;border-radius:50%;width:34px;height:34px;box-shadow:0 3px 15px #0005}.bf-play-circle svg{width:12px;height:12px;margin-left:2px}.bf-story-name{display:block;font-size:18px;font-weight:500;letter-spacing:-.025em;margin-top:15px}.bf-story-result{display:block;font-size:13px;line-height:1.55;color:#a1adbf;margin-top:5px}.bf-stories-more{display:flex;justify-content:center;margin-top:29px}.bf-stories-more .bf-text-link{color:#c4d3e8;border-color:#597398}
.bf-messages{padding:59px 0 5px;background:#fff;color:var(--bf-ink)}.bf-messages-heading{display:flex;align-items:flex-end;justify-content:space-between;gap:35px;margin-bottom:29px}.bf-messages-heading .bf-heading{max-width:520px;font-size:37px}.bf-messages-heading p{color:var(--bf-muted);font-size:14px;max-width:330px;line-height:1.65}.bf-message-grid{display:grid;grid-template-columns:1.05fr 1fr 1fr;gap:19px;list-style:none;padding:0;margin:0;align-items:start}.bf-message-grid li{min-width:0}.bf-message-button{display:block;width:100%;border-radius:8px;background:white;overflow:hidden;border:1px solid #e1e5eb;box-shadow:0 5px 24px #17243a08;transition:transform .2s}.bf-message-button:hover{transform:translateY(-3px)}.bf-message-button img{display:block;width:100%;height:auto}.bf-more-messages{display:inline-flex;align-items:center;gap:12px;border-bottom:1px solid #969eab;padding:0 0 4px;margin-top:24px;font-size:13px;font-weight:500}.bf-more-messages svg{width:14px;height:14px}.bf-messages-note{font-size:11px;color:#8a909b;margin-top:10px!important}
.bf-purchase{padding:65px 0;background:var(--bf-ink);color:#fff}.bf-purchase-grid{display:grid;grid-template-columns:.92fr 1fr;gap:75px;align-items:start}.bf-offer-occasion{display:inline-flex;align-items:center;gap:10px;color:#9dbdea;font-size:15px;margin-bottom:19px!important}.bf-offer-occasion:before{content:'';height:2px;width:28px;background:var(--bf-blue)}.bf-purchase-copy .bf-heading{max-width:435px;font-size:clamp(33px,3.5vw,47px)}.bf-purchase-copy>p{color:#a7b0bf;font-size:16px;line-height:1.7;margin-top:22px}.bf-included{list-style:none;margin:29px 0 0;padding:0}.bf-included li{display:flex;gap:12px;font-size:14px;line-height:1.55;margin-top:13px;color:#d2dae6}.bf-included svg{width:16px;height:16px;flex-shrink:0;color:var(--bf-blue);margin-top:3px}.bf-start-note{border-top:1px solid #ffffff25;margin-top:30px;padding-top:25px}.bf-start-note h3{font-size:18px;letter-spacing:-.025em;font-weight:500}.bf-start-note p{font-size:14px;color:#9faabc;line-height:1.65;margin-top:10px}.bf-offer-help{font-size:13px!important;color:#8794a9!important;margin-top:24px!important}.bf-offer-help a{color:white;text-decoration:underline;text-underline-offset:4px}.bf-purchase-card{scroll-margin-top:18px;background:white;color:var(--bf-ink);padding:29px;border-radius:12px;box-shadow:0 22px 60px #0004;position:relative}.bf-purchase-card h3{font-size:23px;font-weight:600;letter-spacing:-.035em}.bf-purchase-card>p{font-size:14px;color:var(--bf-muted);margin-top:7px}.bf-duration-options{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-top:25px}.bf-duration{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:91px;border:1px solid #d8dbe0;border-radius:7px;padding:16px 5px 10px;background:white;transition:background .2s,border-color .2s}.bf-duration[aria-checked=true]{background:#f0f7ff;border:2px solid var(--bf-blue);padding:15px 4px 9px}.bf-duration:hover{border-color:var(--bf-blue)}.bf-duration-label{font-size:18px;font-weight:600;letter-spacing:-.025em}.bf-duration-price{font-size:13px;color:#6a6e77;margin-top:4px}.bf-recommended{position:absolute;left:50%;transform:translateX(-50%);top:-10px;font-size:10px;line-height:18px;border-radius:4px;padding:0 6px;background:var(--bf-blue);color:white;font-weight:500;white-space:nowrap}.bf-recommendation{font-size:12px!important;line-height:1.6;margin-top:14px!important;color:#737c88!important}.bf-price-block{border-top:1px solid var(--bf-line);margin-top:23px;padding-top:21px}.bf-price-label{font-size:13px;color:#697383}.bf-total-row{display:flex;align-items:baseline;flex-wrap:wrap;gap:7px 13px;margin-top:8px}.bf-was{font-size:30px;letter-spacing:-.04em;color:#929aa6;text-decoration-thickness:1.5px;line-height:1.1}.bf-now{font-size:57px;font-weight:600;letter-spacing:-.055em;line-height:1.1}.bf-currency{font-size:13px;color:#747d8c;font-weight:400;letter-spacing:0;margin-left:4px}.bf-save{color:var(--bf-blue);font-size:18px;font-weight:600;margin-top:8px!important}.bf-save span{font-size:12px;font-weight:500;border-radius:4px;background:#edf5ff;padding:4px 6px;margin-left:7px;white-space:nowrap}.bf-payment-note{font-size:12px;color:#727c8b;margin-top:9px!important}.bf-checkout{display:flex;align-items:center;justify-content:center;gap:15px;background:var(--bf-blue);color:white;width:100%;font-size:16px;font-weight:600;padding:16px 12px;border-radius:7px;margin-top:22px}.bf-checkout svg{width:17px;height:17px}.bf-checkout:disabled{opacity:.5;cursor:not-allowed}.bf-preview-note{font-size:11px;color:#788390;text-align:center;line-height:1.5;margin-top:8px!important}.bf-after-purchase{border-top:1px solid var(--bf-line);margin-top:20px;padding-top:17px;font-size:12px;color:#6b7586;line-height:1.65}.bf-after-purchase strong{color:#303b4c;font-weight:500}.bf-adspend{font-size:11px;color:#7c8695;margin-top:9px!important}
.bf-faq{padding:61px 0;background:white}.bf-faq-layout{max-width:850px}.bf-faq-intro{text-align:center}.bf-faq-intro p{font-size:15px;line-height:1.65;color:var(--bf-muted);margin:17px auto 0;max-width:540px}.bf-faq-list{border-top:1px solid var(--bf-line);margin-top:30px}.bf-faq-item{border-color:var(--bf-line)!important}.bf-faq-trigger{font-size:16px!important;font-weight:500!important;text-align:left;padding:20px 0!important;line-height:1.4!important;gap:25px!important}.bf-faq-trigger:hover{text-decoration:none!important}.bf-faq-answer{font-size:15px!important;line-height:1.7!important;color:var(--bf-muted);padding-bottom:22px!important}.bf-close{background:#edf5ff;border-top:1px solid #dce8f8;padding:41px 0}.bf-close .bf-wrap{display:flex;align-items:center;justify-content:space-between;gap:35px}.bf-close h2{font-size:29px;font-weight:600;letter-spacing:-.035em;line-height:1.2;max-width:620px}.bf-close p{font-size:14px;color:#637187;margin-top:9px}.bf-close .bf-cta{flex-shrink:0}.bf-footer{background:var(--bf-ink);color:#949fb0;padding:34px 0;font-size:12px}.bf-footer .bf-wrap{display:flex;align-items:center;justify-content:space-between;gap:25px}.bf-footer .bf-logo{width:125px}.bf-footer nav{display:flex;flex-wrap:wrap;gap:22px}.bf-footer a:hover{color:white}.bf-mobile-sticky{display:none}.bf-media-dialog{background:#000!important;border-color:#ffffff20!important;padding:0!important;max-width:1000px!important;overflow:hidden}.bf-image-dialog{background:white!important;color:var(--bf-ink);max-width:720px!important;max-height:90vh;overflow-y:auto}.bf-image-dialog img{display:block;width:100%;height:auto}
.bf-media-dialog>button:last-child{display:flex;align-items:center;justify-content:center;right:8px;top:8px;z-index:4;width:44px;height:44px;border:1px solid #ffffff40;border-radius:8px;background:#111827e6;color:#fff;opacity:1}
.bf-media-dialog>button:last-child:hover{background:#1e293b}
.bf-media-dialog>button:last-child:focus-visible{outline:3px solid #0071e3;outline-offset:3px}
.bf-media-dialog>button:last-child svg{width:20px;height:20px}
@media(min-width:1500px){.bf-wrap{width:min(1200px,calc(100% - 144px))}.bf-hero-title{font-size:64px}.bf-hero-grid{gap:74px}.bf-hero{padding-top:52px}.bf-proof-strip{margin-top:47px}}
@media(max-width:1050px){.bf-wrap{width:calc(100% - 80px)}.bf-hero-grid{gap:35px}.bf-hero-title{font-size:44px}.bf-hero-copy{font-size:16px}.bf-placeholder-logo{width:130px;margin-bottom:20px}.bf-video-placeholder h2{font-size:21px}.bf-proof-strip{gap:18px;grid-template-columns:.58fr 1fr 1fr}.bf-proof-tile{gap:11px}.bf-proof-face{width:54px;height:60px}.bf-proof-result{font-size:16px}.bf-proof-tile+.bf-proof-tile{padding-left:18px}.bf-journey-steps{gap:20px}.bf-journey-step h3{font-size:18px}.bf-journey-step p{font-size:13px}.bf-compare-table td,.bf-compare-table th{padding:17px}.bf-video-grid{gap:14px}.bf-purchase-grid{gap:42px}.bf-purchase-card{padding:24px}.bf-now{font-size:50px}.bf-was{font-size:28px}.bf-fit-column{padding:25px}}
@media(max-width:760px){.bf-wrap{width:calc(100% - 40px)}.bf-occasion{font-size:10px}.bf-occasion .bf-wrap{padding:8px 0}.bf-nav{height:66px}.bf-logo{width:142px}.bf-nav-links{gap:0}.bf-nav-links>a:not(.bf-nav-buy){display:none}.bf-nav-buy{font-size:11px;padding:7px 10px}.bf-hero{padding-top:25px}.bf-hero-grid{grid-template-columns:1fr;gap:24px}.bf-hero-occasion{font-size:13px;margin-bottom:14px!important}.bf-hero-title{font-size:clamp(36px,10.3vw,57px);line-height:1.04}.bf-hero-copy{font-size:15px;line-height:1.65;margin-top:18px!important;max-width:490px}.bf-hero-action{margin-top:21px}.bf-hero-action .bf-cta{width:100%;justify-content:space-between;padding:15px 18px}.bf-hero-price{font-size:12px;line-height:1.6;margin-top:11px!important}.bf-hero-start{font-size:11px;margin-top:3px!important}.bf-intro-media{margin-top:0}.bf-video-placeholder{padding:20px}.bf-placeholder-logo{width:125px;margin-bottom:17px}.bf-video-placeholder h2{font-size:21px}.bf-video-placeholder p{font-size:11px;margin-top:8px}.bf-intro-caption{font-size:11px;margin-top:12px;gap:10px}.bf-proof-strip{grid-template-columns:1fr 1fr;gap:14px 12px;margin-top:25px;padding:20px 0 23px}.bf-proof-strip>p{grid-column:1 / -1;max-width:none;font-size:12px}.bf-proof-tile{gap:9px;align-items:flex-start}.bf-proof-tile+.bf-proof-tile{padding-left:12px}.bf-proof-face{width:44px;height:53px;border-radius:5px}.bf-proof-name{font-size:11px}.bf-proof-result{font-size:13px;line-height:1.3}.bf-proof-watch{font-size:11px;gap:5px;margin-top:5px}.bf-proof-watch svg{width:8px;height:8px}.bf-energy{display:block}.bf-energy-mobile .bf-ribbon-beam{stroke-width:8;opacity:.13;filter:none}.bf-energy-mobile .bf-ribbon-halo{stroke-width:3;opacity:.24}.bf-energy-mobile .bf-ribbon-travel-glow{stroke-width:5;opacity:.45}.bf-energy-mobile .bf-ribbon-travel-core{stroke-width:1.4}.bf-energy-mobile .bf-ribbon-orb-glow{r:5px}.bf-energy-mobile .bf-ribbon-orb-core{r:2px}.bf-ribbon-turn{height:34px}.bf-heading{font-size:32px}.bf-center-heading{text-align:left}.bf-center-heading>p{font-size:15px;line-height:1.65;margin-top:17px}.bf-recognition{padding:41px 0 4px}.bf-recognition-copy{display:block;margin-top:23px;padding-top:21px}.bf-recognition-copy p:first-child{font-size:18px;line-height:1.6}.bf-recognition-copy p{font-size:14px;line-height:1.75}.bf-recognition-copy p+p{margin-top:18px}.bf-journey{padding:42px 0 37px}.bf-journey-steps{display:block;margin-top:25px}.bf-journey-step{display:grid;grid-template-columns:37px 1fr;gap:14px;padding:21px 0 22px}.bf-step-number{font-size:28px;margin:2px 0 0}.bf-journey-step h3{font-size:20px;max-width:none}.bf-journey-step p{font-size:14px;line-height:1.7;margin-top:10px}.bf-journey-foot{display:block;margin-top:0;padding-top:20px}.bf-journey-foot p{font-size:11px}.bf-journey-foot .bf-text-link{margin-top:18px;font-size:13px}.bf-comparison{padding:39px 0 0}.bf-compare-table{margin-top:24px;font-size:12px;line-height:1.6}.bf-compare-table thead{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}.bf-compare-table tbody,.bf-compare-table tr,.bf-compare-table td{display:block;width:100%}.bf-compare-table tr{display:grid;grid-template-columns:1fr 1fr;border-bottom:1px solid #dce2ec}.bf-compare-table tr:last-child{border-bottom:0}.bf-compare-table td{border-bottom:0;padding:14px 13px 17px}.bf-compare-table td:first-child{grid-column:1 / -1;padding:12px 13px 11px;background:#f4f6f9;border-bottom:1px solid #e2e6ec;font-size:13px;font-weight:600}.bf-compare-table td:nth-child(2):before,.bf-compare-table td:nth-child(3):before{display:block;font-weight:600;font-size:12px;margin-bottom:8px}.bf-compare-table td:nth-child(2):before{content:'On your own';color:#303c4f}.bf-compare-table td:nth-child(3):before{content:'With the kickstart';color:#0071e3}.bf-compare-cell{display:block}.bf-compare-cell svg{display:none}.bf-fit{margin-top:29px;display:block}.bf-fit-column{padding:24px 20px}.bf-fit-column+.bf-fit-column{border-left:0;border-top:1px solid var(--bf-line)}.bf-fit h3{font-size:21px}.bf-fit ul{margin-top:16px}.bf-fit li{font-size:13px;line-height:1.7;margin-top:13px;gap:10px}.bf-fit-note{font-size:11px}.bf-stories{padding:40px 0 37px}.bf-video-grid{grid-template-columns:1fr 1fr;gap:25px 13px;margin-top:26px}.bf-story-name{font-size:16px;margin-top:11px}.bf-story-result{font-size:11px;margin-top:5px}.bf-play-circle{width:27px;height:27px;left:8px;bottom:8px}.bf-play-circle svg{width:10px;height:10px}.bf-stories-more{margin-top:25px}.bf-stories-more .bf-text-link{font-size:13px}.bf-messages{padding:39px 0 4px}.bf-messages-heading{display:block;margin-bottom:23px}.bf-messages-heading .bf-heading{font-size:31px}.bf-messages-heading p{font-size:14px;margin-top:16px;max-width:none}.bf-message-grid{grid-template-columns:1fr;gap:14px}.bf-more-messages{font-size:13px}.bf-messages-note{font-size:10px}.bf-purchase{padding:41px 0}.bf-purchase-grid{grid-template-columns:1fr;gap:29px}.bf-purchase-copy .bf-heading{font-size:37px;max-width:450px}.bf-offer-occasion{font-size:13px;margin-bottom:16px!important}.bf-purchase-copy>p{font-size:15px;margin-top:18px}.bf-included{margin-top:23px}.bf-included li{font-size:13px;margin-top:11px}.bf-start-note{margin-top:24px;padding-top:21px}.bf-start-note h3{font-size:17px}.bf-start-note p{font-size:13px}.bf-offer-help{font-size:12px!important;margin-top:20px!important}.bf-purchase-card{padding:24px 20px}.bf-purchase-card h3{font-size:22px}.bf-duration-options{gap:7px}.bf-now{font-size:54px}.bf-was{font-size:29px}.bf-recommendation{font-size:11px!important}.bf-faq{padding:41px 0}.bf-faq-intro{text-align:left}.bf-faq-intro p{font-size:14px;margin-top:16px}.bf-faq-list{margin-top:25px}.bf-faq-trigger{font-size:15px!important;padding:18px 0!important}.bf-faq-answer{font-size:14px!important}.bf-close{padding:31px 0}.bf-close .bf-wrap{display:block}.bf-close h2{font-size:27px}.bf-close p{font-size:13px}.bf-close .bf-cta{width:100%;justify-content:space-between;margin-top:21px}.bf-footer{padding:28px 0 92px}.bf-footer .bf-wrap{align-items:flex-start;flex-wrap:wrap;gap:22px}.bf-footer .bf-logo{width:125px}.bf-footer nav{width:100%;gap:18px}.bf-footer p{margin-left:auto;padding-top:3px}.bf-mobile-sticky{position:fixed;z-index:40;bottom:0;left:0;right:0;padding:10px 14px max(10px,env(safe-area-inset-bottom));background:#fffffff5;backdrop-filter:blur(10px);border-top:1px solid #dfe1e5}.bf-mobile-sticky.bf-visible{display:block}.bf-mobile-sticky .bf-cta{width:100%;padding:13px 16px;justify-content:space-between;font-size:14px}.bf-image-dialog{width:calc(100% - 24px)!important;padding:14px!important}}
@media(max-width:380px){.bf-wrap{width:calc(100% - 32px)}.bf-hero-title{font-size:37px}.bf-now{font-size:49px}.bf-was{font-size:27px}.bf-proof-face{width:38px;height:49px}.bf-proof-result{font-size:12px}.bf-proof-tile{gap:7px}.bf-proof-tile+.bf-proof-tile{padding-left:9px}.bf-video-placeholder h2{font-size:19px}.bf-placeholder-logo{width:115px;margin-bottom:15px}}

.bf-recognition.bf-recognition-offer{padding:68px 0 24px}.bf-recognition-offer .bf-wrap{display:grid;grid-template-columns:1.2fr .8fr;gap:70px;align-items:center}.bf-recognition-offer .bf-heading{font-size:clamp(37px,3.9vw,52px);max-width:590px}.bf-recognition-lead{font-size:21px;line-height:1.55;letter-spacing:-.025em;color:#303641;margin-top:24px!important;max-width:580px}.bf-recognition-body{font-size:16px;line-height:1.75;color:#697280;margin-top:19px!important;max-width:570px}.bf-recognition-promise{display:flex;align-items:flex-start;gap:13px;padding-top:24px;border-top:1px solid #dce3ed;margin-top:27px!important;max-width:550px;font-size:16px;font-weight:500;line-height:1.55;color:#0071e3}.bf-recognition-promise svg{flex-shrink:0;width:20px;height:20px;margin-top:3px}.bf-phone-figure{position:relative;margin:0;padding:10px 0 0}.bf-phone-figure:before{content:'';position:absolute;inset:10% -5% 16%;border-radius:50%;background:radial-gradient(ellipse,#e9f3ff,transparent 68%);z-index:-1}.bf-spotify-phone{position:relative;width:246px;max-width:100%;margin:0 auto;padding:10px;background:#fff;border:1px solid #dde3eb;border-radius:41px;box-shadow:0 22px 50px #1d355b1a,0 4px 13px #1d355b10;transform:rotate(5deg)}.bf-spotify-phone:before{content:'';position:absolute;z-index:2;left:50%;top:9px;transform:translateX(-50%);width:76px;height:17px;background:white;border-radius:0 0 12px 12px}.bf-spotify-phone:after{content:'';position:absolute;right:-3px;top:116px;width:3px;height:42px;border-radius:0 2px 2px 0;background:#d4dce8}.bf-phone-screen{overflow:hidden;border-radius:32px;background:#fff;aspect-ratio:887/1920}.bf-phone-screen img{display:block;width:100%;height:100%;object-fit:cover}.bf-phone-caption{text-align:center;color:#8c96a4;font-size:11px;margin:21px 0 0}
.bf-value{padding:65px 0 58px}.bf-value-heading{display:flex;align-items:flex-end;justify-content:space-between;gap:45px;margin-bottom:31px}.bf-value-heading .bf-heading{max-width:600px;font-size:clamp(35px,3.7vw,50px)}.bf-value-heading>p{max-width:340px;color:#a6b1c2;font-size:16px;line-height:1.65}.bf-value-feature{padding:33px 35px;border-radius:10px;border:1px solid #ffffff2a;background:linear-gradient(110deg,#142641,#131b27 68%);display:grid;grid-template-columns:43px .9fr 1.2fr;gap:23px;align-items:start}.bf-value-number{font-size:17px;font-weight:500;letter-spacing:-.03em;color:#56a6ff;padding-top:3px}.bf-value-feature h3{font-size:29px;line-height:1.15;font-weight:500;letter-spacing:-.035em;max-width:350px}.bf-value-feature p{font-size:16px;color:#b9c5d7;line-height:1.7}.bf-value-feature p+p{margin-top:14px}.bf-value-feature strong{color:#fff;font-weight:500}.bf-value-stack{margin:25px 0 0;padding:0;list-style:none}.bf-value-row{display:grid;grid-template-columns:43px .9fr 1.2fr;gap:23px;padding:25px 35px;border-bottom:1px solid #ffffff21;align-items:start}.bf-value-row h3{font-size:23px;font-weight:500;letter-spacing:-.03em;line-height:1.25;max-width:340px}.bf-value-row p{font-size:15px;line-height:1.75;color:#a7b4c7;max-width:570px}.bf-value-row p strong{font-weight:500;color:#e1e9f5}.bf-value-carry{display:flex;align-items:center;justify-content:space-between;gap:25px;margin-top:29px;padding:0 35px}.bf-value-carry p{font-size:16px;line-height:1.65;color:#c6d3e6;max-width:690px}.bf-value-carry .bf-text-link{flex-shrink:0;color:#c4d6ef;border-color:#4d6d99}
.bf-damian-extra{position:relative;overflow:hidden;margin-top:37px;padding:36px 39px;border-radius:10px;background:#0071e3;color:white;display:grid;grid-template-columns:1.15fr 1fr;gap:45px}.bf-damian-extra:after{content:'';position:absolute;width:440px;height:440px;right:-240px;top:-215px;border:1px solid #ffffff26;border-radius:50%;pointer-events:none}.bf-extra-label{font-size:14px;font-weight:500;color:#c8e1ff;display:block;margin-bottom:13px}.bf-damian-extra h3{font-size:clamp(29px,2.9vw,38px);font-weight:500;line-height:1.12;letter-spacing:-.04em;max-width:520px}.bf-damian-extra p{font-size:16px;line-height:1.65;color:#e4f1ff;max-width:440px}.bf-damian-extra .bf-extra-eligibility{color:#fff;font-weight:500;font-size:14px;line-height:1.5;margin-top:17px}.bf-damian-extra .bf-text-link{border-color:#b4d4ff;color:#fff;margin-top:19px;font-size:14px}.bf-hero-bonus{display:flex;align-items:flex-start;gap:8px;font-size:14px;line-height:1.5;color:#d5e6fd;max-width:440px;margin-top:13px!important}.bf-hero-bonus svg{width:15px;height:15px;flex-shrink:0;color:#0071e3;margin-top:3px}.bf-selected-extra{margin-top:18px;padding:15px 16px;border-radius:7px;background:#edf5ff;border:1px solid #d4e6fc;display:flex;gap:11px;align-items:flex-start;color:#1d4f89;font-size:14px;line-height:1.55}.bf-selected-extra svg{width:18px;height:18px;flex-shrink:0;margin-top:2px;color:#0071e3}.bf-selected-extra strong{font-weight:600;color:#164b89}.bf-selected-extra.bf-extra-upgrade{background:#f6f7f9;border-color:#e2e5eb;color:#687489}.bf-purchase-card .bf-recommendation{font-size:13px!important}.bf-was{color:#69717e}.bf-after-purchase{font-size:14px;line-height:1.65}.bf-after-purchase>strong{display:block;font-size:15px;font-weight:600;margin-bottom:12px}.bf-onboarding-steps{list-style:none;margin:0;padding:0;counter-reset:bf-onboard}.bf-onboarding-steps li{counter-increment:bf-onboard;display:flex;gap:10px;margin-top:9px;color:#677487}.bf-onboarding-steps li:before{content:counter(bf-onboard);width:20px;height:20px;flex-shrink:0;background:#f0f4f9;color:#526681;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;margin-top:2px}.bf-purchase .bf-adspend{font-size:12px;margin-top:12px!important}.bf-included .bf-included-extra{color:#fff;font-weight:500}
@media(max-width:1050px){.bf-recognition-offer .bf-wrap{gap:42px}.bf-spotify-phone{width:219px;padding:9px;border-radius:37px}.bf-phone-screen{border-radius:29px}.bf-value-heading{gap:30px}.bf-value-heading>p{max-width:285px;font-size:15px}.bf-value-feature,.bf-value-row{padding-left:25px;padding-right:25px;grid-template-columns:32px .95fr 1.2fr;gap:18px}.bf-value-feature h3{font-size:26px}.bf-value-row h3{font-size:21px}.bf-damian-extra{gap:30px;padding:31px}.bf-value-carry{padding:0 25px}.bf-value-carry p{font-size:15px}}
@media(max-width:760px){.bf-recognition.bf-recognition-offer{padding:41px 0 13px}.bf-recognition-offer .bf-wrap{display:block}.bf-recognition-offer .bf-heading{font-size:37px;max-width:480px}.bf-recognition-lead{font-size:18px;margin-top:20px!important;line-height:1.6}.bf-recognition-body{font-size:15px;margin-top:16px!important;line-height:1.75}.bf-recognition-promise{font-size:15px;margin-top:22px!important;padding-top:20px;gap:11px}.bf-recognition-promise svg{width:18px;height:18px}.bf-phone-figure{padding-top:29px}.bf-spotify-phone{width:220px;padding:9px;transform:rotate(3deg);border-radius:37px}.bf-phone-screen{border-radius:29px}.bf-phone-caption{font-size:11px;margin-top:17px}.bf-value{padding:41px 0 37px}.bf-value-heading{display:block;margin-bottom:25px}.bf-value-heading .bf-heading{font-size:35px}.bf-value-heading>p{font-size:15px;margin-top:18px;max-width:none}.bf-value-feature{display:grid;grid-template-columns:24px 1fr;gap:10px 12px;padding:23px 18px}.bf-value-feature h3{font-size:25px;max-width:none}.bf-value-feature>div{grid-column:2}.bf-value-feature p{font-size:15px;line-height:1.75}.bf-value-number{font-size:14px;padding-top:4px}.bf-value-stack{margin-top:6px}.bf-value-row{display:grid;grid-template-columns:24px 1fr;padding:23px 5px;gap:10px 12px}.bf-value-row h3{font-size:22px;max-width:none}.bf-value-row p{grid-column:2;font-size:15px;line-height:1.75}.bf-value-carry{display:block;padding:0 4px;margin-top:24px}.bf-value-carry p{font-size:15px}.bf-value-carry .bf-text-link{margin-top:17px;font-size:14px}.bf-damian-extra{display:block;padding:26px 23px;margin-top:28px}.bf-damian-extra h3{font-size:32px;max-width:420px}.bf-extra-label{font-size:13px;margin-bottom:14px}.bf-damian-extra p{font-size:15px;line-height:1.7;margin-top:19px}.bf-damian-extra .bf-extra-eligibility{font-size:14px;margin-top:15px}.bf-damian-extra .bf-text-link{font-size:14px;margin-top:18px}.bf-compare-table{font-size:14px;line-height:1.65}.bf-compare-table td{padding:15px 12px 18px}.bf-compare-table td:first-child{font-size:14px}.bf-compare-table td:nth-child(2):before,.bf-compare-table td:nth-child(3):before{font-size:13px}.bf-fit li{font-size:14px}.bf-fit-note{font-size:13px}.bf-selected-extra{font-size:14px;padding:14px 13px}.bf-after-purchase{font-size:14px}.bf-purchase-card .bf-recommendation{font-size:13px!important}.bf-purchase .bf-adspend{font-size:12px}}

.bf-recognition-offer .bf-wrap{display:block;max-width:790px}.bf-recognition-offer .bf-heading{max-width:780px}.bf-recognition-offer .bf-recognition-lead,.bf-recognition-offer .bf-recognition-body{max-width:760px}.bf-recognition-offer .bf-recognition-promise{max-width:760px}.bf-proof-phone{max-width:300px;margin:27px auto 31px;padding-top:0}.bf-proof-phone .bf-spotify-phone{width:234px;transform:none}.bf-stories .bf-phone-figure:before{background:radial-gradient(ellipse,#0071e325,transparent 68%)}.bf-proof-phone+.bf-video-grid{margin-top:30px}@media(max-width:760px){.bf-recognition-offer .bf-wrap{max-width:790px}.bf-proof-phone{margin:25px auto 29px;padding-top:0}.bf-proof-phone .bf-spotify-phone{width:210px;transform:none}}

.bf-recognition-offer .bf-proof-phone{max-width:680px;margin:32px auto 6px;padding:29px 20px 14px;isolation:isolate}.bf-recognition-offer .bf-proof-phone .bf-spotify-phone{width:320px;transform:none;box-shadow:0 28px 66px #1c3d6521,0 6px 18px #1c3d6514}.bf-recognition-offer .bf-phone-figure:before{inset:3% -3% 7%;border-radius:36px;background:radial-gradient(ellipse at 50% 48%,#deecfd 0%,#eef4fb 40%,#f8fafd 60%,transparent 75%);z-index:-1}.bf-recognition-offer .bf-proof-phone:after{content:'';position:absolute;left:50%;bottom:52px;width:350px;height:36px;transform:translateX(-50%);background:#718dad28;border-radius:50%;filter:blur(20px);z-index:-1}.bf-recognition-offer .bf-phone-caption{margin-top:22px;font-size:12px}@media(max-width:760px){.bf-recognition-offer .bf-proof-phone{margin:26px auto 3px;padding:23px 12px 13px}.bf-recognition-offer .bf-proof-phone .bf-spotify-phone{width:260px}.bf-recognition-offer .bf-proof-phone:after{width:255px;bottom:49px;height:27px}.bf-recognition-offer .bf-phone-figure:before{inset:4% -9% 8%;border-radius:28px}}

.bf-recognition-offer .bf-wrap{display:grid;grid-template-columns:minmax(0,1fr) 410px;gap:60px;align-items:center;max-width:1120px}.bf-recognition-offer .bf-proof-phone{width:100%;max-width:450px;margin:0 auto;padding:26px 20px 14px}.bf-recognition-offer .bf-proof-phone .bf-spotify-phone{width:345px;transform:none}.bf-recognition-offer .bf-phone-figure:before{inset:3% -7% 7%;background:radial-gradient(ellipse at 50% 48%,#deecfd 0%,#eef4fb 40%,#f8fafd 60%,transparent 75%)}@media(min-width:761px) and (max-width:1050px){.bf-recognition-offer .bf-wrap{grid-template-columns:minmax(0,1fr) 350px;gap:38px}.bf-recognition-offer .bf-proof-phone{padding-left:15px;padding-right:15px}.bf-recognition-offer .bf-proof-phone .bf-spotify-phone{width:320px}}@media(max-width:760px){.bf-recognition-offer .bf-wrap{display:block;max-width:790px}.bf-recognition-offer .bf-proof-phone{max-width:450px;margin:26px auto 3px;padding:23px 12px 13px}.bf-recognition-offer .bf-proof-phone .bf-spotify-phone{width:260px;transform:none}.bf-recognition-offer .bf-phone-figure:before{inset:4% -9% 8%}}

.bf-highlight{display:inline-block;background:#0071e3;color:#fff;padding:4px 12px;border-radius:8px;font-weight:700;transform:rotate(-2deg);box-shadow:0 0 40px rgba(0,113,227,.5);line-height:1.1;white-space:nowrap;vertical-align:baseline;margin:.06em 0 .12em}.bf-highlight-pale{background:#f5f5f7;color:#1d1d1f;box-shadow:0 0 40px rgba(0,113,227,.7)}.bf-dark .bf-highlight-blue,.bf-hero .bf-highlight-blue{box-shadow:0 0 40px rgba(0,113,227,.7)}.bf-hero-title .bf-title-result{margin-top:7px;margin-bottom:8px}.bf-hero-title .bf-highlight{margin:0;line-height:1.1}.bf-heading:has(.bf-highlight){line-height:1.2}.bf-damian-extra h3:has(.bf-highlight),.bf-close h2:has(.bf-highlight){line-height:1.24}@media(max-width:760px){.bf-hero-title .bf-title-result{margin-top:6px;margin-bottom:8px}.bf-highlight{max-width:100%}.bf-heading:has(.bf-highlight){line-height:1.22}}

@media(prefers-reduced-motion:reduce){.bf-page *, .bf-page *:before,.bf-page *:after{scroll-behavior:auto!important;transition:none!important;animation:none!important}.bf-ribbon-travel,.bf-ribbon-orb{display:none}}
.bf-purchase{position:relative;isolation:isolate;background:radial-gradient(ellipse at 10% 0,#1e3b5f 0,#152338 28%,#101722 60%,#101114 100%)}
.bf-purchase:before{content:'';position:absolute;inset:0;pointer-events:none;opacity:.022;background-image:url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20width%3D%27256%27%20height%3D%27256%27%20viewBox%3D%270%200%20256%20256%27%3E%3Cfilter%20id%3D%27grain%27%20x%3D%270%27%20y%3D%270%27%20width%3D%27100%25%27%20height%3D%27100%25%27%20color-interpolation-filters%3D%27sRGB%27%3E%3CfeTurbulence%20type%3D%27fractalNoise%27%20baseFrequency%3D%27.82%27%20numOctaves%3D%271%27%20seed%3D%2717%27%20stitchTiles%3D%27stitch%27%2F%3E%3CfeColorMatrix%20type%3D%27saturate%27%20values%3D%270%27%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%27100%25%27%20height%3D%27100%25%27%20filter%3D%27url%28%23grain%29%27%2F%3E%3C%2Fsvg%3E");background-size:128px 128px;background-repeat:repeat}
.bf-purchase-card{box-shadow:0 2px 0 #fff6 inset,0 28px 65px #0003,0 0 70px #4492ed0c;border:1px solid #fff9;background:linear-gradient(145deg,#fff,#f6f9fd)}
.bf-bonus-grid article{position:relative;overflow:hidden;isolation:isolate;padding:32px 34px 29px;border:1px solid #fff2;box-shadow:inset 0 1px 0 #fff3,0 17px 38px #050b1621}
.bf-bonus-academy{background:radial-gradient(ellipse at 80% 0,#dceafb,#f3f6fb 55%,#f9fafc)}.bf-bonus-damian{background:radial-gradient(ellipse at 20% 8%,#2b8af1,#1365ba 40%,#16467b 100%)}
.bf-membership-art{height:180px;position:relative;margin:-8px 0 29px;display:flex;align-items:center;justify-content:center;isolation:isolate}.bf-membership-art:before{content:'';position:absolute;left:8%;right:8%;bottom:4%;height:30%;background:#255c9f22;border-radius:50%;filter:blur(20px);z-index:-1}
.bf-membership-pass{position:relative;overflow:hidden;display:flex;flex-direction:column;justify-content:space-between;width:290px;max-width:93%;height:164px;border-radius:12px;padding:20px 23px;color:#eaf4ff;background:linear-gradient(135deg,#1c2f45,#37659c 35%,#1b3b65 51%,#101f33 85%);border:1px solid #8ebded82;box-shadow:inset 0 1px 0 #dbebff57,0 15px 28px #1e497033;transform:rotate(-6deg);text-align:left}.bf-membership-pass:before{content:'';position:absolute;width:240px;height:300px;border:1px solid #acd7ff33;border-radius:50%;right:-110px;top:-10px;transform:rotate(35deg);box-shadow:0 0 0 14px #acd7ff09,0 0 0 29px #acd7ff06,0 0 0 44px #acd7ff04;pointer-events:none}.bf-membership-pass:after{content:'';position:absolute;inset:0;background:linear-gradient(110deg,transparent 15%,#fff1 36%,transparent 60%);pointer-events:none}.bf-membership-pass strong{font-size:18px;letter-spacing:-.02em;font-weight:600;line-height:1.2}.bf-membership-pass>span{font-size:29px;letter-spacing:-.04em;font-weight:500;line-height:1}.bf-membership-pass small{font-size:10px;color:#bad7f7;letter-spacing:.015em}
.bf-private-call-art{height:180px;position:relative;display:flex;align-items:center;justify-content:center;margin:-8px 0 29px;isolation:isolate}.bf-private-call-window{width:290px;max-width:93%;height:164px;border:1px solid #c7e5ff60;border-top-color:#eaf6ffa3;border-radius:12px;background:linear-gradient(125deg,#d5eeff1f,#0a36613b);box-shadow:inset 0 1px 0 #fff2,0 14px 30px #002e5b30;transform:rotate(4deg);padding:18px 22px;display:flex;flex-direction:column;justify-content:space-between;position:relative;overflow:hidden}.bf-private-call-window:after{content:'';position:absolute;inset:0;background:linear-gradient(120deg,#fff1,transparent 44%);pointer-events:none}.bf-private-call-window>span{font-size:11px;color:#d8ebff;display:flex;align-items:center;gap:6px}.bf-private-call-window>span:before{content:'';width:6px;height:6px;border-radius:50%;background:#9ed2ff;box-shadow:0 0 9px #b7ddff88}.bf-private-call-window strong{font-size:27px;font-weight:500;line-height:1;letter-spacing:-.035em;color:#fff}.bf-private-call-window svg{width:100%;height:34px}
.bf-compare-table{border:1px solid #dbe4f0;border-radius:13px;overflow:hidden;box-shadow:0 12px 35px #21487908}.bf-compare-table thead th{background:#f4f6f9}.bf-compare-table thead th:last-child{background:linear-gradient(125deg,#136ac4,#0855a4);color:white}.bf-compare-table td:nth-child(3){background:linear-gradient(110deg,#edf5ff,#f6f9fe)}
.bf-fit{gap:34px;border-top:0!important;padding-top:39px!important}.bf-fit-column{border:0!important;border-radius:12px;padding:25px 28px!important;background:#f3f7fc}.bf-fit-column+.bf-fit-column{background:#f7f7f8}.bf-fit-column h3{font-size:23px;letter-spacing:-.03em}.bf-fit li{color:#526378;line-height:1.65}.bf-fit-column:first-child li svg{padding:3px;box-sizing:content-box;background:#e0edfd;border-radius:50%;width:13px;height:13px}.bf-fit-column:last-child li svg{color:#8d96a5}
.bf-close{background:radial-gradient(ellipse at 70% 50%,#e7f0fc,#f5f7fb 70%)}
@media(max-width:760px){.bf-bonus-grid article{padding:27px 24px}.bf-membership-art,.bf-private-call-art{height:165px;margin:-5px 0 23px}.bf-membership-pass,.bf-private-call-window{width:265px;height:150px}.bf-fit{gap:16px}.bf-fit-column{padding:24px 22px!important}.bf-fit-column+.bf-fit-column{margin-top:16px}.bf-fit-column h3{font-size:22px}.bf-compare-table{border:0;box-shadow:none;border-radius:0}.bf-compare-table td:nth-child(3){background:linear-gradient(120deg,#eaf3ff,#f5f9ff)}}

`;

const scrollToKickstart = (event?: React.MouseEvent) => {
  event?.preventDefault();
  const target = document.getElementById("kickstart");
  if (!target) return;
  target.scrollIntoView({
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    block: "start",
  });
  history.replaceState(null, "", "#kickstart");
};

function Highlight({ children, variant = "blue" }: { children: React.ReactNode; variant?: "blue" | "pale" }) {
  return <span className={`bf-highlight bf-highlight-${variant}`}>{children}</span>;
}

function KickstartCTA({ className = "", id }: { className?: string; id?: string }) {
  return (
    <a id={id} href="#kickstart" onClick={scrollToKickstart} className={`bf-cta ${className}`}>
      Choose your kickstart <ArrowRight aria-hidden />
    </a>
  );
}

type Point = { x: number; y: number };
type Rect = { left: number; top: number; width: number; height: number };
type Centre = Point & { nx: number; ny: number; spread: number; t: number };
type Geometry = {
  width: number;
  mobile: boolean;
  hero: Rect;
  pitch: Rect;
  proof: Rect;
  phone: Rect | null;
  bounds: Rect;
  centre: Centre[];
};
type Wisp = { lane: number; phase: number; strength: number; depth: number; weight: number };
type Layer = { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; scale: number };

const energyRibbonStyles = `
.dk-energy-flow{position:absolute;inset:0;z-index:2;pointer-events:none!important;overflow:clip;user-select:none}
.dk-energy-scene{position:absolute;pointer-events:none!important;isolation:isolate;mask-composite:intersect;-webkit-mask-composite:source-in}
.dk-energy-scene canvas{position:absolute;inset:0;display:block;width:100%;height:100%;pointer-events:none!important}
.dk-energy-haze{filter:blur(24px);opacity:.80}
.dk-energy-bloom{filter:blur(7px);opacity:.92}
.dk-energy-fine{opacity:.94}
.dk-energy-phone{position:absolute;pointer-events:none!important;background:radial-gradient(ellipse,rgba(0,113,227,.061),rgba(32,130,234,.036) 34%,rgba(66,146,232,.013) 62%,transparent 76%);animation:dk-energy-breath 23s ease-in-out infinite alternate;transform-origin:50% 50%}
@keyframes dk-energy-breath{from{transform:translate3d(-5px,3px,0) scale(.97);opacity:.89}to{transform:translate3d(7px,-4px,0) scale(1.035);opacity:1}}
@media(max-width:760px){.dk-energy-haze{filter:blur(18px);opacity:.6}.dk-energy-bloom{filter:blur(6px);opacity:.78}.dk-energy-fine{opacity:.83}}
@media(prefers-reduced-motion:reduce){.dk-energy-scene{opacity:.82}.dk-energy-phone{animation:none!important}}
`;

const clamp = (v: number, a = 0, b = 1) => Math.max(a, Math.min(b, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const smooth = (a: number, b: number, v: number) => {
  const t = clamp((v - a) / (b - a));
  return t * t * (3 - 2 * t);
};
function hash(x: number, y: number) {
  let n = Math.imul(x, 374761393) + Math.imul(y, 668265263);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
}
/** Quintic coherent noise gives continuous position, velocity and acceleration. */
function noise(x: number, y: number) {
  const ix = Math.floor(x),
    iy = Math.floor(y),
    fx = x - ix,
    fy = y - iy;
  const u = fx * fx * fx * (fx * (fx * 6 - 15) + 10);
  const v = fy * fy * fy * (fy * (fy * 6 - 15) + 10);
  return lerp(lerp(hash(ix, iy), hash(ix + 1, iy), u), lerp(hash(ix, iy + 1), hash(ix + 1, iy + 1), u), v);
}
function field(x: number, y: number) {
  return noise(x, y) * 0.7 + noise(x * 2.03 + 19.2, y * 2.03 + 7.4) * 0.3;
}
function cubic(a: Point, b: Point, c: Point, d: Point, t: number) {
  const u = 1 - t;
  const x = u * u * u * a.x + 3 * u * u * t * b.x + 3 * u * t * t * c.x + t * t * t * d.x;
  const y = u * u * u * a.y + 3 * u * u * t * b.y + 3 * u * t * t * c.y + t * t * t * d.y;
  const dx = 3 * u * u * (b.x - a.x) + 6 * u * t * (c.x - b.x) + 3 * t * t * (d.x - c.x);
  const dy = 3 * u * u * (b.y - a.y) + 6 * u * t * (c.y - b.y) + 3 * t * t * (d.y - c.y);
  const length = Math.hypot(dx, dy) || 1;
  return { x, y, nx: -dy / length, ny: dx / length };
}
function measure(main: HTMLElement): Geometry | null {
  const b = main.getBoundingClientRect();
  const heroElement = main.querySelector<HTMLElement>(".bf-hero");
  const pitchElement = main.querySelector<HTMLElement>(".bf-hero-pitch");
  const proofElement = main.querySelector<HTMLElement>(".bf-hero .bf-proof-strip");
  if (!heroElement || !pitchElement || !proofElement || b.width < 1) return null;
  const relative = (element: HTMLElement): Rect => {
    const r = element.getBoundingClientRect();
    return { left: r.left - b.left, top: r.top - b.top, width: r.width, height: r.height };
  };
  const hero = relative(heroElement),
    pitch = relative(pitchElement),
    proof = relative(proofElement);
  const phoneElement = main.querySelector<HTMLElement>(
    ".bf-recognition-offer .bf-proof-phone, .bf-recognition .bf-phone-figure",
  );
  const phone = phoneElement ? relative(phoneElement) : null;
  const mobile = b.width <= 760,
    width = b.width,
    cx = pitch.left + pitch.width / 2;
  // An open current frames the reading area. Its broad upper fan and lower sweep
  // share a downward tangent at the right bend, avoiding a loop or a visible join.
  const bend = {
    x: mobile ? width - 27 : cx + Math.min(pitch.width * 0.5 + 100, width * 0.43),
    y: pitch.top + pitch.height * (mobile ? 0.54 : 0.48),
  };
  const centre: Centre[] = [],
    steps = mobile ? 64 : 90,
    split = 0.49;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const p =
      t <= split
        ? cubic(
            { x: -Math.max(100, width * 0.12), y: hero.top - (mobile ? 130 : 220) },
            { x: mobile ? width + 55 : cx + pitch.width * 0.3, y: hero.top - 10 },
            { x: bend.x + (mobile ? 32 : 28), y: bend.y - 125 },
            bend,
            t / split,
          )
        : cubic(
            bend,
            { x: bend.x - (mobile ? 40 : 35), y: bend.y + 155 },
            { x: cx + (mobile ? 90 : 60), y: proof.top + proof.height + 38 },
            { x: mobile ? -90 : cx - pitch.width * 0.74, y: hero.top + hero.height + 125 },
            (t - split) / (1 - split),
          );
    const distance = Math.abs(t - split) / (t < split ? split : 1 - split);
    const fan = smooth(0, 1, distance);
    const spread = mobile ? 8 + fan * 58 : 13 + fan * (t < split ? 155 : 125);
    centre.push({ ...p, spread, t });
  }
  // Allow the entire strand fan, pointer bend and four blur radii on every side.
  // Fixed hero-sized canvases cropped the lower sweep on narrow screens.
  const padding = (mobile ? 18 : 24) * 4 + 24;
  const left = Math.min(0, ...centre.map((p) => p.x - p.spread * 1.65)) - padding;
  const right = Math.max(width, ...centre.map((p) => p.x + p.spread * 1.65)) + padding;
  const top = Math.min(hero.top, ...centre.map((p) => p.y - p.spread * 1.65)) - padding;
  const bottom = Math.max(hero.top + hero.height, ...centre.map((p) => p.y + p.spread * 1.65)) + padding;
  const bounds = { left, top, width: right - left, height: bottom - top };
  return { width, mobile, hero, pitch, proof, phone, bounds, centre };
}
function wisps(count: number): Wisp[] {
  return Array.from({ length: count }, (_, i) => {
    const u = (i / (count - 1)) * 2 - 1;
    return {
      lane: Math.sign(u) * Math.pow(Math.abs(u), 1.35),
      phase: hash(i, 17) * 9,
      strength: 0.35 + hash(i, 51) * 0.65,
      depth: hash(i, 72),
      weight: 0.42 + hash(i, 99) * 0.7,
    };
  });
}
function makeLayer(canvas = document.createElement("canvas")): Layer {
  return { canvas, ctx: canvas.getContext("2d", { alpha: true })!, scale: 1 };
}
function sizeLayer(layer: Layer, bounds: Rect, scale: number) {
  layer.scale = scale;
  const width = Math.ceil(bounds.width * scale),
    height = Math.ceil(bounds.height * scale);
  if (layer.canvas.width !== width) layer.canvas.width = width;
  if (layer.canvas.height !== height) layer.canvas.height = height;
}
function resetLayer(layer: Layer, bounds: Rect) {
  layer.ctx.setTransform(1, 0, 0, 1, 0, 0);
  layer.ctx.clearRect(0, 0, layer.canvas.width, layer.canvas.height);
  layer.ctx.setTransform(layer.scale, 0, 0, layer.scale, -bounds.left * layer.scale, -bounds.top * layer.scale);
  layer.ctx.globalAlpha = 1;
  layer.ctx.globalCompositeOperation = "source-over";
}
/** Whole smooth curves avoid tiny independently lit segments that shimmer as they change. */
function smoothPath(points: Point[]) {
  const path = new Path2D();
  path.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length - 1; i++) {
    path.quadraticCurveTo(
      points[i].x,
      points[i].y,
      (points[i].x + points[i + 1].x) * 0.5,
      (points[i].y + points[i + 1].y) * 0.5,
    );
  }
  const last = points[points.length - 1];
  path.lineTo(last.x, last.y);
  return path;
}
/** Static protection is feathered once per layout, not erased per moving segment. */
function buildMask(layer: Layer, g: Geometry, foreground: boolean) {
  resetLayer(layer, g.bounds);
  const ctx = layer.ctx;
  ctx.fillStyle = "#fff";
  ctx.fillRect(g.bounds.left, g.bounds.top, g.bounds.width, g.bounds.height);
  ctx.globalCompositeOperation = "destination-out";
  // Continuous elliptical protection gives the type a calm centre without the
  // rectangular holes produced by masking individual text elements.
  const protect = (r: Rect, rx: number, ry: number, strength: number) => {
    ctx.save();
    ctx.translate(r.left + r.width / 2, r.top + r.height / 2);
    ctx.scale(rx, ry);
    const field = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
    field.addColorStop(0, `rgba(0,0,0,${strength})`);
    field.addColorStop(0.48, `rgba(0,0,0,${strength})`);
    field.addColorStop(0.72, `rgba(0,0,0,${strength * 0.76})`);
    field.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = field;
    ctx.fillRect(-1, -1, 2, 2);
    ctx.restore();
  };
  protect(g.pitch, g.pitch.width * (g.mobile ? 0.68 : 0.69), g.pitch.height * 0.75, foreground ? 0.985 : 0.76);
  protect(g.proof, g.proof.width * 0.66, g.proof.height * 1.05, foreground ? 0.96 : 0.72);
  ctx.filter = "none";
  ctx.globalCompositeOperation = "destination-in";
  // Fade well before the hero's physical top/bottom. Blur has no bright edge to crop.
  const edge = ctx.createLinearGradient(0, g.hero.top, 0, g.hero.top + g.hero.height);
  const top = Math.min(g.mobile ? 65 : 80, g.hero.height * 0.24) / g.hero.height;
  const bottom = Math.min(g.mobile ? 95 : 135, g.hero.height * 0.24) / g.hero.height;
  edge.addColorStop(0, "rgba(255,255,255,0)");
  edge.addColorStop(top, "#fff");
  edge.addColorStop(1 - bottom, "#fff");
  edge.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = edge;
  ctx.fillRect(g.bounds.left, g.bounds.top, g.bounds.width, g.bounds.height);
  ctx.globalCompositeOperation = "source-over";
}
function applyMask(layer: Layer, mask: Layer) {
  const ctx = layer.ctx;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "destination-in";
  ctx.drawImage(mask.canvas, 0, 0, layer.canvas.width, layer.canvas.height);
  ctx.restore();
}

/**
 * The browser scrolls these small scene surfaces with the page. RAF only advances the
 * medium, never reconstructs a scrolling camera. Bloom is composited by CSS, not
 * re-blurred into offscreen buffers on every frame. Hover gently bends a broad field.
 */
function EnergyRibbon({ mainRef }: { mainRef: React.RefObject<HTMLElement> }) {
  const sceneRef = useRef<HTMLDivElement | null>(null);
  const bloomRef = useRef<HTMLCanvasElement | null>(null);
  const hazeRef = useRef<HTMLCanvasElement | null>(null);
  const fineRef = useRef<HTMLCanvasElement | null>(null);
  const phoneRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const main = mainRef.current,
      scene = sceneRef.current,
      phoneElement = phoneRef.current;
    if (!main || !scene || !bloomRef.current || !hazeRef.current || !fineRef.current || !phoneElement) return;
    const bloom = makeLayer(bloomRef.current),
      haze = makeLayer(hazeRef.current),
      fine = makeLayer(fineRef.current);
    const fogMask = makeLayer(),
      fineMask = makeLayer();
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    let geometry: Geometry | null = null,
      rays: Wisp[] = [],
      raf = 0,
      layoutRaf = 0;
    let disposed = false,
      heroVisible = false,
      phoneVisible = false,
      last = 0,
      clock = 23.6,
      elapsed = 0;
    let heroClient: DOMRect | null = null;
    const hover = { t: 0.57, targetT: 0.57, x: 0, y: 0, targetX: 0, targetY: 0, amount: 0, targetAmount: 0 };
    const active = () => !disposed && heroVisible && !document.hidden && !motion.matches;
    const draw = () => {
      const g = geometry;
      if (!g) return;
      resetLayer(bloom, g.bounds);
      resetLayer(fine, g.bounds);
      const context = bloom.ctx,
        crisp = fine.ctx;
      context.lineCap = crisp.lineCap = "round";
      context.lineJoin = crisp.lineJoin = "round";
      context.globalCompositeOperation = crisp.globalCompositeOperation = "lighter";
      const gradientTop = g.centre[0].y,
        gradientBottom = g.centre[g.centre.length - 1].y;
      for (const ray of rays) {
        const points: Point[] = [],
          alphas: number[] = [];
        for (let i = 0; i < g.centre.length; i++) {
          const base = g.centre[i],
            advected = base.t * 8.8 - clock * 0.235;
          const shared = field(advected * 0.53, ray.lane * 0.22 + clock * 0.014) - 0.5;
          const curl = field(advected * 1.17 + 30, ray.lane * 0.61 + clock * 0.011) - 0.5;
          const separation = ray.lane + shared * 0.39 + curl * 0.17 * (0.3 + Math.abs(ray.lane));
          const influence = Math.exp(-Math.pow((base.t - hover.t) / 0.26, 2) * 0.5) * hover.amount;
          const expansion = 0.88 + field(base.t * 3.1 + clock * 0.029, 12.7) * 0.25 + influence * 0.045;
          const offset = base.spread * separation * expansion;
          // One broad coherent bend; no radial normalization, velocity impulse or local cusp.
          points.push({
            x: base.x + base.nx * offset + hover.x * influence,
            y: base.y + base.ny * offset + hover.y * influence,
          });
          const life =
            0.14 + smooth(0.13, 0.78, field(advected * 1.1 + ray.phase * 0.14, ray.lane * 1.55 + 4.2)) * 0.86;
          const ends = smooth(0, 0.075, base.t) * (1 - smooth(0.87, 1, base.t));
          // A single broad arrival surge, followed by a quieter travelling current.
          // Light changes continuously along whole strands; no blinking particles.
          const arrivalPosition = (elapsed / 2.6) * 1.5 - 0.25;
          const arrival = motion.matches
            ? 0
            : Math.exp(-Math.pow((base.t - arrivalPosition) / 0.15, 2) * 0.5) * (1 - smooth(2.6, 3.2, elapsed));
          const driftPosition = ((elapsed / 7.8 + ray.phase * 0.012) % 1) * 1.5 - 0.25;
          const current = motion.matches
            ? 0
            : Math.exp(-Math.pow((base.t - driftPosition) / 0.1, 2) * 0.5) * smooth(2, 4, elapsed);
          alphas.push(life * ends * ray.strength * (1 + influence * 0.2 + arrival * 0.95 + current * 0.24));
        }
        const path = smoothPath(points);
        const fog = context.createLinearGradient(0, gradientTop, 0, gradientBottom);
        const sharp = crisp.createLinearGradient(0, gradientTop, 0, gradientBottom);
        // Continuous gradient stops, not brightness buckets or vanishing path segments.
        for (let i = 0; i < g.centre.length; i += 3) {
          const base = g.centre[i],
            alpha = alphas[i],
            exit = smooth(0.6, 0.82, base.t);
          const stop = clamp((base.y - gradientTop) / (gradientBottom - gradientTop));
          // Lift the broad volume more than the already-bright waist.
          const presence = 1.16 + smooth(0.08, 0.38, Math.abs(base.t - 0.57)) * 0.06;
          fog.addColorStop(
            stop,
            `rgba(20,132,250,${clamp(alpha * presence * (1 - exit * 0.28) * (g.mobile ? 0.3 : 0.39))})`,
          );
          sharp.addColorStop(
            stop,
            `rgba(184,229,255,${clamp(alpha * 1.16 * (1 - exit * 0.34) * (g.mobile ? 0.28 : 0.35))})`,
          );
        }
        fog.addColorStop(1, "rgba(20,132,250,0)");
        sharp.addColorStop(1, "rgba(184,229,255,0)");
        context.strokeStyle = fog;
        context.lineWidth = (g.mobile ? 3.6 : 5.6) * ray.weight;
        context.stroke(path);
        if (ray.depth > 0.34) {
          crisp.strokeStyle = sharp;
          crisp.lineWidth = 0.46 + ray.weight * 0.43;
          crisp.stroke(path);
        }
      }
      applyMask(bloom, fogMask);
      applyMask(fine, fineMask);
      haze.ctx.setTransform(1, 0, 0, 1, 0, 0);
      haze.ctx.clearRect(0, 0, haze.canvas.width, haze.canvas.height);
      haze.ctx.drawImage(bloom.canvas, 0, 0, haze.canvas.width, haze.canvas.height);
    };
    const tick = (now: number) => {
      raf = 0;
      if (!active()) return;
      const dt = last ? Math.min((now - last) / 1000, 0.045) : 1 / 60;
      last = now;
      clock += dt * 0.66;
      elapsed += dt;
      const allowed = finePointer.matches && !motion.matches;
      // Analytic first-order easing is stable at every refresh rate and cannot overshoot.
      const ease = 1 - Math.exp(-dt * 2.4),
        settle = 1 - Math.exp(-dt * 1.9);
      hover.t += (hover.targetT - hover.t) * ease;
      hover.x += (hover.targetX - hover.x) * ease;
      hover.y += (hover.targetY - hover.y) * ease;
      hover.amount += ((allowed ? hover.targetAmount : 0) - hover.amount) * settle;
      draw();
      raf = requestAnimationFrame(tick);
    };
    const sync = () => {
      phoneElement.style.animationPlayState =
        phoneVisible && !document.hidden && !motion.matches ? "running" : "paused";
      if (!finePointer.matches || motion.matches || document.hidden) {
        hover.targetAmount = 0;
        hover.amount = 0;
      }
      if (active()) {
        if (!raf) {
          last = 0;
          raf = requestAnimationFrame(tick);
        }
      } else {
        cancelAnimationFrame(raf);
        raf = 0;
        last = 0;
        if (!document.hidden) draw();
      }
    };
    const layout = () => {
      layoutRaf = 0;
      if (disposed) return;
      geometry = measure(main);
      heroClient = null;
      if (!geometry) {
        scene.style.display = "none";
        return;
      }
      const g = geometry;
      scene.style.display = "";
      Object.assign(scene.style, {
        left: `${g.bounds.left}px`,
        top: `${g.bounds.top}px`,
        width: `${g.bounds.width}px`,
        height: `${g.bounds.height}px`,
      });
      // Mask the composited, already-blurred scene. Canvas masks alone cannot stop
      // CSS bloom from reaching a hard crop at the viewport or section boundary.
      const x0 = -g.bounds.left,
        x1 = g.width - g.bounds.left;
      const y0 = g.hero.top - g.bounds.top,
        y1 = y0 + g.hero.height;
      const side = g.mobile ? 22 : 42;
      const finalMask = `linear-gradient(to right,transparent ${x0}px,#000 ${x0 + side}px,#000 ${x1 - side}px,transparent ${x1}px),linear-gradient(to bottom,transparent ${y0}px,#000 ${y0 + 32}px,#000 ${y1 - 80}px,transparent ${y1}px)`;
      scene.style.maskImage = finalMask;
      scene.style.webkitMaskImage = finalMask;
      const dpr = Math.min(window.devicePixelRatio || 1, g.mobile ? 1.3 : 1.45);
      sizeLayer(fine, g.bounds, dpr);
      sizeLayer(bloom, g.bounds, g.mobile ? 0.5 : 0.58);
      sizeLayer(haze, g.bounds, 0.32);
      sizeLayer(fineMask, g.bounds, dpr);
      sizeLayer(fogMask, g.bounds, bloom.scale);
      buildMask(fineMask, g, true);
      buildMask(fogMask, g, false);
      rays = wisps(g.mobile ? 36 : 64);
      if (g.phone) {
        const phone = g.phone,
          width = Math.min(g.mobile ? 470 : 670, g.width * 1.05),
          height = phone.height + 180;
        Object.assign(phoneElement.style, {
          display: "",
          left: `${phone.left + phone.width * 0.5 - width * 0.5}px`,
          top: `${phone.top - 90}px`,
          width: `${width}px`,
          height: `${height}px`,
        });
      } else phoneElement.style.display = "none";
      draw();
      sync();
    };
    const queueLayout = () => {
      if (!disposed && !layoutRaf) layoutRaf = requestAnimationFrame(layout);
    };
    const leave = () => {
      hover.targetAmount = 0;
      hover.targetX = 0;
      hover.targetY = 0;
    };
    const move = (event: PointerEvent) => {
      const g = geometry;
      if (!g || !finePointer.matches || motion.matches || event.pointerType === "touch") return;
      if (!heroClient) heroClient = main.querySelector<HTMLElement>(".bf-hero")!.getBoundingClientRect();
      const p = { x: event.clientX - heroClient.left + g.hero.left, y: event.clientY - heroClient.top + g.hero.top };
      if (p.y < g.hero.top || p.y > g.hero.top + g.hero.height) {
        leave();
        return;
      }
      // A weighted mean avoids jumping between sample indices as the pointer crosses lanes.
      let weight = 0,
        along = 0,
        nearbyX = 0,
        nearbyY = 0,
        nearest = Infinity;
      for (const base of g.centre) {
        const distance = Math.hypot(base.x - p.x, base.y - p.y),
          w = Math.exp(-Math.pow(distance / 320, 2) * 0.5);
        weight += w;
        along += base.t * w;
        nearbyX += base.x * w;
        nearbyY += base.y * w;
        nearest = Math.min(nearest, distance);
      }
      hover.targetT = weight ? along / weight : 0.57;
      hover.targetAmount = 1 - smooth(140, 440, nearest);
      hover.targetX = clamp((p.x - nearbyX / (weight || 1)) / 180, -1, 1) * 12;
      hover.targetY = clamp((p.y - nearbyY / (weight || 1)) / 180, -1, 1) * 8;
    };
    // Scrolling only invalidates pointer coordinates. Geometry and canvases move natively.
    const scroll = () => {
      heroClient = null;
      leave();
    };
    const observer = new ResizeObserver(queueLayout);
    observer.observe(main);
    main
      .querySelectorAll<HTMLElement>(
        "[data-energy-tone],[data-ribbon-turn],.bf-hero-pitch,.bf-proof-strip,.bf-proof-phone",
      )
      .forEach((el) => observer.observe(el));
    const intersection = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === scene) heroVisible = entry.isIntersecting;
          else if (entry.target === phoneElement) phoneVisible = entry.isIntersecting;
        }
        sync();
      },
      { rootMargin: "120px 0px" },
    );
    intersection.observe(scene);
    intersection.observe(phoneElement);
    main.addEventListener("pointermove", move, { passive: true });
    main.addEventListener("pointerleave", leave, { passive: true });
    window.addEventListener("scroll", scroll, { passive: true });
    window.addEventListener("resize", queueLayout, { passive: true });
    main.addEventListener("load", queueLayout, true);
    document.addEventListener("visibilitychange", sync);
    motion.addEventListener("change", sync);
    finePointer.addEventListener("change", sync);
    document.fonts?.ready.then(queueLayout);
    queueLayout();
    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      cancelAnimationFrame(layoutRaf);
      observer.disconnect();
      intersection.disconnect();
      main.removeEventListener("pointermove", move);
      main.removeEventListener("pointerleave", leave);
      window.removeEventListener("scroll", scroll);
      window.removeEventListener("resize", queueLayout);
      main.removeEventListener("load", queueLayout, true);
      document.removeEventListener("visibilitychange", sync);
      motion.removeEventListener("change", sync);
      finePointer.removeEventListener("change", sync);
    };
  }, [mainRef]);
  return (
    <div className="dk-energy-flow" aria-hidden="true">
      <style>{energyRibbonStyles}</style>
      <div ref={sceneRef} className="dk-energy-scene">
        <canvas ref={hazeRef} className="dk-energy-haze" />
        <canvas ref={bloomRef} className="dk-energy-bloom" />
        <canvas ref={fineRef} className="dk-energy-fine" />
      </div>
      <div ref={phoneRef} className="dk-energy-phone" />
    </div>
  );
}

function RibbonTurn() {
  return <div className="bf-ribbon-turn" data-ribbon-turn data-energy-tone="light" aria-hidden="true" />;
}

const STORY_SELECTION = [
  {
    id: "brian-michael",
    result: "500K streams on one song",
    thumb:
      "https://i.vimeocdn.com/video/2104113225-a299b739d6333fdf51a64d29c61da1139b3d6092ae3b85e6bc38c3438e551dde-d_640x360?region=us",
  },
  {
    id: "kaylee-bell",
    result: "31M TikTok views",
    thumb:
      "https://i.vimeocdn.com/video/2104113692-72cfeb6a306920752ac724fed47c958b2dab01e40705a88237e4502a006b6a91-d_640x360?region=us",
  },
  {
    id: "telco",
    result: "100K streams on a first release",
    thumb:
      "https://i.vimeocdn.com/video/1023819689-9b225d870a99e5974c470caaf06a2f007e8c2e181822f00690eb02c7c95695c8-d_640x360?region=us",
  },
  {
    id: "emerger",
    result: "100K plays on a debut",
    thumb:
      "https://i.vimeocdn.com/video/1082768634-5f4504b0d11fde080a273db0e437c8eef23de62ba47e5313d72b48db71cc95d9-d_640x360?region=us",
  },
];

type OpenStory = (id: string, trigger: HTMLButtonElement) => void;

// Reuse the Academy's public artwork and keep its audience claim attributed to DK-MBA.
function AcademyTrust() {
  const portraits = [
    "maoli-K77omo2y.jpg",
    "artist-bw-Bs5ImGoz.jpeg",
    "artist-cap-tw6Qe-os.jpg",
    "artist-blonde-CtNv3j3l.jpg",
  ];
  return (
    <div className="bf-academy-trust">
      <div className="bf-trust-portraits" aria-hidden="true">
        {portraits.map((file) => (
          <img
            key={file}
            src={`https://www.dk-mba.com/assets/${file}`}
            alt=""
            width={28}
            height={28}
            decoding="async"
          />
        ))}
      </div>
      <p>
        DK-MBA is trusted by <strong>40,000+ musicians</strong>
      </p>
    </div>
  );
}

function IndustryProof() {
  return (
    <div className="bf-industry-proof">
      <svg width="0" height="0" aria-hidden="true" style={{ position: "absolute" }}>
        <defs>
          <filter id="bf-tidal-knockout" colorInterpolationFilters="sRGB">
            <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  -.2126 -.7152 -.0722 1 0" />
          </filter>
        </defs>
      </svg>
      <p>Working directly with artists and teams across the industry</p>
      <ul aria-label="Music industry organisations">
        <li className="bf-industry-warner">
          <svg viewBox="100 150 520 380" fill="currentColor" role="img" aria-label="Warner Music">
            <path d="m 160.22381,179.69515 c 33.33452,-13.24311 71.17106,3.78755 84.83683,38.18595 l 79.85171,200.99651 c 13.66576,34.39837 -2.16811,72.75116 -35.50262,85.99428 -33.33452,13.2431 -71.17288,-3.78686 -84.83863,-38.18523 l -79.85171,-200.9965 c -13.66575,-34.39839 2.1699,-72.75191 35.50442,-85.99501 z" />
            <path d="m 330.07343,181.88379 c 33.33452,-13.24311 71.17179,3.78939 84.83754,38.18776 l 79.3426,199.71502 c 13.66576,34.39838 -2.1681,72.75118 -35.50261,85.99428 -33.33451,13.24311 -71.17106,-3.78757 -84.83682,-38.18594 l -79.3426,-199.71502 c -13.66574,-34.39838 2.16739,-72.75301 35.50189,-85.9961 z" />
            <path d="m 495.83858,181.2502 c 33.3345,-13.24311 71.17106,3.78757 84.83681,38.18595 l 18.81889,47.36942 c 13.66575,34.39838 -2.1681,72.75119 -35.50261,85.99429 -33.3345,13.2431 -71.17288,-3.78685 -84.83865,-38.18523 L 460.33415,267.2452 c -13.66576,-34.39838 2.16992,-72.7519 35.50443,-85.995 z" />
          </svg>
        </li>
        <li className="bf-industry-columbia">
          <img
            src="https://www.dk-mba.com/images/labels/columbia.png"
            alt="Columbia Records"
            width={76}
            height={56}
            decoding="async"
          />
        </li>
        <li className="bf-industry-cinq">
          <img
            src="https://www.dk-mba.com/images/labels/cinq.png"
            alt="Cinq Music Group"
            width={76}
            height={56}
            decoding="async"
          />
        </li>
        <li className="bf-industry-tidal">
          <img
            src="https://www.dk-mba.com/images/labels/tidal.webp"
            alt="Tidal"
            width={65}
            height={44}
            decoding="async"
          />
        </li>
        <li className="bf-industry-spotify">
          <img
            src="https://www.dk-mba.com/images/labels/spotify.png?v=2026-02-02"
            alt="Spotify"
            width={46}
            height={46}
            decoding="async"
          />
        </li>
      </ul>
    </div>
  );
}

function ArtistProofStrip({ onPlay }: { onPlay: OpenStory }) {
  return (
    <div className="bf-proof-strip" aria-label="Artist stories">
      {[
        { id: "brian-michael", name: "Brian Michael", result: "500K streams on one song", face: "brian" },
        { id: "kaylee-bell", name: "Kaylee Bell", result: "31M TikTok views", face: "kaylee" },
      ].map((artist) => (
        <button
          key={artist.id}
          className="bf-proof-tile"
          type="button"
          onClick={(event) => onPlay(artist.id, event.currentTarget)}
          aria-label={`Watch ${artist.name}'s story`}
        >
          <span className={`bf-proof-face bf-face-${artist.face}`} role="img" aria-label={artist.name} />
          <span>
            <span className="bf-proof-name">{artist.name}</span>
            <span className="bf-proof-result">{artist.result}</span>
            <span className="bf-proof-watch">
              <Play aria-hidden fill="currentColor" /> Watch the story
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}

const kickstartJourneyV4Styles = `
.bf-kickstart-journey.bf-kj4{margin:28px 0 0;max-width:650px;padding:0;font-family:Geist,Inter,system-ui,sans-serif;color:#23334a}
.bf-kj4 figcaption{display:flex;justify-content:space-between;align-items:center;gap:16px;margin:0;font-size:15px;font-weight:600;line-height:1.5;color:#0071e3}
.bf-kj4 figcaption:before{display:none}
.bf-kj4 figcaption>span{font-size:14px;font-weight:400;color:#69788c;white-space:nowrap}
.bf-kj4-art{position:relative;height:183px;margin:5px -8px 0;isolation:isolate}
.bf-kj4-art svg{position:absolute;inset:0;display:block;width:100%;height:100%;overflow:visible}
.bf-kj4-art .bf-kj4-mobile{display:none}
.bf-kj4-landmark{position:absolute;z-index:2;font-size:14px;line-height:1.35;font-weight:500;letter-spacing:-.015em;color:#64778e;transition:color .3s,opacity .3s;pointer-events:none}
.bf-kj4-landmark-start{top:18px;left:2.7%;max-width:100px}
.bf-kj4-landmark-work{top:5px;left:28%;max-width:130px;color:#0071e3}
.bf-kj4-landmark-review{bottom:1px;left:32%;padding:0 4px;background:#fff;color:#0071e3}
.bf-kj4-art[data-stage="0"] .bf-kj4-landmark-start{color:#0071e3}
.bf-kj4-art[data-stage="2"] .bf-kj4-landmark{opacity:.7}
.bf-kj4-track{fill:none;stroke-linecap:round;stroke-linejoin:round}
.bf-kj4-work-bed{transition:opacity .35s}
.bf-kj4-art[data-stage="0"] .bf-kj4-work-bed,.bf-kj4-art[data-stage="2"] .bf-kj4-work-bed{opacity:.46}
.bf-kj4-source,.bf-kj4-future{transition:opacity .35s}
.bf-kj4-art[data-stage="0"] .bf-kj4-source{opacity:1}
.bf-kj4-art[data-stage="1"] .bf-kj4-source,.bf-kj4-art[data-stage="2"] .bf-kj4-source{opacity:.65}
.bf-kj4-art[data-stage="2"] .bf-kj4-future{opacity:1}
.bf-kj4-future{opacity:.65}
.bf-kj4-current-ring{fill:none;stroke:#0071e3;stroke-width:1;opacity:0;transform-box:fill-box;transform-origin:center;transform:scale(.82);transition:opacity .3s,transform .3s}
.bf-kj4-marker[data-current="true"] .bf-kj4-current-ring{opacity:.38;transform:scale(1)}
.bf-kj4-light{fill:none;stroke-linecap:round;stroke-dasharray:5 95;stroke-dashoffset:74;opacity:0;animation:bf-kj4-travel 12s linear infinite;animation-play-state:paused}
.bf-kj4[data-running="true"] .bf-kj4-light{animation-play-state:running}
@keyframes bf-kj4-travel{0%,8%{stroke-dashoffset:100;opacity:0}16%{opacity:.95}82%{opacity:.95}92%,100%{stroke-dashoffset:0;opacity:0}}
.bf-kj4-tabs{display:grid;grid-template-columns:31% 48% 21%;align-items:stretch;margin-top:4px;gap:0;border-bottom:1px solid #dfe7f0}
.bf-kj4-tab{appearance:none;position:relative;min-width:0;border:0;background:transparent;color:#66768a;text-align:center;padding:5px 8px 12px;cursor:pointer;transition:color .2s}
.bf-kj4-tab:after{content:'';position:absolute;bottom:-1px;left:14px;right:14px;height:2px;background:#0071e3;transform:scaleX(0);transform-origin:center;transition:transform .25s}
.bf-kj4-tab[aria-selected="true"]{color:#0071e3}
.bf-kj4-tab[aria-selected="true"]:after{transform:scaleX(1)}
.bf-kj4-tab:hover{color:#0071e3}
.bf-kj4-tab:focus-visible{outline:2px solid #0071e3!important;outline-offset:4px!important;border-radius:3px}
.bf-kj4-stage{display:block;font-size:23px;line-height:1.1;letter-spacing:-.045em;font-weight:600;margin-bottom:5px}
.bf-kj4-tab-label{display:block;max-width:140px;margin:0 auto;font-size:15px;line-height:1.35;font-weight:500;letter-spacing:-.02em}
.bf-kj4-stage-link{stroke:#b9cce1;stroke-width:1;stroke-dasharray:2 4;fill:none}
.bf-kj4-detail{padding:15px 0 0;min-height:108px;outline-offset:5px}
.bf-kj4-detail strong{display:block;font-size:16px;line-height:1.4;font-weight:600;letter-spacing:-.02em;color:#243954}
.bf-kj4-detail p{font-size:15px!important;line-height:1.65!important;margin:5px 0 0!important;max-width:590px;color:#647184}
@media(max-width:760px){
  .bf-kickstart-journey.bf-kj4{margin-top:25px}
  .bf-kj4 figcaption{font-size:16px;gap:10px}
  .bf-kj4 figcaption>span{font-size:13px}
  .bf-kj4-art{height:186px;margin:9px -3px 0}
  .bf-kj4-art .bf-kj4-desktop{display:none}
  .bf-kj4-art .bf-kj4-mobile{display:block}
  .bf-kj4-landmark{font-size:14px;line-height:1.35}
  .bf-kj4-landmark-start{top:10px;left:0;max-width:100px}
  .bf-kj4-landmark-work{top:0;left:29%;max-width:130px}
  .bf-kj4-landmark-review{bottom:0;left:28%}
  .bf-kj4-tabs{grid-template-columns:28% 44% 28%;margin-top:8px}
  .bf-kj4-tab{padding:8px 5px 12px;min-height:85px}
  .bf-kj4-tab:after{left:7px;right:7px}
  .bf-kj4-stage{font-size:24px;margin-bottom:6px}
  .bf-kj4-tab-label{font-size:16px;line-height:1.3}
  .bf-kj4-detail{padding-top:16px;min-height:154px}
  .bf-kj4-detail strong{font-size:17px}
  .bf-kj4-detail p{font-size:16px!important;line-height:1.65!important;margin-top:7px!important}
}
@media(prefers-reduced-motion:reduce){
  .bf-kj4 *{transition:none!important}
  .bf-kj4-light{animation:none!important;opacity:0}
}
`;

function KickstartJourneyGraphic() {
  const [stage, setStage] = React.useState(1);
  const [running, setRunning] = React.useState(false);
  const figureRef = React.useRef<HTMLElement | null>(null);
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const instanceId = `bf-kj4-${React.useId().replace(/:/g, "")}`;
  const stages = [
    {
      marker: "A",
      label: "Where you are now",
      title: "Start with what you have.",
      detail:
        "Your music, your ideas and what you’ve already tried give us a starting point. Together, we work out what deserves your attention first.",
    },
    {
      marker: "A.5",
      label: "Momentum taking shape",
      title: "Put a direction into practice.",
      detail:
        "Find the content that makes people care. We help you shape it before you film, get it in front of potential fans and use the response to work out what to make next.",
    },
    {
      marker: "B",
      label: "Keep building",
      title: "Carry the understanding forward.",
      detail:
        "Your kickstart covers the first stretch. The aim is to leave knowing which ideas deserve more work, so you can keep developing them after your time with us.",
    },
  ];

  React.useEffect(() => {
    const figure = figureRef.current;
    if (!figure) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let inView = false;
    const syncMotion = () => setRunning(inView && !reducedMotion.matches && document.visibilityState === "visible");
    const observer = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        syncMotion();
      },
      { threshold: 0.15 },
    );
    observer.observe(figure);
    reducedMotion.addEventListener("change", syncMotion);
    document.addEventListener("visibilitychange", syncMotion);
    return () => {
      observer.disconnect();
      reducedMotion.removeEventListener("change", syncMotion);
      document.removeEventListener("visibilitychange", syncMotion);
    };
  }, []);

  const selectWithKeyboard = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index;
    if (event.key === "ArrowRight") next = (index + 1) % stages.length;
    else if (event.key === "ArrowLeft") next = (index + stages.length - 1) % stages.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = stages.length - 1;
    else return;
    event.preventDefault();
    setStage(next);
    tabRefs.current[next]?.focus();
  };

  const routeIllustration = (mobile: boolean) => {
    const width = mobile ? 360 : 640;
    const height = 200;
    const start = mobile ? { x: 51, y: 137 } : { x: 99, y: 141 };
    const momentum = mobile ? { x: 180, y: 94 } : { x: 352, y: 92 };
    const future = mobile ? { x: 316, y: 142 } : { x: 574, y: 132 };
    const workPath = mobile
      ? "M51 137 C81 137 77 67 111 67 C143 67 138 130 159 130 C178 130 158 94 180 94"
      : "M99 141 C150 141 133 64 183 64 C236 64 245 120 290 120 C319 120 318 92 352 92";
    const futurePath = mobile
      ? "M180 94 C229 94 230 153 268 153 C294 153 278 99 300 99 C317 99 305 142 316 142 C331 142 336 132 351 127"
      : "M352 92 C413 92 401 157 461 157 C508 157 496 71 541 71 C579 71 550 132 574 132 C602 132 611 111 629 104";
    const feedbackPath = mobile
      ? "M155 128 C178 162 145 185 120 165 C104 152 111 125 132 113"
      : "M290 120 C309 159 269 185 235 165 C213 151 222 122 244 111";
    const sourcePath = mobile
      ? "M13 75 C45 75 16 110 51 137 M10 172 C31 178 39 164 51 137 M23 187 C46 181 38 152 51 137"
      : "M23 75 C84 75 47 111 99 141 M24 175 C64 191 76 166 99 141 M43 195 C78 186 71 158 99 141";
    const localId = `${instanceId}-${mobile ? "mobile" : "desktop"}`;
    return (
      <svg
        className={mobile ? "bf-kj4-mobile" : "bf-kj4-desktop"}
        viewBox={`0 0 ${width} ${height}`}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id={`${localId}-bed`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#eaf4ff" stopOpacity=".15" />
            <stop offset=".55" stopColor="#d7eaff" stopOpacity=".78" />
            <stop offset="1" stopColor="#edf6ff" stopOpacity=".3" />
          </linearGradient>
          <linearGradient id={`${localId}-ribbon`} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor="#b4d8ff" />
            <stop offset=".5" stopColor="#0071e3" />
            <stop offset="1" stopColor="#5ba8f4" />
          </linearGradient>
          <marker id={`${localId}-arrow`} markerWidth="8" markerHeight="8" refX="5" refY="4" orient="auto">
            <path
              d="M2 1 L5 4 L2 7"
              fill="none"
              stroke="#70a0d4"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </marker>
        </defs>

        {/* The offset surface and edge give the working route a physical shape. */}
        <g className="bf-kj4-work-bed">
          <path
            d={workPath}
            className="bf-kj4-track"
            transform="translate(0 6)"
            stroke="#bacde4"
            strokeWidth="19"
            opacity=".2"
          />
          <path d={workPath} className="bf-kj4-track" stroke={`url(#${localId}-bed)`} strokeWidth="38" />
          <path d={workPath} className="bf-kj4-track" stroke="#d2e7fc" strokeWidth="17" />
          <path d={workPath} className="bf-kj4-track" stroke="#f7fbff" strokeWidth="13" />
        </g>

        {/* Separate music and idea paths meet at the artist's starting point. */}
        <g className="bf-kj4-source">
          <path d={sourcePath} className="bf-kj4-track" stroke="#8db6e2" strokeWidth="1.4" />
          <g
            transform={mobile ? "translate(7 71)" : "translate(16 71)"}
            fill="none"
            stroke="#0071e3"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M0 4 L0 -4 M5 9 L5 -9 M10 5 L10 -5 M15 13 L15 -13 M20 8 L20 -8 M25 3 L25 -3" />
          </g>
          <circle cx={mobile ? 10 : 24} cy="172" r="3.5" fill="#c7ddf4" />
          <circle cx={mobile ? 23 : 43} cy={mobile ? 187 : 195} r="2.5" fill="#a8c8ec" />
        </g>

        {/* The return loop shows reviewing and refining, rather than a growth axis. */}
        <path
          d={feedbackPath}
          className="bf-kj4-track"
          stroke="#9dbfe4"
          strokeWidth="1.5"
          strokeDasharray="3 5"
          markerEnd={`url(#${localId}-arrow)`}
        />
        <path d={workPath} className="bf-kj4-track" stroke={`url(#${localId}-ribbon)`} strokeWidth="5" />
        <path d={workPath} className="bf-kj4-track" stroke="#0064ca" strokeWidth="1.3" opacity=".7" />

        {/* One measured moving highlight, shared by the fine core and its tail. */}
        <path d={workPath} pathLength="100" className="bf-kj4-light" stroke="#90c7ff" strokeWidth="7" />
        <path d={workPath} pathLength="100" className="bf-kj4-light" stroke="#fff" strokeWidth="2.2" />

        <g className="bf-kj4-future">
          <path d={futurePath} className="bf-kj4-track" transform="translate(0 3)" stroke="#edf3fa" strokeWidth="9" />
          <path
            d={futurePath}
            className="bf-kj4-track"
            stroke="#8baacb"
            strokeWidth="2"
            strokeDasharray="4 6"
            markerEnd={`url(#${localId}-arrow)`}
          />
        </g>

        {/* A music performance takes shape as content; these are drawn artifacts,
            not playable videos or screenshots of invented software. */}
        <g transform={`translate(${mobile ? 111 : 183} ${mobile ? 67 : 64})`}>
          <g transform="translate(4 -7) rotate(9)">
            <rect x="-12" y="-18" width="25" height="36" rx="2" fill="#eef5fd" stroke="#bad2ed" strokeWidth="1" />
          </g>
          <g transform="translate(-2 -6) rotate(-7)">
            <rect x="-13" y="-19" width="26" height="38" rx="2.5" fill="#fff" stroke="#5e9fdf" strokeWidth="1.3" />
            <path d="M-7 -19V19 M7 -19V19" fill="none" stroke="#c5dbf2" strokeWidth="1" />
            {[-13, -4, 5, 14].map((y) => (
              <g key={y} fill="#a8c9eb">
                <rect x="-10.5" y={y} width="2" height="3" rx=".5" />
                <rect x="8.5" y={y} width="2" height="3" rx=".5" />
              </g>
            ))}
            <path d="M-4 0V5 M0 -7V10 M4 -3V7" stroke="#0071e3" strokeWidth="1.5" strokeLinecap="round" />
          </g>
        </g>

        {/* The pencilled crop marks and returning arrow express another pass. */}
        <g transform={`translate(${mobile ? 159 : 272} ${mobile ? 151 : 156})`}>
          <path
            d="M-12 -12H-4 M-12 -12V-4 M12 -12H4 M12 -12V-4 M-12 6V14H-4 M12 6V14H4"
            fill="none"
            stroke="#4d8fce"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          <path d="M-6 2C-3 -7 1 8 6 -1" fill="none" stroke="#0071e3" strokeWidth="1.7" strokeLinecap="round" />
        </g>

        {/* These quiet leaders make each plotted stage belong to its control. */}
        {[start, momentum, future].map((point, index) => (
          <path
            key={`leader-${index}`}
            d={`M${point.x} ${point.y + (index === 1 ? 23 : 13)} V198`}
            className="bf-kj4-stage-link"
            opacity={stage === index ? ".7" : ".35"}
          />
        ))}

        {[start, momentum, future].map((point, index) => (
          <g key={index} className="bf-kj4-marker" data-current={stage === index}>
            <circle cx={point.x} cy={point.y} r={index === 1 ? 26 : 19} className="bf-kj4-current-ring" />
            {index === 1 && <circle cx={point.x} cy={point.y} r="20" fill="#dcecff" />}
            <circle
              cx={point.x}
              cy={point.y}
              r={index === 1 ? 13 : 9}
              fill={index === 1 ? "#0071e3" : "#fff"}
              stroke={index === 2 ? "#8baacb" : "#0071e3"}
              strokeWidth={index === 1 ? 0 : 2}
            />
            <circle
              cx={point.x}
              cy={point.y}
              r={index === 1 ? 4 : 3}
              fill={index === 1 ? "#fff" : index === 2 ? "#8baacb" : "#0071e3"}
            />
          </g>
        ))}
      </svg>
    );
  };

  return (
    <figure
      ref={figureRef}
      className="bf-kickstart-journey bf-kj4"
      data-running={running}
      aria-labelledby={`${instanceId}-caption`}
    >
      <style>{kickstartJourneyV4Styles}</style>
      <figcaption id={`${instanceId}-caption`}>
        Your Kickstart: A → A.5 <span>Select a stage</span>
      </figcaption>
      <div className="bf-kj4-art" data-stage={stage}>
        {routeIllustration(false)}
        {routeIllustration(true)}
        <span className="bf-kj4-landmark bf-kj4-landmark-start" aria-hidden="true">
          Your music
          <br />
          and ideas
        </span>
        <span className="bf-kj4-landmark bf-kj4-landmark-work" aria-hidden="true">
          Put it into practice
        </span>
        <span className="bf-kj4-landmark bf-kj4-landmark-review" aria-hidden="true">
          Review together
        </span>
      </div>
      <div className="bf-kj4-tabs" role="tablist" aria-label="Explore the artist kickstart journey">
        {stages.map((item, index) => (
          <button
            key={item.marker}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={`${instanceId}-tab-${index}`}
            className="bf-kj4-tab"
            aria-selected={stage === index}
            aria-controls={`${instanceId}-panel`}
            tabIndex={stage === index ? 0 : -1}
            onClick={() => setStage(index)}
            onKeyDown={(event) => selectWithKeyboard(event, index)}
          >
            <span className="bf-kj4-stage">{item.marker}</span>
            <span className="bf-kj4-tab-label">{item.label}</span>
          </button>
        ))}
      </div>
      <div
        id={`${instanceId}-panel`}
        className="bf-kj4-detail"
        role="tabpanel"
        aria-labelledby={`${instanceId}-tab-${stage}`}
        tabIndex={0}
      >
        <strong>{stages[stage].title}</strong>
        <p>{stages[stage].detail}</p>
      </div>
    </figure>
  );
}

function Recognition() {
  return (
    <section
      className="bf-section bf-light bf-recognition bf-recognition-offer"
      data-energy-tone="light"
      aria-labelledby="recognition-title"
    >
      <div className="bf-wrap">
        <div>
          <h2 id="recognition-title" className="bf-heading">
            A <Highlight>kickstart</Highlight> for the music you’re already making.
          </h2>
          <p className="bf-recognition-lead">
            You want people to hear a song and care enough to come back for the next one. The kickstart focuses on that
            first stretch, from the music you have now to momentum you can build on.
          </p>
          <KickstartJourneyGraphic />
        </div>
        <SpotifyPhone />
      </div>
    </section>
  );
}

type WorkshopStage = {
  key: string;
  label: string;
  question: string;
  guidance: string;
};
const WORKSHOP_STAGES: WorkshopStage[] = [
  {
    key: "angle",
    label: "The angle",
    question: "Who is this song for?",
    guidance:
      "Brian’s on-screen hook connects the song to moms who filled both parent roles. The angle gives a specific person a reason to care.",
  },
  {
    key: "opening",
    label: "The opening",
    question: "What would make them stop?",
    guidance:
      "Work from the strongest line or performance moment. In an opening like this, make the connection clear while the song is being performed.",
  },
  {
    key: "shoot",
    label: "The shoot",
    question: "How would you film this?",
    guidance:
      "Brian’s clip uses an outdoor performance at a microphone. Work through a practical setting and framing that keep attention on the song.",
  },
  {
    key: "next",
    label: "The next version",
    question: "What deserves another version?",
    guidance:
      "After publishing, review the actual response with the team. Keep the part that connects and change the approach where the idea needs more work.",
  },
];

const contentWorkshopStyles = `
.dk-workshop{position:relative;isolation:isolate;width:100%;max-width:650px;min-width:0;padding:18px 18px 16px;border:1px solid #bedaff24;border-top-color:#d8eaff40;border-radius:15px;background:linear-gradient(143deg,#283a516b,#141f30c7 48%,#111b2be6);box-shadow:inset 0 1px 0 #ffffff0b,0 24px 65px #00000038,0 3px 10px #00000014;-webkit-backdrop-filter:blur(17px);backdrop-filter:blur(17px);color:#fff;font-family:Geist,Inter,system-ui,sans-serif;box-sizing:border-box}
.dk-workshop *{box-sizing:border-box}
.dk-workshop .dk-workshop-header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin:0 2px 15px}
.dk-workshop .dk-workshop-header>h3{font-size:16px;font-weight:500;line-height:1.4;letter-spacing:-.015em;color:#e4ecf7;margin:0}
.dk-workshop .dk-workshop-brand{display:flex;align-items:center;gap:8px;flex:none;color:#63aaff}
.dk-workshop .dk-workshop-brand svg{display:block;width:31px;height:20px}
.dk-workshop .dk-workshop-brand span{font-size:11px;line-height:1;font-weight:600;letter-spacing:-.025em;color:#bacce2}
.dk-workshop .dk-workshop-sheet-stack{position:relative;margin:0 0 16px}
.dk-workshop .dk-workshop-sheet-stack:before{content:'';position:absolute;inset:7px 8px -5px;border-radius:9px;background:#d7e4f0;transform:rotate(.8deg);opacity:.48;z-index:-1;box-shadow:0 6px 13px #0000001f}
.dk-workshop .dk-workshop-sheet{position:relative;display:grid;grid-template-columns:138px minmax(0,1fr);align-content:center;align-items:start;gap:21px;min-height:326px;height:auto;min-width:0;padding:23px 22px;border-radius:9px;background:#f7f9fc;color:#18263b;box-shadow:0 3px 12px #00000019;outline-offset:4px}
.dk-workshop .dk-workshop-sheet:focus-visible{outline:2px solid #82b9ff}
.dk-workshop .dk-workshop-example{position:relative;margin:0;min-width:0}
.dk-workshop .dk-workshop-photo{position:relative;aspect-ratio:194/262;overflow:hidden;border-radius:7px;background:#233b55;box-shadow:0 5px 17px #10244221}
.dk-workshop .dk-workshop-photo img{position:absolute;display:block;width:487.629%;max-width:none;height:auto;left:-194.33%;top:-53.817%;margin:0;user-select:none;pointer-events:none}
.dk-workshop .dk-workshop-frame{position:absolute;inset:9px;pointer-events:none;opacity:0}
.dk-workshop[data-stage=shoot] .dk-workshop-frame{opacity:1}
.dk-workshop .dk-workshop-frame:before,.dk-workshop .dk-workshop-frame:after{content:'';position:absolute;inset:0;border-color:#eaf4ff;border-style:solid;border-width:1.5px 0}
.dk-workshop .dk-workshop-frame:after{inset:0 0;border-width:0 1.5px;clip-path:polygon(0 0,16% 0,16% 14%,84% 14%,84% 0,100% 0,100% 100%,84% 100%,84% 86%,16% 86%,16% 100%,0 100%)}
.dk-workshop .dk-workshop-example figcaption{font-size:11px;line-height:1.5;color:#74839a;margin-top:10px}
.dk-workshop .dk-workshop-example figcaption strong{display:block;color:#304660;font-size:12px;font-weight:500}
.dk-workshop .dk-workshop-writing{min-width:0}
.dk-workshop .dk-workshop-question{font-size:25px;line-height:1.16;font-weight:600;letter-spacing:-.035em;color:#132641;max-width:340px;margin:0;min-height:58px}
.dk-workshop .dk-workshop-guidance{font-size:13px;line-height:1.65;color:#5a6b82;min-height:65px;margin:11px 0 17px}
.dk-workshop .dk-workshop-example-label{font-size:11px;line-height:1.4;color:#0069d3;margin:0 0 8px;font-weight:500}
.dk-workshop .dk-workshop-hook{font-size:15px;line-height:1.5;color:#24405f;font-weight:500;letter-spacing:-.015em;margin:0}
.dk-workshop .dk-workshop-hook mark{color:inherit;background:#0071e313;box-shadow:0 2px 0 #0071e36b;padding:1px 0;-webkit-box-decoration-break:clone;box-decoration-break:clone}
.dk-workshop .dk-workshop-prompts{display:flex;flex-direction:column;gap:11px;min-height:88px;margin:0}
.dk-workshop .dk-workshop-prompts p{font-size:14px;line-height:1.45;color:#24405f;font-weight:500;margin:0;padding-left:15px;position:relative}
.dk-workshop .dk-workshop-prompts p:before{content:'';position:absolute;left:0;top:7px;width:5px;height:5px;border-radius:50%;background:#0071e3}
.dk-workshop .dk-workshop-review{margin:0;display:flex;flex-direction:column;gap:10px}
.dk-workshop .dk-workshop-review>div{display:flex;align-items:baseline;gap:10px}
.dk-workshop .dk-workshop-review dt{font-size:11px;font-weight:500;color:#0071e3;min-width:36px;margin:0}
.dk-workshop .dk-workshop-review dd{font-size:13px;line-height:1.5;color:#24405f;margin:0}
.dk-workshop .dk-workshop-tabs{display:grid;grid-template-columns:.9fr .99fr .9fr 1.25fr;gap:4px;padding:4px;border-radius:9px;background:#09111c60;box-shadow:inset 0 1px 4px #00000017}
.dk-workshop .dk-workshop-tab{display:flex;align-items:center;justify-content:center;min-width:0;min-height:41px;margin:0;border:1px solid transparent;border-radius:6px;padding:7px 5px;background:transparent;color:#92a7c1;font:inherit;font-size:12px;font-weight:500;line-height:1.25;letter-spacing:-.02em;cursor:pointer;transition:color .18s,background .18s,border-color .18s}
.dk-workshop .dk-workshop-tab:hover{background:#ffffff08;color:#e0eafb}
.dk-workshop .dk-workshop-tab[aria-selected=true]{background:#0071e3;border-color:#68adff52;color:#fff;box-shadow:0 2px 7px #00000022,inset 0 1px 0 #ffffff1c}
.dk-workshop .dk-workshop-tab:focus-visible{outline:2px solid #9cc9ff;outline-offset:2px}
.dk-workshop .dk-workshop-footer{margin:13px 2px 0;display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:4px 12px}
.dk-workshop .dk-workshop-footer p{font-size:11px;line-height:1.5;color:#98adc7;margin:0}
.dk-workshop .dk-workshop-footer span{font-size:10px;line-height:1.5;color:#7189a8}
@media(max-width:1050px) and (min-width:761px){.dk-workshop{padding:17px 16px 15px}.dk-workshop .dk-workshop-sheet{grid-template-columns:108px minmax(0,1fr);gap:16px;padding:20px 18px}.dk-workshop .dk-workshop-question{font-size:23px;min-height:54px}.dk-workshop .dk-workshop-hook{font-size:14px}.dk-workshop .dk-workshop-guidance{font-size:12px;min-height:80px;margin-bottom:14px}.dk-workshop .dk-workshop-tab{font-size:11px;padding-left:3px;padding-right:3px}}
@media(max-width:760px){.dk-workshop{padding:17px 15px 15px;border-radius:14px}.dk-workshop .dk-workshop-header{margin:0 1px 15px}.dk-workshop .dk-workshop-header>h3{font-size:15px}.dk-workshop .dk-workshop-sheet{height:auto;min-height:344px;grid-template-columns:94px minmax(0,1fr);gap:16px;padding:21px 18px;align-content:start;align-items:center}.dk-workshop .dk-workshop-writing{display:contents}.dk-workshop .dk-workshop-example{grid-column:1;grid-row:1}.dk-workshop .dk-workshop-question{grid-column:2;grid-row:1;font-size:25px;min-height:0;max-width:390px}.dk-workshop .dk-workshop-guidance{grid-column:1 / -1;min-height:69px;margin:0;font-size:13px;line-height:1.65}.dk-workshop .dk-workshop-evidence{grid-column:1 / -1;min-height:100px}.dk-workshop .dk-workshop-hook{font-size:15px;line-height:1.5}.dk-workshop .dk-workshop-example-label{font-size:11px;margin-bottom:8px}.dk-workshop .dk-workshop-example figcaption{font-size:10px;line-height:1.4;margin-top:8px}.dk-workshop .dk-workshop-example figcaption strong{font-size:11px}.dk-workshop .dk-workshop-tabs{gap:2px;padding:3px}.dk-workshop .dk-workshop-tab{font-size:11px;min-height:44px;padding:7px 3px}.dk-workshop .dk-workshop-footer{margin-top:13px}.dk-workshop .dk-workshop-footer p{font-size:10px}.dk-workshop .dk-workshop-footer span{font-size:10px}}
@media(max-width:370px){.dk-workshop{padding:14px 11px}.dk-workshop .dk-workshop-header>h3{font-size:14px}.dk-workshop .dk-workshop-brand{gap:5px}.dk-workshop .dk-workshop-brand svg{width:25px}.dk-workshop .dk-workshop-sheet{grid-template-columns:83px minmax(0,1fr);padding:18px 15px;gap:15px}.dk-workshop .dk-workshop-question{font-size:23px}.dk-workshop .dk-workshop-guidance{min-height:85px}.dk-workshop .dk-workshop-evidence{min-height:110px}.dk-workshop .dk-workshop-tab{font-size:10px}.dk-workshop .dk-workshop-footer{display:block}.dk-workshop .dk-workshop-footer span{display:block;margin-top:3px}}
@media(prefers-reduced-motion:reduce){.dk-workshop .dk-workshop-tab{transition:none}}
`;

function WorkshopEvidence({ stage }: { stage: number }) {
  if (stage === 2)
    return (
      <div className="dk-workshop-evidence">
        <p className="dk-workshop-example-label">Questions before filming</p>
        <div className="dk-workshop-prompts">
          <p>Where could you make the idea work?</p>
          <p>What should the viewer notice?</p>
        </div>
      </div>
    );
  if (stage === 3)
    return (
      <div className="dk-workshop-evidence">
        <p className="dk-workshop-example-label">Ideas for the next version</p>
        <dl className="dk-workshop-review">
          <div>
            <dt>Keep</dt>
            <dd>The connection in the song.</dd>
          </div>
          <div>
            <dt>Try</dt>
            <dd>A different way into the clip.</dd>
          </div>
        </dl>
      </div>
    );
  return (
    <div className="dk-workshop-evidence">
      <p className="dk-workshop-example-label">The on-screen hook in Brian’s clip</p>
      <blockquote className="dk-workshop-hook">
        {stage === 0 ? (
          <>
            To the <mark>moms who played both roles of mom and dad,</mark> this one’s for you.
          </>
        ) : (
          <>
            To the moms who played both roles of mom and dad, <mark>this one’s for you.</mark>
          </>
        )}
      </blockquote>
    </div>
  );
}

/** Actual artist material paired with illustrative guidance, not a client platform. */
function ContentWorkshop({ id = "dk-content-workshop" }: { id?: string }) {
  const [active, setActive] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);
  const stage = WORKSHOP_STAGES[active];
  const activate = (index: number, focus = false) => {
    setActive(index);
    if (focus) tabs.current[index]?.focus();
  };
  const onKey = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = -1;
    if (event.key === "ArrowRight") next = (index + 1) % WORKSHOP_STAGES.length;
    if (event.key === "ArrowLeft") next = (index + WORKSHOP_STAGES.length - 1) % WORKSHOP_STAGES.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = WORKSHOP_STAGES.length - 1;
    if (next !== -1) {
      event.preventDefault();
      activate(next, true);
    }
  };
  return (
    <div className="dk-workshop bf-reactive" data-stage={stage.key}>
      <style>{contentWorkshopStyles}</style>
      <span className="bf-surface-shine" aria-hidden="true" />
      <div className="dk-workshop-header">
        <h3>Inside the content work</h3>
        <div className="dk-workshop-brand" aria-hidden="true">
          <svg viewBox="0 0 31 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M2 8v4m5-7v10m5-13v16m5-15v14m5-11v8m5-7v6" />
          </svg>
          <span>DK</span>
        </div>
      </div>
      <div
        className="dk-workshop-tabs"
        role="tablist"
        aria-label="Explore the content work"
        aria-orientation="horizontal"
      >
        {WORKSHOP_STAGES.map((item, index) => (
          <button
            key={item.key}
            ref={(element) => {
              tabs.current[index] = element;
            }}
            id={`${id}-tab-${item.key}`}
            className="dk-workshop-tab"
            role="tab"
            type="button"
            tabIndex={active === index ? 0 : -1}
            aria-selected={active === index}
            aria-controls={`${id}-panel`}
            onClick={() => activate(index)}
            onKeyDown={(event) => onKey(event, index)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="dk-workshop-sheet-stack">
        <div
          id={`${id}-panel`}
          className="dk-workshop-sheet"
          role="tabpanel"
          aria-labelledby={`${id}-tab-${stage.key}`}
          tabIndex={0}
        >
          <figure className="dk-workshop-example">
            <div className="dk-workshop-photo">
              <img
                src={brianPhoto}
                alt="Brian Michael performing at an outdoor microphone, cropped from the existing clip analytics image"
                width={946}
                height={1920}
                loading="lazy"
              />
              <div className="dk-workshop-frame" aria-hidden="true" />
            </div>
            <figcaption>
              <strong>Brian Michael</strong>Seeing Mama Cry
            </figcaption>
          </figure>
          <div className="dk-workshop-writing">
            <h4 className="dk-workshop-question">{stage.question}</h4>
            <p className="dk-workshop-guidance">{stage.guidance}</p>
            <WorkshopEvidence stage={active} />
          </div>
        </div>
      </div>
      <div className="dk-workshop-results">
        <div>
          <strong>1.1M</strong>
          <span>views on this clip</span>
        </div>
        <div>
          <strong>16,824</strong>
          <span>new followers</span>
        </div>
        <button
          type="button"
          onClick={() => setShowResults(true)}
          aria-label="Open Brian’s original video results screenshot"
        >
          <ArrowUpRight aria-hidden />
          <span>See the results</span>
        </button>
      </div>
      <Dialog open={showResults} onOpenChange={setShowResults}>
        <DialogContent className="bf-content-results-dialog" aria-describedby="bf-content-results-description">
          <DialogTitle>Brian’s original clip results</DialogTitle>
          <p id="bf-content-results-description">The original video analytics shared with DK Artists.</p>
          <img
            src={brianPhoto}
            width={946}
            height={1920}
            alt="Brian Michael’s video analytics: 1,109,351 video views and 16,824 new followers"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

const offerInclusionStyles = `
.bf-offer-v3{background:#101114;color:white;isolation:isolate}
.bf-offer-v3 p{font-size:16px;line-height:1.75;color:#b7c0ce}
.bf-offer-v3 h2,.bf-offer-v3 h3{font-weight:600;letter-spacing:-.045em;line-height:1.15}
.bf-offer-v3 h2{font-size:clamp(38px,4.1vw,56px)}
.bf-offer-v3 h3{font-size:clamp(30px,3.3vw,44px)}
.bf-offer-v3 .bf-content-stage{position:relative;overflow:hidden;padding:76px 0 70px;background:radial-gradient(ellipse at 90% 40%,#122c49 0%,#121c2a 27%,#101114 64%)}
.bf-content-stage:after,.bf-creative-atmosphere:after{content:'';position:absolute;inset:0;pointer-events:none;opacity:.026;background-image:url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20width%3D%27256%27%20height%3D%27256%27%20viewBox%3D%270%200%20256%20256%27%3E%3Cfilter%20id%3D%27grain%27%20x%3D%270%27%20y%3D%270%27%20width%3D%27100%25%27%20height%3D%27100%25%27%20color-interpolation-filters%3D%27sRGB%27%3E%3CfeTurbulence%20type%3D%27fractalNoise%27%20baseFrequency%3D%27.82%27%20numOctaves%3D%271%27%20seed%3D%2717%27%20stitchTiles%3D%27stitch%27%2F%3E%3CfeColorMatrix%20type%3D%27saturate%27%20values%3D%270%27%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%27100%25%27%20height%3D%27100%25%27%20filter%3D%27url%28%23grain%29%27%2F%3E%3C%2Fsvg%3E");background-size:128px 128px;background-repeat:repeat;z-index:0}
.bf-content-stage .bf-wrap{display:grid;grid-template-columns:1fr 1fr;gap:58px;align-items:center}
.bf-content-intro h2{max-width:520px}.bf-content-intro>p{margin-top:22px;max-width:500px}.bf-content-intro>p+p{margin-top:16px}
.bf-content-ambition{display:flex;align-items:center;gap:15px;margin-top:26px;padding:19px 0 0;color:#9babbe;flex-wrap:wrap}
.bf-content-ambition>span{font-size:13px;flex-basis:100%;margin-bottom:1px}.bf-content-ambition strong{font-size:29px;font-weight:500;letter-spacing:-.045em;line-height:1;color:#fff}.bf-content-ambition strong:first-of-type{color:#8191a7;font-size:24px}.bf-content-ambition strong:last-of-type{color:#67b0ff}.bf-content-ambition small{font-size:12px;letter-spacing:0;font-weight:400;margin-left:4px;color:#91a3bc}.bf-content-ambition svg{width:25px;height:25px;stroke-width:1.3;color:#3b83d5}.bf-content-ambition>p{font-size:11px;flex-basis:100%;color:#7d8da5;margin-top:0;line-height:1.5}
.bf-working-stage{background:radial-gradient(ellipse at 12% 50%,#e5edf9 0%,#f2f5f9 35%,#fafafa 75%);color:#132035;padding:76px 0 69px;position:relative}
.bf-working-stage .bf-wrap{display:grid;grid-template-columns:1fr 1fr;gap:72px;align-items:center}.bf-working-stage p{color:#57677b}
.bf-working-copy h3{max-width:450px}.bf-working-copy>p{margin-top:20px;max-width:490px}.bf-working-copy h4{font-size:20px;font-weight:550;letter-spacing:-.025em;margin:25px 0 8px;line-height:1.3}.bf-working-copy h4+p{margin-top:0}
.bf-project-composition{position:relative;isolation:isolate;padding:28px 14px 34px 0}.bf-project-composition:before{content:'';position:absolute;inset:0 -25px;background:radial-gradient(ellipse at 45% 45%,#b8d2f35c,#dae7f400 68%);z-index:-1;filter:blur(16px)}
.bf-project-paper{background:linear-gradient(135deg,#fff,#fafcff);border:1px solid #fff;border-radius:14px;padding:28px 30px 34px;box-shadow:0 3px 5px #294c6c05,0 22px 60px #23436c17;transform:none;position:relative;z-index:1}.bf-project-paper:before{content:'';position:absolute;inset:7px 9px -8px;border-radius:13px;background:#e6edf6;border:1px solid #d9e4f1;transform:rotate(1.2deg);z-index:-2;box-shadow:0 12px 24px #19395b0a}.bf-project-paper:after{content:'';position:absolute;inset:0;background:linear-gradient(135deg,#fff,#fafcff);border-radius:14px;z-index:-1}
.bf-project-paper-head{display:flex;justify-content:space-between;align-items:center;gap:20px;margin-bottom:22px}.bf-project-paper-head img{width:105px;height:auto;opacity:.82}.bf-project-paper-head>span{font-size:11px;line-height:1.3;color:#8291a6}.bf-project-paper h4{font-size:28px;letter-spacing:-.04em;font-weight:600;line-height:1.15;margin:0 0 8px}.bf-project-paper>p{font-size:13px;color:#748199;margin:0 0 23px;line-height:1.6;max-width:330px}
.bf-project-focus{display:flex;align-items:center;justify-content:space-between;gap:17px;background:#eef5ff;border:1px solid #dceafa;border-radius:9px;padding:14px 16px;margin-bottom:22px}.bf-project-focus>span{font-size:15px;font-weight:500;color:#1663bb}.bf-project-focus svg{width:17px;height:17px;color:#2882e6}
.bf-project-session{display:flex;align-items:center;gap:15px;margin:0 0 19px}.bf-session-tile{width:50px;height:53px;flex:none;border-radius:8px;background:#fff;box-shadow:0 3px 9px #2145750b;border:1px solid #dce6f2;display:grid;place-content:center;color:#2876cc}.bf-session-tile svg{height:24px;width:24px;stroke-width:1.4}.bf-project-session strong{display:block;font-size:15px;letter-spacing:-.015em;font-weight:550;color:#263a55}.bf-project-session p{font-size:12px;color:#8190a3;line-height:1.5;margin-top:3px}
.bf-project-cycle{display:flex;align-items:center;justify-content:space-between;gap:8px;padding-top:12px;color:#65748b;font-size:11px}.bf-project-cycle svg{width:14px;height:14px;color:#8199b5;flex:none}
.bf-project-slack{position:relative;z-index:2;margin:-3px -10px 0 35px;padding:20px 23px;border:1px solid #d2e6ff38;border-radius:12px;background:radial-gradient(ellipse at 95% 0,#2a5484,#1d3551 55%,#182b44);color:white;box-shadow:0 12px 27px #193d6f23;transform:none}.bf-project-slack-head{display:flex;align-items:center;gap:9px;font-size:14px;font-weight:500;color:#f0f6ff;margin-bottom:9px}.bf-project-slack-head svg{width:18px;height:18px;color:#8ac2ff}.bf-project-slack p{font-size:13px;color:#bed1e8;line-height:1.65}.bf-project-slack span{display:block;margin-top:10px;font-size:11px;color:#89a6c7}
.bf-amplify-stage{position:relative;overflow:hidden;padding:63px 0;background:radial-gradient(ellipse at 30% 110%,#173653,#111b28 40%,#101114 78%)}.bf-amplify-stage .bf-wrap{display:grid;grid-template-columns:1.02fr .98fr;gap:72px;align-items:center}.bf-amplify-stage h3{font-size:clamp(30px,3.3vw,43px)}.bf-amplify-stage p{margin-top:21px}.bf-amplify-stage .bf-budget-note{font-size:12px;line-height:1.65;color:#8b9eb6;margin-top:16px}.bf-amplify-stage .bf-cta{margin-top:25px}
.bf-reach-art{position:relative;min-height:300px;display:flex;align-items:center;justify-content:center;isolation:isolate}.bf-reach-art:before{content:'';position:absolute;inset:10% -5%;background:radial-gradient(ellipse,#0071e334,#00498609 57%,transparent 72%);filter:blur(20px);z-index:-1}.bf-reach-art>svg{width:100%;height:auto;overflow:visible}.bf-reach-art text{font-family:Geist,Inter,system-ui,sans-serif}.bf-reach-art figcaption{position:absolute;bottom:0;left:0;right:0;text-align:center;font-size:12px;color:#8297b2}
@media(min-width:761px) and (max-width:1050px){.bf-content-stage .bf-wrap{gap:35px;grid-template-columns:.9fr 1.1fr}.bf-offer-v3 h2{font-size:40px}.bf-working-stage .bf-wrap,.bf-amplify-stage .bf-wrap{gap:45px}.bf-project-paper{padding:25px 22px 27px}.bf-project-slack{margin-left:20px}.bf-working-copy h3{font-size:36px}.bf-offer-v3 p{font-size:15px}}
@media(max-width:760px){.bf-offer-v3 .bf-content-stage{padding:43px 0 42px}.bf-content-stage .bf-wrap{display:block}.bf-offer-v3 h2{font-size:37px}.bf-offer-v3 h3{font-size:34px}.bf-offer-v3 p{font-size:15px;line-height:1.75}.bf-content-intro>p{margin-top:20px}.bf-content-ambition{margin-top:20px;padding-top:0;gap:12px}.bf-content-ambition strong{font-size:29px}.bf-content-ambition>p{font-size:11px}.bf-content-stage .dk-workshop{margin-top:28px}.bf-working-stage{padding:43px 0 41px}.bf-working-stage .bf-wrap{display:flex;flex-direction:column;gap:22px}.bf-working-copy{order:0}.bf-project-composition{order:1;width:100%;max-width:480px;padding:18px 8px 13px}.bf-project-paper{padding:25px 23px 28px}.bf-project-paper h4{font-size:27px}.bf-project-paper>p{font-size:13px}.bf-project-session p{font-size:12px}.bf-project-slack{margin-left:20px;margin-right:-3px;padding:18px 20px}.bf-project-slack p{font-size:13px}.bf-working-copy h4{font-size:21px;margin-top:24px}.bf-amplify-stage{padding:38px 0 42px}.bf-amplify-stage .bf-wrap{display:flex;flex-direction:column-reverse;gap:24px}.bf-amplify-stage h3{font-size:33px}.bf-amplify-stage .bf-budget-note{font-size:12px}.bf-reach-art{width:100%;max-width:460px;min-height:230px;padding:0 0 20px}.bf-reach-art figcaption{font-size:11px}.bf-amplify-stage .bf-cta{width:100%;justify-content:space-between}}
`;

function WorkingRhythm() {
  return (
    <figure
      className="bf-project-composition"
      aria-label="Your kickstart includes personal strategy, one-to-one meetings every two weeks and feedback in Slack between calls"
    >
      <div className="bf-project-paper bf-reactive">
        <span className="bf-surface-shine" aria-hidden="true" />
        <div className="bf-project-paper-head">
          <img src={logo} alt="DK Artists" />
          <span>Built around your project</span>
        </div>
        <h4>
          Your next move,
          <br />
          worked out together.
        </h4>
        <p>Strategy from Damian, put into practice with your account manager.</p>
        <div className="bf-project-focus">
          <span>Your music. Your starting point.</span>
          <ArrowUpRight aria-hidden />
        </div>
        <div className="bf-project-session">
          <div className="bf-session-tile" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="3" y="6" width="18" height="15" rx="3" />
              <path d="M7 3v6m10-6v6M3 11h18m-12 5h6" />
            </svg>
          </div>
          <div>
            <strong>One-to-one meetings every two weeks</strong>
            <p>Review the work with your account manager.</p>
          </div>
        </div>
        <div className="bf-project-cycle">
          <span>Make the content</span>
          <ArrowRight aria-hidden />
          <span>Read the response</span>
          <ArrowRight aria-hidden />
          <span>Refine it</span>
        </div>
      </div>
      <div className="bf-project-slack bf-reactive">
        <span className="bf-surface-shine" aria-hidden="true" />
        <div className="bf-project-slack-head">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            aria-hidden
          >
            <path d="M8 3v13m8-8v13M3 8h13M8 16h13" />
          </svg>
          Keep the work moving in Slack
        </div>
        <p>Share an idea before filming, or a clip while you’re still shaping it. Bring the team into the decision.</p>
        <span>Your team’s Slack channel, open to you 24/7</span>
      </div>
    </figure>
  );
}

type ReachScreenshot = {
  id: string;
  position: "wide" | "main" | "tall";
  src: string;
  width: number;
  height: number;
  crop: { x: number; y: number; width: number; height: number };
  title: string;
  description: string;
  alt: string;
};

// Artist attribution follows the existing case-study source.
// These are historical social screenshots, not examples of paid-ad performance.
const REACH_SCREENSHOTS: ReachScreenshot[] = [
  {
    id: "sean",
    position: "wide",
    src: reachSeanOriginal,
    width: 814,
    height: 742,
    crop: { x: 0, y: 150, width: 814, height: 592 },
    title: "Sean Stephens · Instagram insights",
    description: "Original Instagram insights for 24 March to 22 April.",
    alt: "Sean Stephens’ Instagram insights showing 5M accounts reached, 107K accounts engaged, 9,921 total followers and 38 pieces of content shared.",
  },
  {
    id: "sean-video",
    position: "main",
    src: reachSeanVideoOriginal,
    width: 480,
    height: 508,
    crop: { x: 0, y: 115, width: 480, height: 393 },
    title: "Sean Stephens · TikTok video results",
    description: "Original video analytics showing 1.8M views and more than 5,000 hours of watch time.",
    alt: "Sean Stephens’ original TikTok video analytics showing 1.8M views, 48K likes, 2,764 comments, 27K shares, 4,189 saves and 5061 hours of total play time.",
  },
  {
    id: "kaylee",
    position: "tall",
    src: reachKayleeOriginal,
    width: 887,
    height: 1920,
    crop: { x: 0, y: 325, width: 887, height: 1220 },
    title: "Kaylee Bell · TikTok analytics",
    description: "Original TikTok analytics for 25 September to 22 October.",
    alt: "Kaylee Bell’s TikTok analytics showing 31M video views, 807K profile views, 1.8M likes and 20K comments during the displayed 28-day period.",
  },
];

const reachIllustrationStyles = `
.bf-reach-art.bf-reach-social{display:block;width:100%;max-width:520px;min-height:0;margin:0;padding:0;isolation:isolate}
.bf-reach-social .bf-reach-social-scene{position:relative;width:100%;aspect-ratio:520/410;perspective:1200px;isolation:isolate}
.bf-reach-social .bf-reach-social-scene:before{content:'';position:absolute;left:5%;right:5%;bottom:4%;height:25%;border-radius:50%;background:radial-gradient(ellipse,#00000061,transparent 69%);filter:blur(15px);transform:rotate(-5deg);pointer-events:none;z-index:-1}
.bf-reach-social .bf-reach-social-card{position:absolute;display:block;box-sizing:border-box;margin:0;padding:3px;border:1px solid #d5e2f38c;border-radius:10px;appearance:none;cursor:zoom-in;text-align:left;isolation:isolate;background:linear-gradient(135deg,#e7eff9,#b9c8d8 28%,#f5f8fd 46%,#8295ab 78%,#d1e0ef);box-shadow:inset 0 1px 0 #fff9,0 2px 4px #0002,0 20px 38px -12px #0007;transition:transform .72s cubic-bezier(.2,.75,.2,1),box-shadow .72s cubic-bezier(.2,.75,.2,1);transform-style:preserve-3d;will-change:auto}
.bf-reach-social .bf-reach-social-card:after{content:'';position:absolute;inset:1px;pointer-events:none;border:1px solid #ffffff70;border-radius:9px;background:linear-gradient(127deg,#ffffff12,transparent 22%,transparent 75%,#deecff0c);box-shadow:inset -1px -1px 0 #31475b38}
.bf-reach-social .bf-reach-social-crop{position:relative;display:block;overflow:hidden;width:100%;border-radius:6px;background:#fff;box-shadow:0 0 0 1px #172f4b22;isolation:isolate}
.bf-reach-social .bf-reach-social-crop img{position:absolute;display:block;max-width:none;height:auto;margin:0;border:0;border-radius:0;pointer-events:none;user-select:none}
.bf-reach-social .bf-reach-social-card--wide{left:0;top:5%;width:50%;z-index:1;transform:rotate(-12deg) rotateY(9deg)}
.bf-reach-social .bf-reach-social-card--tall{right:0;top:1%;width:36%;z-index:2;transform:rotate(11deg) rotateY(-10deg)}
.bf-reach-social .bf-reach-social-card--main{left:22%;top:35%;width:58%;z-index:3;transform:rotate(-3deg) rotateY(-3deg);border-color:#f5f9ffdb;box-shadow:inset 0 1px 0 #fffb,0 3px 6px #0003,0 24px 46px -12px #0008}
.bf-reach-social .bf-reach-social-open{position:absolute;right:11px;bottom:11px;display:grid;place-items:center;width:29px;height:29px;border:1px solid #eaf3ffb3;border-radius:7px;color:#fff;background:#07182cd6;box-shadow:0 2px 8px #0003;backdrop-filter:blur(8px);pointer-events:none;opacity:0;transition:opacity .2s}
.bf-reach-social .bf-reach-social-open svg{width:15px;height:15px;display:block}
.bf-reach-social .bf-reach-social-card--main .bf-reach-social-open{top:11px;bottom:auto}
.bf-reach-social .bf-reach-social-card:focus-visible{outline:3px solid #78bdff;outline-offset:5px;z-index:5;transform:rotate(0deg) translateY(-5px);box-shadow:inset 0 1px 0 #fff,0 16px 42px #0008}
.bf-reach-social .bf-reach-social-card:focus-visible .bf-reach-social-open{opacity:1}
.bf-reach-art.bf-reach-social>figcaption{position:static;inset:auto;margin:12px 0 0;padding:0 8px;text-align:center;color:#a7b8ce;font-size:14px;line-height:1.55;letter-spacing:0}
.bf-reach-proof-dialog{box-sizing:border-box;display:block;width:min(840px,calc(100vw - 32px));max-width:840px;max-height:90dvh;overflow-y:auto;overscroll-behavior:contain;border:1px solid #d6e2ef;border-radius:16px;padding:25px;background:#f6f8fb;color:#15283e;gap:0;scrollbar-gutter:stable}
.bf-reach-proof-dialog .bf-reach-proof-title{padding-right:28px;margin:0 0 8px;font-size:22px;line-height:1.25;font-weight:650;letter-spacing:-.025em}
.bf-reach-proof-dialog .bf-reach-proof-description{margin:0 0 20px;color:#53657a;font-size:15px;line-height:1.55}
.bf-reach-proof-dialog .bf-reach-proof-original{display:block;width:100%;height:auto;max-width:100%;max-height:none;margin:0;background:#fff;border:1px solid #dce4ed;border-radius:8px;object-fit:contain}
.bf-reach-proof-dialog .bf-reach-proof-link{display:inline-flex;margin-top:14px;color:#0067cf;font-size:14px;line-height:1.5;text-underline-offset:3px;text-decoration:underline}
.bf-reach-proof-dialog .bf-reach-proof-link:focus-visible{outline:2px solid #0071e3;outline-offset:4px;border-radius:2px}
@media(hover:hover) and (pointer:fine){.bf-reach-social:hover .bf-reach-social-card--wide{transform:rotate(-13deg) rotateY(7deg) translateX(-4px)}.bf-reach-social:hover .bf-reach-social-card--tall{transform:rotate(12deg) rotateY(-8deg) translateX(4px)}.bf-reach-social:hover .bf-reach-social-card--main{transform:rotate(-2deg) rotateY(-2deg) translateY(-3px)}.bf-reach-social .bf-reach-social-card:hover,.bf-reach-social .bf-reach-social-card:focus-visible{z-index:5;box-shadow:inset 0 1px 0 #fffb,0 20px 40px -10px #0008}.bf-reach-social .bf-reach-social-card--wide:hover,.bf-reach-social .bf-reach-social-card--wide:focus-visible{transform:rotate(-10deg) rotateY(5deg) translate(-3px,-4px)}.bf-reach-social .bf-reach-social-card--tall:hover,.bf-reach-social .bf-reach-social-card--tall:focus-visible{transform:rotate(9deg) rotateY(-6deg) translate(3px,-4px)}.bf-reach-social .bf-reach-social-card--main:hover,.bf-reach-social .bf-reach-social-card--main:focus-visible{transform:rotate(-1deg) translateY(-5px)}.bf-reach-social .bf-reach-social-card:hover .bf-reach-social-open{opacity:1}}
@media(max-width:760px){.bf-reach-art.bf-reach-social{max-width:450px;padding-top:8px}.bf-reach-social .bf-reach-social-scene{aspect-ratio:350/295}.bf-reach-social .bf-reach-social-card{padding:3px;border-radius:9px}.bf-reach-social .bf-reach-social-card:after{border-radius:7px}.bf-reach-social .bf-reach-social-card--wide{width:51%;top:6%;transform:rotate(-10deg) rotateY(5deg)}.bf-reach-social .bf-reach-social-card--tall{width:37%;top:1%;transform:rotate(10deg) rotateY(-6deg)}.bf-reach-social .bf-reach-social-card--main{left:21%;top:35%;width:58%;transform:rotate(-3deg)}.bf-reach-social .bf-reach-social-open{width:25px;height:25px;right:8px;bottom:8px;opacity:1}.bf-reach-art.bf-reach-social>figcaption{margin-top:10px;font-size:14px}.bf-reach-proof-dialog{width:calc(100vw - 20px);max-height:92dvh;padding:22px 14px 16px;border-radius:13px}.bf-reach-proof-dialog .bf-reach-proof-title{font-size:19px;line-height:1.3}.bf-reach-proof-dialog .bf-reach-proof-description{font-size:14px;margin-bottom:16px}}
@media(prefers-reduced-motion:reduce){.bf-reach-proof-dialog,[data-state]:has(+ .bf-reach-proof-dialog){animation:none!important;transition:none!important}.bf-reach-social .bf-reach-social-card,.bf-reach-social .bf-reach-social-open{transition:none}.bf-reach-social:hover .bf-reach-social-card--wide{transform:rotate(-12deg) rotateY(9deg)}.bf-reach-social:hover .bf-reach-social-card--tall{transform:rotate(11deg) rotateY(-10deg)}.bf-reach-social:hover .bf-reach-social-card--main{transform:rotate(-3deg) rotateY(-3deg)}.bf-reach-social .bf-reach-social-card:hover,.bf-reach-social .bf-reach-social-card:focus-visible{transform:rotate(0deg)}}
`;

function ReachIllustration() {
  const captionId = React.useId();
  const [selected, setSelected] = React.useState<ReachScreenshot | null>(null);
  const [open, setOpen] = React.useState(false);
  const lastTrigger = React.useRef<HTMLButtonElement | null>(null);

  return (
    <figure className="bf-reach-art bf-reach-social" aria-labelledby={captionId}>
      <style>{reachIllustrationStyles}</style>
      <div className="bf-reach-social-scene">
        {REACH_SCREENSHOTS.map((screenshot) => (
          <button
            key={screenshot.id}
            type="button"
            className={`bf-reach-social-card bf-reach-social-card--${screenshot.position}`}
            aria-label={`Open full screenshot: ${screenshot.title}`}
            aria-haspopup="dialog"
            onClick={(event) => {
              lastTrigger.current = event.currentTarget;
              setSelected(screenshot);
              setOpen(true);
            }}
          >
            <span
              className="bf-reach-social-crop"
              style={{ aspectRatio: `${screenshot.crop.width} / ${screenshot.crop.height}` }}
            >
              <img
                src={screenshot.src}
                alt=""
                width={screenshot.width}
                height={screenshot.height}
                loading="lazy"
                decoding="async"
                draggable={false}
                style={{
                  width: `${(screenshot.width / screenshot.crop.width) * 100}%`,
                  left: `${(-screenshot.crop.x / screenshot.crop.width) * 100}%`,
                  top: `${(-screenshot.crop.y / screenshot.crop.height) * 100}%`,
                }}
              />
            </span>
            <span className="bf-reach-social-open" aria-hidden="true">
              <svg viewBox="0 0 20 20" fill="none">
                <path
                  d="M12 3H17V8M17 3L10.5 9.5M8 17H3V12M3 17L9.5 10.5"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
        ))}
      </div>
      <figcaption id={captionId}>Select a screenshot to see the full original.</figcaption>
      <Dialog open={open} onOpenChange={setOpen}>
        {selected && (
          <DialogContent
            className="bf-reach-proof-dialog"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              lastTrigger.current?.focus({ preventScroll: true });
            }}
          >
            <DialogTitle className="bf-reach-proof-title">{selected.title}</DialogTitle>
            <DialogDescription className="bf-reach-proof-description">{selected.description}</DialogDescription>
            <img
              className="bf-reach-proof-original"
              src={selected.src}
              alt={selected.alt}
              width={selected.width}
              height={selected.height}
            />
            <a className="bf-reach-proof-link" href={selected.src} target="_blank" rel="noopener noreferrer">
              Open original image in a new tab
            </a>
          </DialogContent>
        )}
      </Dialog>
    </figure>
  );
}

function Journey() {
  return (
    <section id="bf-support" className="bf-section bf-offer-v3" aria-labelledby="journey-title">
      <style>{offerInclusionStyles}</style>
      <div className="bf-content-stage" data-energy-tone="dark">
        <div className="bf-wrap">
          <div className="bf-content-intro">
            <h2 id="journey-title">
              Let’s find your <Highlight variant="pale">breakout</Highlight> content.
            </h2>
            <p>
              You’re putting yourself into the music. The next job is finding the part that makes someone stop and
              think, “this is for me.” That’s where we get to work with you.
            </p>
            <p>
              We help shape the angle of a clip and how it opens, so the song has a reason to matter to someone who
              hasn’t heard of you yet. Bring an idea before filming or a clip in progress. Get feedback while you can
              still change it, with help planning the shoot when useful.
            </p>
            <p>
              Then we look at the response together. Develop the ideas that connect. Change or drop the ones that don’t.
            </p>
            <div className="bf-content-ambition">
              <span>The kind of jump you’re working towards</span>
              <strong>
                200<small>views</small>
              </strong>
              <ArrowRight aria-hidden />
              <strong>
                200,000<small>views</small>
              </strong>
            </div>
          </div>
          <ContentWorkshop />
        </div>
      </div>
      <div className="bf-working-stage" data-energy-tone="light">
        <div className="bf-wrap">
          <WorkingRhythm />
          <div className="bf-working-copy">
            <h3>
              Your project.
              <br />
              Us <Highlight>in your corner.</Highlight>
            </h3>
            <p>
              Your own account manager works with you throughout your 30, 60 or 90 days. Start with the music you have
              and what you’ve already tried. Damian provides the strategy for your project, and your manager helps you
              put it into practice.
            </p>
            <h4>Get into the work, every two weeks.</h4>
            <p>
              Bring your ideas or clips to a one-to-one meeting with your manager every two weeks. As the work goes out,
              review the response with your account manager and agree what to develop before the next call.
            </p>
            <h4>You don’t have to wait for the next call.</h4>
            <p>
              Share work in Slack as you’re making it. Whether you’re unsure about an opening or choosing which idea to
              film, you have the team’s perspective while there’s still time to shape it.
            </p>
          </div>
        </div>
      </div>
      <div className="bf-amplify-stage bf-creative-atmosphere" data-energy-tone="dark">
        <div className="bf-wrap">
          <ReachIllustration />
          <div>
            <h3>
              Give good content
              <br />a <Highlight variant="pale">bigger audience.</Highlight>
            </h3>
            <div className="bf-ads-credential">
              <span>Your ads manager</span>
              <MetaExperienceBadge />
            </div>
            <p>
              When your music and content are ready, our ads manager runs the agreed campaigns to put them in front of
              more potential fans. We work on the creative with you and manage the advertising, so your time can go into
              making the music and content.
            </p>
            <KickstartCTA />
          </div>
        </div>
      </div>
    </section>
  );
}

const comparisonV4Styles = `
.bf-comparison.bf-cv4{padding-top:64px;background:radial-gradient(ellipse at 90% 24%,#eaf0f8,#f7f9fc 42%,#fff 80%)}
.bf-cv4 .bf-center-heading{max-width:800px}
.bf-cv4 .bf-center-heading>p{max-width:655px}
.bf-cv4-workbench{margin-top:36px;border:1px solid #d9e0e9;border-radius:10px;overflow:hidden;background:#fff;box-shadow:0 2px 4px #19325103,0 20px 50px -20px #19325119}
.bf-cv4-columns,.bf-cv4-row{display:grid;grid-template-columns:minmax(0,.77fr) minmax(0,1fr) minmax(0,1.35fr)}
.bf-cv4-columns{align-items:stretch;border-bottom:1px solid #d4dee9;background:linear-gradient(180deg,#fff,#f6f8fa)}
.bf-cv4-columns>div{display:flex;align-items:center;min-height:66px;padding:19px 24px;font-size:16px;font-weight:600;line-height:1.35;letter-spacing:-.015em;color:#354254}
.bf-cv4-columns>div:first-child{color:#758091;font-size:13px;font-weight:400;letter-spacing:0}
.bf-cv4-columns>div:last-child{background:linear-gradient(150deg,#edf5ff,#e6f0fd);color:#0065c9;border-left:1px solid #dce8f8}
.bf-cv4-column-label{display:block}
.bf-cv4-row{position:relative;isolation:isolate;align-items:stretch}
.bf-cv4-row+.bf-cv4-row{border-top:1px solid #e2e7ee}
.bf-cv4-moment{display:block;padding:24px;min-width:0}
.bf-cv4-moment>span{display:block;font-size:13px;color:#7c8796;line-height:1.4;margin-bottom:8px}
.bf-cv4-moment h3{font-size:18px;font-weight:550;line-height:1.35;letter-spacing:-.02em;color:#182538;max-width:210px}
.bf-cv4-alone{display:block;padding:24px;color:#697483;font-size:15px;line-height:1.6}
.bf-cv4-together{position:relative;background:#f2f7fd;padding:24px;border-left:1px solid #dce8f8;transition:background .45s ease}
.bf-cv4-together h4{font-size:18px;line-height:1.35;font-weight:600;letter-spacing:-.02em;color:#14314f;margin:0 0 9px}
.bf-cv4-together p{font-size:15px;line-height:1.6;color:#475d77;max-width:42ch}
.bf-cv4-mobile-label{display:none}
.bf-cv4 .bf-fit{margin-top:40px}
@media(hover:hover) and (pointer:fine){.bf-cv4-row:hover .bf-cv4-together{background:#e9f3ff}}
@media(min-width:761px) and (max-width:1000px){.bf-cv4-columns,.bf-cv4-row{grid-template-columns:minmax(0,.7fr) minmax(0,.9fr) minmax(0,1.3fr)}.bf-cv4-moment,.bf-cv4-alone,.bf-cv4-together{padding:22px 18px}.bf-cv4-columns>div{padding-left:18px;padding-right:18px;font-size:15px}.bf-cv4-moment h3,.bf-cv4-together h4{font-size:17px}}
@media(max-width:760px){
.bf-comparison.bf-cv4{padding-top:42px}
.bf-cv4-workbench{margin-top:28px;border:0;border-radius:0;box-shadow:none;background:transparent}
.bf-cv4-columns{grid-template-columns:minmax(0,.84fr) minmax(0,1.16fr);border-bottom:1px solid #d9e0e9;background:transparent;align-items:stretch}
.bf-cv4-columns>div{padding:14px 12px 14px 0;min-height:60px;font-size:14px;line-height:1.4}
.bf-cv4-columns>div:first-child{display:none}
.bf-cv4-columns>div:last-child{padding-left:15px;padding-right:12px;border:0;border-radius:7px 7px 0 0;background:#e9f2ff}
.bf-cv4-row{grid-template-columns:minmax(0,.84fr) minmax(0,1.16fr)}
.bf-cv4-row+.bf-cv4-row{border-top:1px solid #d9e0e9}
.bf-cv4-moment{grid-column:1/-1;display:block;padding:23px 0 17px}
.bf-cv4-moment>span{margin-bottom:7px;font-size:12px}
.bf-cv4-moment h3{font-size:19px;line-height:1.3;max-width:310px}
.bf-cv4-alone{padding:16px 15px 22px 0;font-size:14px;line-height:1.65}
.bf-cv4-together{padding:16px 15px 22px;border-left:0;border-radius:0;background:#eff5fd}
.bf-cv4-mobile-label{display:block;font-size:12px;font-weight:500;line-height:1.4;color:#798391;margin-bottom:11px}
.bf-cv4-together>.bf-cv4-mobile-label{color:#0064c9}
.bf-cv4-together h4{font-size:17px;line-height:1.4;margin-bottom:9px}
.bf-cv4-together p{font-size:14px;line-height:1.65}
.bf-cv4 .bf-fit{margin-top:30px}
}
@media(prefers-reduced-motion:reduce){.bf-cv4-together{transition:none}}
`;

function ComparisonAndFit() {
  const moments = [
    {
      moment: "Your artist identity",
      question: "What should people remember you for?",
      alone: "You know what your music means to you. Making that clear to someone discovering you is a different job.",
      togetherTitle: "Build a brand that feels like you.",
      together:
        "Work with us on what makes your music and story distinctive, so your content and the way you present yourself feel like the same artist.",
    },
    {
      moment: "Your fanbase",
      question: "What makes someone come back?",
      alone: "A post gets attention. Turning that moment into a reason to follow your music takes more thought.",
      togetherTitle: "Build beyond a single moment.",
      together:
        "Work out who your music connects with and what gives them a reason to stay interested between releases. Lay the foundations for a fanbase you can keep growing.",
    },
    {
      moment: "Your content",
      question: "Is this idea worth making?",
      alone: "You put time into filming and editing before finding out whether the idea connects.",
      togetherTitle: "Shape it before you shoot.",
      together:
        "Get feedback on the angle and opening in Slack before you film, with help planning the shoot when useful.",
    },
    {
      moment: "Reaching more people",
      question: "How will new listeners find you?",
      alone: "You’re learning ad tools and managing campaigns alongside making the music.",
      togetherTitle: "Keep your time for the music.",
      together:
        "When your music and content are ready, our ads manager runs the agreed campaigns to reach more potential fans.",
    },
    {
      moment: "Your next move",
      question: "What deserves your effort next?",
      alone: "You can see the numbers. Deciding what they mean for your next release or idea is harder on your own.",
      togetherTitle: "Make the next decision with us.",
      together:
        "Use your one-to-one meetings every two weeks to review the response and agree what to develop. Carry what you learn into the next release, so you have something to build on after the kickstart.",
    },
  ];

  return (
    <section
      className="bf-section bf-light bf-comparison bf-cv4"
      data-energy-tone="light"
      aria-labelledby="comparison-title"
    >
      <style>{comparisonV4Styles}</style>
      <div className="bf-wrap">
        <div className="bf-center-heading">
          <h2 id="comparison-title" className="bf-heading">
            What changes when
            <br />
            you <Highlight>work with us?</Highlight>
          </h2>
          <p>
            The aim is a fanbase that stays with you beyond one song. That starts with a clearer sense of who you are as
            an artist, and knowing what will bring the right people closer.
          </p>
        </div>
        <div className="bf-cv4-workbench" aria-label="Working on your own compared with the Artist Kickstart">
          <div className="bf-cv4-columns">
            <div>The moment</div>
            <div>
              <span className="bf-cv4-column-label">Working on your own</span>
            </div>
            <div>
              <span className="bf-cv4-column-label">With us in your corner</span>
            </div>
          </div>
          {moments.map((item) => (
            <article className="bf-cv4-row" key={item.moment} aria-label={item.moment}>
              <div className="bf-cv4-moment">
                <span>{item.moment}</span>
                <h3>{item.question}</h3>
              </div>
              <div className="bf-cv4-alone">
                <div>
                  <span className="bf-cv4-mobile-label">On your own</span>
                  <span className="sr-only">Working on your own: </span>
                  {item.alone}
                </div>
              </div>
              <div className="bf-cv4-together">
                <span className="bf-cv4-mobile-label">With the kickstart</span>
                <h4>
                  <span className="sr-only">With the kickstart: </span>
                  {item.togetherTitle}
                </h4>
                <p>{item.together}</p>
              </div>
            </article>
          ))}
        </div>
        <div className="bf-fit">
          <div className="bf-fit-column">
            <h3>This is for you if…</h3>
            <ul>
              {[
                "You have music you believe in and want more people to care about it.",
                "You want content that feels like you and gives people a reason to care.",
                "You want us involved while you create, with honest feedback on what to change.",
                "You’re ready to put the work out and build on what gets a response.",
              ].map((text) => (
                <li key={text}>
                  <Check aria-hidden />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bf-fit-column">
            <h3>This will not be the right fit if…</h3>
            <ul>
              {[
                "You’re looking for a guaranteed number of streams or followers.",
                "You’re not open to feedback or trying a different approach.",
              ].map((text) => (
                <li key={text}>
                  <X aria-hidden />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function SpotifyPhone() {
  return (
    <figure className="bf-phone-figure bf-proof-phone">
      <div className="bf-spotify-phone">
        <div className="bf-phone-screen">
          <img
            src={spotifyDashboard}
            alt="Spotify for Artists audience dashboard showing 1,038,963 listeners in the last 28 days"
            width={887}
            height={1920}
            loading="lazy"
          />
        </div>
      </div>
      <figcaption className="bf-phone-caption">Spotify for Artists</figcaption>
    </figure>
  );
}

const artistProofStyles = `
.bf-stories{position:relative;overflow:hidden;background:radial-gradient(ellipse at 45% 0,#1d3047,#111822 38%,#101114 76%);padding:70px 0 55px}.bf-stories .bf-center-heading>p{color:#aab7c9;max-width:560px}.bf-artist-features{display:grid;grid-template-columns:1fr 1fr;gap:25px;margin-top:37px}.bf-artist-feature{display:block;width:100%;text-align:left;border:1px solid #d8eaff24;border-radius:14px;background:linear-gradient(120deg,#20334b8a,#151f2dd9);box-shadow:inset 0 1px 0 #fff1,0 17px 40px #0002;overflow:hidden;cursor:pointer;color:white;position:relative;transition:border-color .25s,transform .25s}.bf-artist-feature:hover{border-color:#6faeff77;transform:translateY(-3px)}.bf-artist-photo{display:block;height:260px;position:relative;overflow:hidden;background:#1e2a38;isolation:isolate}.bf-artist-photo:after{content:'';position:absolute;inset:0;background:linear-gradient(180deg,#101a2708 35%,#101d30bd 100%),linear-gradient(100deg,#1b344c22,transparent);z-index:1;pointer-events:none}.bf-artist-photo img{width:100%;height:100%;object-fit:cover;display:block}.bf-artist-photo-brian img{object-position:50% 28%}.bf-artist-photo-kaylee{background-image:url('${kayleePhoto}');background-size:181% auto;background-position:50% 10.9%;background-repeat:no-repeat}.bf-artist-photo-kaylee:after{background:linear-gradient(180deg,#15243a00 25%,#101d30c9 100%)}.bf-artist-photo-name{position:absolute;left:28px;bottom:22px;z-index:2;font-size:24px;font-weight:500;letter-spacing:-.025em;line-height:1.2}.bf-artist-watch{position:absolute;right:24px;bottom:20px;z-index:2;display:flex;align-items:center;gap:8px;font-size:12px;padding:9px 12px;border:1px solid #fff4;border-radius:25px;background:#14263a4d;backdrop-filter:blur(12px);box-shadow:inset 0 1px 0 #fff2}.bf-artist-watch svg{height:12px;width:12px}.bf-artist-result{display:grid;grid-template-columns:auto 1fr;align-items:center;gap:23px;padding:23px 27px 26px}.bf-artist-result>strong{font-size:57px;font-weight:550;letter-spacing:-.065em;line-height:1;color:#82bcff}.bf-artist-result>span{font-size:14px;line-height:1.55;color:#b3c3d8;max-width:250px}.bf-artist-result>span b{display:block;font-weight:500;color:#f1f5fb;font-size:16px;margin-bottom:3px}.bf-artist-secondary{display:grid;grid-template-columns:1fr 1fr;gap:25px;margin-top:22px}.bf-artist-mini{display:flex;align-items:center;gap:17px;text-align:left;padding:16px;background:#fff04;border:1px solid #ffffff12;border-radius:10px;color:white;transition:background .2s}.bf-artist-mini:hover{background:#ffffff09}.bf-artist-mini-image{position:relative;flex:none;width:101px;height:65px;border-radius:6px;overflow:hidden;background:#263448}.bf-artist-mini-image img{display:block;width:100%;height:100%;object-fit:cover}.bf-artist-mini-image:after{content:'';position:absolute;inset:0;background:#0e1b3222}.bf-artist-mini-copy{display:block;font-size:13px;color:#9bb0ca;line-height:1.55}.bf-artist-mini b{display:block;font-size:16px;font-weight:500;color:#e6edf7;letter-spacing:-.02em}.bf-artist-mini>svg{width:15px;height:15px;margin-left:auto;color:#7ea8db;flex:none}.bf-stories-more{margin-top:27px}.bf-stories-more .bf-text-link{color:#cad7e8;border-color:#7086a5}.bf-artist-results-note{font-size:11px;color:#74859d;text-align:center;margin-top:19px!important}
@media(min-width:761px) and (max-width:1050px){.bf-artist-result{gap:15px;padding:22px}.bf-artist-result>strong{font-size:47px}.bf-artist-photo-name{left:22px;font-size:22px}.bf-artist-watch{right:18px;font-size:11px}.bf-artist-photo{height:235px}}
@media(max-width:760px){.bf-stories{padding:44px 0 36px}.bf-artist-features{display:block;margin-top:27px}.bf-artist-feature+.bf-artist-feature{margin-top:19px}.bf-artist-photo{height:220px}.bf-artist-photo-name{left:21px;bottom:18px;font-size:23px}.bf-artist-watch{right:18px;bottom:16px;font-size:11px;padding:8px 10px}.bf-artist-result{gap:18px;padding:22px 21px}.bf-artist-result>strong{font-size:48px}.bf-artist-result>span{font-size:12px}.bf-artist-result>span b{font-size:14px}.bf-artist-photo-kaylee{background-position:50% 9.7%}.bf-artist-photo-brian img{object-position:50% 25%}.bf-artist-secondary{display:block;margin-top:19px}.bf-artist-mini+.bf-artist-mini{margin-top:12px}.bf-artist-mini{padding:13px;gap:14px}.bf-artist-mini-image{width:91px;height:60px}.bf-artist-mini b{font-size:15px}.bf-artist-mini-copy{font-size:12px}.bf-stories-more{margin-top:23px}.bf-artist-results-note{font-size:10px;margin-top:15px!important}}
@media(prefers-reduced-motion:reduce){.bf-artist-feature{transition:none}.bf-artist-feature:hover{transform:none}}
`;
const BRIAN_PERFORMANCE =
  "https://i.vimeocdn.com/video/2072123086-faed0ad20da4f56e9dd4eef4db5ccc1e277039d77c3ec2fedaa985ad923f518d-d_960?region=us";
function ArtistStories({ onPlay }: { onPlay: OpenStory }) {
  return (
    <section
      id="bf-artists"
      className="bf-section bf-dark bf-stories"
      data-energy-tone="dark"
      aria-labelledby="stories-title"
    >
      <style>{artistProofStyles}</style>
      <div className="bf-wrap">
        <div className="bf-center-heading">
          <h2 id="stories-title" className="bf-heading">
            Artist <Highlight variant="pale">stories.</Highlight>
          </h2>
          <p>
            Brian started from zero and took one song past 500,000 streams. Hear what changed for him, and how the
            others built on their own starting points.
          </p>
        </div>
        <div className="bf-artist-features">
          <button
            className="bf-artist-feature bf-reactive"
            type="button"
            onClick={(event) => onPlay("brian-michael", event.currentTarget)}
            aria-label="Watch Brian Michael's story: 500K streams on one song"
          >
            <span className="bf-surface-shine" aria-hidden="true" />
            <span className="bf-artist-photo bf-artist-photo-brian">
              <img
                src={BRIAN_PERFORMANCE}
                alt="Brian Michael performing Seeing Mama Cry"
                width={960}
                height={1707}
                loading="lazy"
              />
              <span className="bf-artist-photo-name">Brian Michael</span>
              <span className="bf-artist-watch">
                <Play aria-hidden fill="currentColor" />
                Watch the story
              </span>
            </span>
            <span className="bf-artist-result">
              <strong>500K</strong>
              <span>
                <b>Streams on one song.</b>Starting from zero, with content built around the story in his music.
              </span>
            </span>
          </button>
          <button
            className="bf-artist-feature bf-reactive"
            type="button"
            onClick={(event) => onPlay("kaylee-bell", event.currentTarget)}
            aria-label="Watch Kaylee Bell's story: 31 million TikTok views"
          >
            <span className="bf-surface-shine" aria-hidden="true" />
            <span
              className="bf-artist-photo bf-artist-photo-kaylee"
              role="img"
              aria-label="Kaylee Bell, portrait from her shared Spotify Wrapped"
            >
              <span className="bf-artist-photo-name">Kaylee Bell</span>
              <span className="bf-artist-watch">
                <Play aria-hidden fill="currentColor" />
                Watch the story
              </span>
            </span>
            <span className="bf-artist-result">
              <strong>31M</strong>
              <span>
                <b>TikTok views.</b>A fresh creative approach to the music she already had.
              </span>
            </span>
          </button>
        </div>
        <div className="bf-artist-secondary">
          {STORY_SELECTION.slice(2).map((story) => {
            const artist = videoTestimonials.find((item) => item.id === story.id)!;
            return (
              <button
                className="bf-artist-mini"
                key={story.id}
                type="button"
                onClick={(event) => onPlay(story.id, event.currentTarget)}
                aria-label={`Watch ${artist.artistName}'s story: ${story.result}`}
              >
                <span className="bf-artist-mini-image">
                  <img src={story.thumb} alt="" width={640} height={360} loading="lazy" />
                </span>
                <span className="bf-artist-mini-copy">
                  <b>{artist.artistName}</b>
                  {story.result}
                </span>
                <Play aria-hidden fill="currentColor" />
              </button>
            );
          })}
        </div>
        <div className="bf-stories-more">
          <Link to="/case-studies" target="_blank" rel="noopener noreferrer" className="bf-text-link">
            Explore more artist stories <ArrowUpRight aria-hidden />
          </Link>
        </div>
      </div>
    </section>
  );
}

const FAQS: { q: string; a: string }[] = [
  {
    q: "Is this a course or personal support?",
    a: "Your kickstart is work on your own project, with one-to-one meetings every two weeks and feedback in Slack as you put the strategy into practice. DK Artists manages your agreed advertising while you create and publish content with the team’s direction. Free lifetime DK-MBA Academy membership is included too, so you can keep learning after your kickstart.",
  },
  {
    q: "Do I need a following or released music already?",
    a: "No existing large audience is required. The programme is designed to help artists work towards a real fanbase from their current starting point. If you have not released music yet, we can work on the foundations and preparation around a release. Advertising needs suitable music, content and account access, so campaign activity depends on what is ready.",
  },
  {
    q: "Will I reach 1,000 fans in my chosen period?",
    a: "Building your fanbase is the goal, with content that gives people a reason to care about your music. Specific fan or view counts are not guaranteed within 30, 60 or 90 days. We agree priorities around your starting point and develop the work based on the response.",
  },
  {
    q: "What will I need to do myself?",
    a: "Bring your music and what you’ve tried so far. Make time for the one-to-one meetings and share feedback as the work develops. Your manager will agree the next steps with you, including where you need to be involved to keep your release and content plans moving.",
  },
  {
    q: "Are advertising costs included?",
    a: "Advertising management is included. The money spent showing your ads is separate and funded by you. The campaign budget is agreed with you before spending.",
  },
  {
    q: "Will I work with Damian?",
    a: "Damian provides the strategy for your project. Your own account manager works with you throughout the kickstart, with one-to-one meetings every two weeks. The 60- and 90-day options also include one private strategy call with Damian as a Black Friday bonus.",
  },
  {
    q: "Can I start in January?",
    a: "Yes. Purchase during the promotion, then tell us your preferred start window. The team confirms your exact date, and your purchased service period runs from that agreed date.",
  },
  {
    q: "What happens when my kickstart ends?",
    a: "Your lifetime DK-MBA Academy membership continues. The paid work with the team runs for your chosen duration, with no automatic renewal. Use what you’ve learned from the kickstart to keep building on the content you’ve put out.",
  },
];

const messageLayoutStyles = `
.bf-messages.bf-messages-v4.bf-message-wall{position:relative;isolation:isolate;overflow:hidden;background:#f7f9fc;color:#101114}
.bf-message-wall:before{content:'';position:absolute;inset:0;pointer-events:none;z-index:0;background:radial-gradient(ellipse at 65% 35%,#e3edfa99,transparent 65%),linear-gradient(180deg,#fff,#f5f8fc 55%,#fff)}
.bf-message-wall .bf-message-wall-heading{position:relative;z-index:2;display:flex;justify-content:space-between;align-items:flex-end;gap:35px;margin-bottom:var(--bf-space-content)}
.bf-message-wall .bf-messages-heading{display:block;max-width:650px;margin:0!important}
.bf-message-wall .bf-messages-heading .bf-heading{max-width:650px}
.bf-message-wall .bf-messages-heading>p{max-width:510px;margin-top:var(--bf-space-intro)!important;color:#69788b;font-size:16px;line-height:1.7}
.bf-message-wall-control{display:inline-flex;align-items:center;justify-content:center;gap:8px;flex:none;padding:10px 13px;border:1px solid #ccd8e6;border-radius:7px;background:#ffffffb3;color:#38516d;font-size:13px;font-weight:500;line-height:1.4;cursor:pointer;box-shadow:inset 0 1px #fff,0 3px 12px #193b6205}
.bf-message-wall-control svg{width:14px;height:14px;flex:none}
.bf-message-wall-control:hover{border-color:#86acd5;color:#0066ce}
.bf-message-wall-control:focus-visible{outline:3px solid #0071e3;outline-offset:4px}
.bf-message-wall-static{font-size:13px;color:#687c94;white-space:nowrap}
.bf-message-wall-rows{position:relative;z-index:2;display:grid;gap:0;padding:10px 0 20px;width:100%}
.bf-message-wall-row{position:relative;overflow-x:auto;overflow-y:hidden;width:100%;padding:7px 0 9px;scrollbar-width:none;overscroll-behavior-x:contain;-webkit-mask-image:linear-gradient(90deg,transparent,#000 5%,#000 95%,transparent);mask-image:linear-gradient(90deg,transparent,#000 5%,#000 95%,transparent)}
.bf-message-wall-row::-webkit-scrollbar{display:none}
.bf-message-wall-row[data-manual="true"],.bf-message-wall-row:focus-within{-webkit-mask-image:none;mask-image:none}
.bf-message-wall-track{display:flex;width:max-content;will-change:transform;animation:bf-message-wall-left var(--bf-message-duration,96s) linear infinite;animation-play-state:paused}
.bf-message-wall-row[data-direction="right"] .bf-message-wall-track{animation-name:bf-message-wall-right}
.bf-message-wall[data-running="true"][data-paused="false"] .bf-message-wall-track{animation-play-state:running}
.bf-message-wall-row:hover .bf-message-wall-track,.bf-message-wall-row:focus-within .bf-message-wall-track{animation-play-state:paused!important}
.bf-message-wall-row[data-manual="true"] .bf-message-wall-track{animation:none;transform:none;will-change:auto}
.bf-message-wall-group{display:flex;flex:none;gap:16px;list-style:none;margin:0;padding:0 16px 0 0}
.bf-message-wall-group>li{display:block;flex:none;margin:0;padding:0}
.bf-message-wall .bf-message-button{display:block;position:relative;width:300px;height:145px;padding:5px;border:1px solid #d5dee9;border-radius:10px;overflow:hidden;background:white;box-shadow:inset 0 1px 0 #fff,0 2px 3px #17365c08,0 8px 17px #1939580d;cursor:pointer;transform:none;transition:border-color .2s,box-shadow .2s}
.bf-message-wall .bf-message-button:hover{transform:none;border-color:#8fb8e3;box-shadow:0 3px 6px #17365c09,0 10px 20px #19395817}
.bf-message-wall .bf-message-button:focus-visible{outline:3px solid #0071e3;outline-offset:3px;border-color:#0071e3}
.bf-message-wall .bf-message-window{display:flex;flex-direction:column;justify-content:flex-start;justify-content:safe center;width:100%;height:100%;overflow:hidden;border-radius:5px;background:#fff}
.bf-message-wall .bf-message-button img{display:block;flex:none;width:100%;max-width:100%;height:auto;border-radius:0}
.bf-message-wall .bf-message-button:after{content:'';position:absolute;left:5px;right:5px;bottom:5px;height:15px;pointer-events:none;background:linear-gradient(transparent,#ffffffe6)}
.bf-message-wall-hint{position:relative;z-index:2;font-size:13px;line-height:1.6;color:#718097;margin-top:6px!important;text-align:center}
@keyframes bf-message-wall-left{from{transform:translateX(-33.333333%)}to{transform:translateX(-66.666667%)}}
@keyframes bf-message-wall-right{from{transform:translateX(-33.333333%)}to{transform:translateX(0)}}
@media(max-width:760px){
  .bf-message-wall .bf-message-wall-heading{display:block;margin-bottom:var(--bf-space-content)}
  .bf-message-wall .bf-messages-heading>p{font-size:15px;line-height:1.7;margin-top:var(--bf-space-intro)!important;max-width:490px}
  .bf-message-wall-control{margin-top:20px;padding:9px 12px;font-size:13px}
  .bf-message-wall-static{display:block;margin-top:15px}
  .bf-message-wall-rows{gap:0;padding:5px 0 12px}
  .bf-message-wall-row{padding:6px 0 8px;-webkit-mask-image:linear-gradient(90deg,transparent,#000 4%,#000 96%,transparent);mask-image:linear-gradient(90deg,transparent,#000 4%,#000 96%,transparent)}
  .bf-message-wall-group{gap:13px;padding-right:13px}
  .bf-message-wall .bf-message-button{width:240px;height:130px;padding:4px;border-radius:9px}
  .bf-message-wall-hint{font-size:12px;text-align:left;margin-top:5px!important}
}
@media(prefers-reduced-motion:reduce){
  .bf-message-wall .bf-message-wall-track{animation:none!important;transform:none!important;will-change:auto}
  .bf-message-wall .bf-message-wall-clone{display:none}
  .bf-message-wall .bf-message-button{transition:none}
  .bf-message-wall .bf-message-wall-row{-webkit-mask-image:none;mask-image:none}
}
`;

function Messages() {
  const [open, setOpen] = useState<ArtistMessage | null>(null);
  const [paused, setPaused] = useState(false);
  const [running, setRunning] = useState(false);
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width:760px)").matches,
  );
  const [reducedMotion, setReducedMotion] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion:reduce)").matches,
  );
  const sectionRef = useRef<HTMLElement | null>(null);
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const originalRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const lastTrigger = useRef<HTMLButtonElement | null>(null);
  const leadIds = ["andrew-gemmell-500k", "robb-grant", "ron-querian"];
  const ordered = [
    ...leadIds.map((id) => ARTIST_MESSAGES.find((message) => message.id === id)!).filter(Boolean),
    ...ARTIST_MESSAGES.filter((message) => !leadIds.includes(message.id)),
  ];
  const rowCount = mobile ? 2 : 3;
  const rows = Array.from({ length: rowCount }, (_, index) =>
    ordered.filter((_, messageIndex) => messageIndex % rowCount === index),
  );
  const rowDuration = (messageCount: number, rowIndex: number) =>
    Math.max(96, (messageCount * (mobile ? 253 : 316)) / 20) + rowIndex * 5;

  // Convert the current animated position into native scrolling without moving
  // the visible cards. Keyboard focus and touch can then explore every original.
  const freezeRows = React.useCallback(() => {
    rowRefs.current.forEach((row) => {
      if (!row || row.dataset.manual === "true") return;
      const track = row.querySelector<HTMLElement>(".bf-message-wall-track");
      if (!track) return;
      const transform = window.getComputedStyle(track).transform;
      const offset = transform === "none" ? 0 : -new DOMMatrixReadOnly(transform).m41;
      const position = row.scrollLeft + offset;
      row.dataset.manual = "true";
      row.scrollLeft = position;
    });
  }, []);

  // The repeated groups are identical. Choose an equivalent position in the
  // animation cycle so Play continues from the cards the visitor was reading.
  const resumeRows = React.useCallback(() => {
    rowRefs.current.forEach((row) => {
      if (!row || row.dataset.manual !== "true") return;
      const track = row.querySelector<HTMLElement>(".bf-message-wall-track");
      const group = row.querySelector<HTMLElement>(".bf-message-wall-originals");
      if (!track || !group) return;
      const width = group.getBoundingClientRect().width;
      if (!width) return;
      const fraction = (((row.scrollLeft % width) + width) % width) / width;
      const progress = row.dataset.direction === "right" ? 1 - fraction : fraction;
      track.style.animationDelay = `-${progress * Number(row.dataset.duration || 96)}s`;
      row.scrollLeft = 0;
      delete row.dataset.manual;
    });
  }, []);

  const pauseWall = React.useCallback(() => {
    freezeRows();
    setPaused(true);
  }, [freezeRows]);

  const revealButton = (button: HTMLButtonElement) => {
    const row = button.closest<HTMLElement>(".bf-message-wall-row");
    if (!row) return;
    const bounds = row.getBoundingClientRect();
    const card = button.getBoundingClientRect();
    if (card.left < bounds.left + 12) row.scrollLeft += card.left - bounds.left - 12;
    else if (card.right > bounds.right - 12) row.scrollLeft += card.right - bounds.right + 12;
  };

  React.useLayoutEffect(() => {
    if (paused || reducedMotion) freezeRows();
    else resumeRows();
    if (reducedMotion)
      rowRefs.current.forEach((row) => {
        if (row) row.scrollLeft = 0;
      });
  }, [paused, reducedMotion, rowCount, freezeRows, resumeRows]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const phoneQuery = window.matchMedia("(max-width:760px)");
    const motionQuery = window.matchMedia("(prefers-reduced-motion:reduce)");
    let inView = false;
    const syncRunning = () => setRunning(inView && !motionQuery.matches && document.visibilityState === "visible");
    const syncPhone = () => setMobile(phoneQuery.matches);
    const syncMotion = () => {
      setReducedMotion(motionQuery.matches);
      syncRunning();
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        syncRunning();
      },
      { threshold: 0.05 },
    );
    observer.observe(section);
    phoneQuery.addEventListener("change", syncPhone);
    motionQuery.addEventListener("change", syncMotion);
    document.addEventListener("visibilitychange", syncRunning);
    return () => {
      observer.disconnect();
      phoneQuery.removeEventListener("change", syncPhone);
      motionQuery.removeEventListener("change", syncMotion);
      document.removeEventListener("visibilitychange", syncRunning);
    };
  }, []);

  const messageButton = (message: ArtistMessage, clone: boolean) => (
    <button
      ref={
        clone
          ? undefined
          : (node) => {
              originalRefs.current[message.id] = node;
            }
      }
      type="button"
      className="bf-message-button"
      tabIndex={clone ? -1 : 0}
      aria-label={clone ? undefined : `Read the full message: ${message.alt}`}
      onMouseDown={clone ? (event) => event.preventDefault() : undefined}
      onClick={(event) => {
        pauseWall();
        lastTrigger.current = clone ? originalRefs.current[message.id] : event.currentTarget;
        setOpen(message);
      }}
    >
      <span className="bf-message-window">
        <img
          src={message.src}
          alt={clone ? "" : message.alt}
          width={message.width}
          height={message.height}
          loading="lazy"
          decoding="async"
        />
      </span>
    </button>
  );

  return (
    <section
      ref={sectionRef}
      id="bf-messages"
      className="bf-section bf-messages bf-messages-v4 bf-message-wall"
      data-energy-tone="light"
      data-running={running && !open}
      data-paused={paused || reducedMotion}
      aria-labelledby="messages-title"
    >
      <style>{messageLayoutStyles}</style>
      <div className="bf-wrap bf-message-wall-heading">
        <div className="bf-messages-heading">
          <h2 id="messages-title" className="bf-heading">
            From the artists
            <br />
            <span className="bf-highlight bf-highlight-blue">themselves.</span>
          </h2>
          <p>Their own words about what changed. Select a message to read it in full.</p>
        </div>
        {reducedMotion ? (
          <span className="bf-message-wall-static">Scroll to explore</span>
        ) : (
          <button
            type="button"
            className="bf-message-wall-control"
            onClick={() => (paused ? setPaused(false) : pauseWall())}
            aria-controls="bf-message-wall-rows"
            aria-label={paused ? "Play moving artist messages" : "Pause moving artist messages"}
          >
            {paused ? (
              <Play aria-hidden="true" fill="currentColor" />
            ) : (
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <rect x="4" y="3" width="2.5" height="10" rx=".8" fill="currentColor" />
                <rect x="9.5" y="3" width="2.5" height="10" rx=".8" fill="currentColor" />
              </svg>
            )}
            {paused ? "Play messages" : "Pause messages"}
          </button>
        )}
      </div>
      <div id="bf-message-wall-rows" className="bf-message-wall-rows">
        {rows.map((messages, rowIndex) => (
          <div
            key={`${rowCount}-${rowIndex}`}
            ref={(node) => {
              rowRefs.current[rowIndex] = node;
            }}
            className="bf-message-wall-row"
            data-direction={rowIndex % 2 ? "right" : "left"}
            data-duration={rowDuration(messages.length, rowIndex)}
            onFocusCapture={(event) => {
              const button = (event.target as HTMLElement).closest<HTMLButtonElement>(".bf-message-button");
              if (button) {
                pauseWall();
                revealButton(button);
              }
            }}
            onPointerDown={(event) => {
              if (event.pointerType !== "mouse") pauseWall();
            }}
            onWheel={(event) => {
              if (Math.abs(event.deltaX) > 0 || event.shiftKey) pauseWall();
            }}
          >
            <div
              className="bf-message-wall-track"
              style={{ "--bf-message-duration": `${rowDuration(messages.length, rowIndex)}s` } as React.CSSProperties}
            >
              {[true, false, true].map((clone, copyIndex) => (
                <ul
                  key={copyIndex}
                  className={`bf-message-wall-group ${clone ? "bf-message-wall-clone" : "bf-message-wall-originals"}`}
                  aria-hidden={clone ? true : undefined}
                  aria-label={clone ? undefined : `Artist messages, row ${rowIndex + 1}`}
                >
                  {messages.map((message) => (
                    <li key={message.id}>{messageButton(message, clone)}</li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="bf-wrap">
        <p className="bf-message-wall-hint">Shared with Damian and the team.</p>
      </div>
      <Dialog
        open={!!open}
        onOpenChange={(isOpen) => {
          if (!isOpen) setOpen(null);
        }}
      >
        <DialogContent
          aria-describedby={undefined}
          className="bf-image-dialog"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const trigger = lastTrigger.current;
            if (trigger) {
              trigger.focus({ preventScroll: true });
              revealButton(trigger);
            }
          }}
        >
          <DialogTitle className="sr-only">{open?.alt}</DialogTitle>
          {open && <img src={open.src} alt={open.alt} width={open.width} height={open.height} />}
        </DialogContent>
      </Dialog>
    </section>
  );
}

const durationGuideStyles = `
.bf-duration-guide{scroll-margin-top:30px;margin-top:40px;padding:32px 34px;display:grid;grid-template-columns:.8fr 1.4fr;gap:46px;border:1px solid #ffffff24;border-radius:12px;background:radial-gradient(ellipse at 0 0,#b6c7dc15,transparent 62%),linear-gradient(122deg,#242d39,#19212b 49%,#252f3c);box-shadow:inset 0 1px #ffffff15,0 15px 40px #00000012}
.bf-duration-guide h3{font-size:var(--bf-type-chapter);line-height:1.2;letter-spacing:-.025em;font-weight:600;max-width:330px}
.bf-duration-guide-intro>p{color:#bdc8d7;font-size:16px;line-height:1.7;margin-top:18px;max-width:340px}
.bf-duration-guide-intro .bf-duration-budget{font-size:14px;color:#9eafc3;margin-top:22px}
.bf-duration-guide-list{min-width:0}
.bf-duration-guide-row{display:grid;grid-template-columns:87px minmax(0,1fr);gap:20px;padding:20px 0}
.bf-duration-guide-row:first-child{padding-top:0}
.bf-duration-guide-row+.bf-duration-guide-row{border-top:1px solid #ffffff1a}
.bf-duration-guide-length{font-size:36px;font-weight:550;letter-spacing:-.045em;line-height:1;color:#e7edf5;padding-top:3px}
.bf-duration-guide-length>span{display:block;font-size:13px;font-weight:400;line-height:1.4;letter-spacing:0;color:#a4b3c7;margin-top:5px}
.bf-duration-guide-row h4{font-size:18px;font-weight:550;line-height:1.35;letter-spacing:-.015em;color:#f4f7fb}
.bf-duration-guide-row p{font-size:15px;line-height:1.65;color:#bac7d8;margin-top:8px}
.bf-duration-guide-badge{display:inline-block;font-size:12px;line-height:1.4;color:#a9d1ff;padding:3px 7px;background:#0071e326;border:1px solid #5aaaff33;border-radius:4px;margin-bottom:9px}
.bf-duration-guide-back{display:inline-flex;gap:9px;align-items:center;font-size:14px;color:#c8e3ff;text-decoration:underline;text-underline-offset:5px;margin-top:8px}
.bf-duration-guide-back svg{width:15px;height:15px}
.bf-duration-guide-link{display:inline-block;font-size:13px;color:#0066cc;text-decoration:underline;text-underline-offset:4px;margin-top:8px}
.bf-duration-guide-back:focus-visible,.bf-duration-guide-link:focus-visible{outline:2px solid #62aeff;outline-offset:5px;border-radius:2px}
@media(max-width:900px){.bf-duration-guide{gap:28px;padding:28px;grid-template-columns:.85fr 1.3fr}.bf-duration-guide-row{grid-template-columns:64px minmax(0,1fr);gap:13px}}
@media(max-width:760px){.bf-duration-guide{display:block;padding:25px 22px;margin-top:28px}.bf-duration-guide h3{font-size:30px;max-width:300px}.bf-duration-guide-intro>p{font-size:16px;margin-top:16px}.bf-duration-guide-intro .bf-duration-budget{font-size:14px;margin-top:14px}.bf-duration-guide-list{margin-top:25px}.bf-duration-guide-row{padding:22px 0;grid-template-columns:53px minmax(0,1fr);gap:15px}.bf-duration-guide-length{font-size:32px}.bf-duration-guide-row h4{font-size:17px}.bf-duration-guide-row p{font-size:15px}.bf-duration-guide-back{font-size:14px;margin-top:2px}}
`;

function DurationGuide() {
  return (
    <div id="bf-duration-guide" className="bf-wrap bf-duration-guide" aria-labelledby="bf-duration-guide-title">
      <style>{durationGuideStyles}</style>
      <div className="bf-duration-guide-intro">
        <h3 id="bf-duration-guide-title">
          Which length makes sense <Highlight variant="pale">for you?</Highlight>
        </h3>
        <p>
          Every length includes the same core work on your project. Choose how much time together makes sense for where
          you are now.
        </p>
        <p className="bf-duration-budget">
          Choose a budget you’re comfortable with. The 30-day kickstart includes the full core support, with the
          smallest upfront commitment.
        </p>
      </div>
      <div className="bf-duration-guide-list">
        {DURATIONS.map((item) => (
          <article className="bf-duration-guide-row" key={item.days}>
            <div className="bf-duration-guide-length" aria-hidden="true">
              {item.days}
              <span>days</span>
            </div>
            <div>
              <h4>
                <span className="sr-only">{item.days} days: </span>
                {item.descriptor}
              </h4>
              <p>{item.guidance}</p>
            </div>
          </article>
        ))}
        <a className="bf-duration-guide-back" href="#kickstart" onClick={scrollToKickstart}>
          Back to your duration choice <ArrowRight aria-hidden />
        </a>
      </div>
    </div>
  );
}

const whyNowStyles = `
.bf-why-now{padding:var(--bf-space-section) 0;background:linear-gradient(115deg,#fff 0%,#f8fafc 60%,#eef3f9 100%);color:var(--bf-ink)}
.bf-why-now .bf-why-now-layout{display:grid;grid-template-columns:1fr 1fr;column-gap:72px;align-items:center}
.bf-why-now .bf-why-now-heading{grid-column:1;grid-row:1;align-self:end}
.bf-why-now .bf-why-now-label{color:#56677d;font-size:15px;line-height:1.5;font-weight:500;margin-bottom:20px}
.bf-why-now .bf-heading{font-size:var(--bf-type-section);line-height:1.18;font-weight:600;letter-spacing:-.03em;max-width:520px;text-wrap:balance}
.bf-why-now .bf-why-now-thread{grid-column:2;grid-row:1 / span 2;min-width:0;margin:0;display:flex;flex-direction:column;gap:14px}
.bf-why-now-thread>figcaption{font-size:14px;line-height:1.5;color:#657489;margin:0 0 6px;text-align:center}
.bf-why-now .bf-why-now-message{max-width:88%;padding:18px 21px;margin:0;font-size:17px;line-height:1.55;letter-spacing:-.01em;position:relative;isolation:isolate;transition:transform .7s cubic-bezier(.2,.7,.2,1),box-shadow .7s ease}
.bf-why-now .bf-why-now-message p{margin:0;position:relative;z-index:1}
.bf-why-now-message .bf-surface-shine{z-index:0;mix-blend-mode:normal;background:radial-gradient(ellipse 210px 150px at var(--surface-x) var(--surface-y),#ffffff30,transparent 78%)}
.bf-why-now .bf-why-now-artist{align-self:flex-end;color:#fff;background:linear-gradient(125deg,#0879e9,#0067d2);border:1px solid #0071e3;border-radius:22px 22px 6px 22px;box-shadow:inset 0 1px #ffffff24,0 5px 14px #0067cf0c}
.bf-why-now .bf-why-now-team{align-self:flex-start;color:#253448;background:linear-gradient(130deg,#eef1f5,#e6ebf2);border:1px solid #dce3ec;border-radius:22px 22px 22px 6px;box-shadow:inset 0 1px #fff9,0 5px 14px #223c5505}
.bf-why-now .bf-why-now-offer{grid-column:1;grid-row:2;align-self:start;display:block;margin-top:28px;padding-top:23px;border-top:1px solid #cfd8e5;font-size:17px;line-height:1.6;color:#123c68;font-weight:500;text-decoration:none;max-width:470px}
.bf-why-now-offer strong{color:#0067cf;font-weight:650}
.bf-why-now-offer svg{display:inline-block;width:18px;height:18px;margin-left:8px;vertical-align:-3px;transition:transform .35s}
@media(hover:hover) and (pointer:fine){.bf-why-now-offer:hover svg{transform:translateX(4px)}.bf-why-now-message:hover{transform:translateY(-3px);box-shadow:inset 0 1px #ffffff50,0 12px 26px #16477614}}
@media(max-width:900px){.bf-why-now .bf-why-now-layout{column-gap:38px;row-gap:0}}
@media(max-width:760px){.bf-why-now .bf-why-now-layout{display:flex;flex-direction:column;align-items:stretch;gap:0}.bf-why-now .bf-why-now-heading{align-self:stretch}.bf-why-now .bf-why-now-label{font-size:14px;margin-bottom:18px}.bf-why-now .bf-heading{max-width:420px}.bf-why-now .bf-why-now-thread{margin-top:var(--bf-space-content);gap:12px}.bf-why-now-thread>figcaption{text-align:left;margin-bottom:3px;font-size:13px}.bf-why-now .bf-why-now-message{max-width:88%;padding:15px 18px;font-size:16px;line-height:1.55}.bf-why-now .bf-why-now-offer{font-size:16px;margin-top:28px;padding-top:22px;align-self:stretch}}
@media(prefers-reduced-motion:reduce){.bf-why-now-offer svg,.bf-why-now-message{transition:none}.bf-why-now-offer:hover svg,.bf-why-now-message:hover{transform:none}}
`;

function WhyNow() {
  const exchange = [
    { side: "artist", text: "Maybe the next release will be the one." },
    { side: "team", text: "What are you going to do differently with it?" },
    { side: "artist", text: "Honestly? That’s the bit I need help with." },
    {
      side: "team",
      text: "Then let’s build the plan together. Your manager will work through it with you, helping you use the response to decide where to push next.",
    },
  ];
  return (
    <section className="bf-section bf-why-now" data-energy-tone="light" aria-labelledby="bf-why-now-title">
      <style>{whyNowStyles}</style>
      <div className="bf-wrap bf-why-now-layout">
        <div className="bf-why-now-heading">
          <p className="bf-why-now-label">Why start now?</p>
          <h2 id="bf-why-now-title" className="bf-heading">
            What are you going to do <Highlight>differently</Highlight> this time?
          </h2>
        </div>
        <figure className="bf-why-now-thread" aria-labelledby="bf-why-now-caption">
          <figcaption id="bf-why-now-caption">Sound familiar?</figcaption>
          {exchange.map((message, index) => (
            <div key={index} className={`bf-why-now-message bf-why-now-${message.side} bf-reactive`}>
              <span className="bf-surface-shine" aria-hidden="true" />
              <p>{message.text}</p>
            </div>
          ))}
        </figure>
        <a className="bf-why-now-offer" href="#kickstart" onClick={scrollToKickstart}>
          Make this the point where you change the approach.{" "}
          <strong>Black Friday kickstarts from {formatUsd(LOWEST_PRICE)}.</strong> <ArrowRight aria-hidden />
        </a>
      </div>
    </section>
  );
}

const nutshellStyles = `
.bf-page .bf-duration{justify-content:flex-start;min-height:131px}
.bf-duration-description{display:block;max-width:128px;min-height:2.6em;margin-top:9px;font-size:11px;line-height:1.3;font-weight:500;color:#53657b;text-wrap:balance}
.bf-duration[aria-checked=true] .bf-duration-description{color:#0065cc}
.bf-page .bf-purchase-card>.bf-duration-guidance{min-height:4.8em;margin-top:16px;font-size:13px;line-height:1.6;color:#586779}
@media(max-width:760px){.bf-page .bf-duration{min-height:139px}.bf-duration-description{min-height:3.9em;font-size:11px;line-height:1.3}.bf-page .bf-purchase-card>.bf-duration-guidance{min-height:6.4em}}
.bf-page .bf-offer-heading{margin-bottom:40px;max-width:940px}
.bf-offer-heading>.bf-audio-badge{margin-bottom:20px}
.bf-page .bf-offer-heading>.bf-heading{font-size:var(--bf-type-section);font-weight:600;line-height:1.2;letter-spacing:-.03em;max-width:900px}
.bf-page .bf-offer-heading>p{font-size:var(--bf-type-body);line-height:1.7;color:#b5c2d3;max-width:64ch;margin-top:20px}
.bf-page .bf-purchase-grid{grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:26px 64px;align-items:start}
.bf-purchase-grid>.bf-purchase-card{grid-column:2;grid-row:1 / span 2}
.bf-page .bf-purchase-card>.bf-stripe-security{display:flex;align-items:center;justify-content:center;gap:6px;margin:12px 0 0!important;font-size:12px!important;line-height:1.5;color:#64748b}
.bf-stripe-security svg{width:13px;height:13px;flex:none;stroke-width:1.8}
.bf-stripe-security strong{font-weight:600;color:#475569}
.bf-page .bf-purchase-grid>.bf-after-purchase{grid-column:1;grid-row:2;margin:0;padding-top:24px;border-color:#b8d3f426;color:#b4c2d3}
.bf-purchase-grid>.bf-after-purchase>strong{color:#f5f8fc;font-size:16px}
.bf-purchase-grid>.bf-after-purchase .bf-onboarding-steps li{color:#b4c2d3}
.bf-purchase-grid>.bf-after-purchase .bf-onboarding-steps li:before{background:#b5d7ff14;color:#a6d1ff}
.bf-page .bf-nutshell{min-width:0;margin:0;padding:23px 0 0}
.bf-nutshell-heading{margin-bottom:25px}
.bf-page .bf-nutshell-heading h3{font-size:25px;font-weight:550;line-height:1.25;letter-spacing:-.025em;color:#f5f8fc}
.bf-nutshell-heading>p{font-size:14px;line-height:1.6;color:#aebed0;margin-top:9px}
.bf-nutshell-list{display:grid;grid-template-columns:1fr;gap:20px;list-style:none;margin:0;padding:0}
.bf-nutshell-list li{display:flex;align-items:flex-start;gap:13px;min-width:0;color:#b4c2d3;font-size:16px;line-height:1.6}
.bf-nutshell-check{flex:none;display:grid;place-items:center;width:24px;height:24px;margin-top:1px;border:1px solid #72b8ff52;border-radius:6px;background:linear-gradient(145deg,#0071e332,#0071e312);box-shadow:inset 0 1px 0 #c6e3ff0d}
.bf-nutshell-check svg{width:14px;height:14px;color:#75bbff;stroke-width:2.4}
.bf-nutshell-list strong{font-weight:550;color:#f5f8fc}
.bf-page .bf-meta-badge{font-family:inherit;letter-spacing:0;font-size:12px;line-height:1.4;padding:5px 8px;color:#cbd8e8;white-space:nowrap}
.bf-meta-badge strong{font-weight:600;color:#f4f8ff;margin-left:4px}
.bf-nutshell-list .bf-meta-badge{margin-top:7px}
.bf-nutshell .bf-offer-help{margin-top:27px!important;line-height:1.6}
.bf-ads-credential{display:flex;align-items:center;flex-wrap:wrap;gap:10px;margin:22px 0 18px;color:#b0c2d7;font-size:14px;line-height:1.5}
@media(min-width:761px) and (max-width:1050px){.bf-page .bf-purchase-grid{gap:26px 32px;grid-template-columns:minmax(0,.95fr) minmax(0,1.05fr)}.bf-nutshell-list li{font-size:15px;gap:11px}.bf-nutshell-list{gap:18px}.bf-page .bf-purchase-card{padding:25px}}
@media(max-width:760px){.bf-purchase-grid>.bf-purchase-card,.bf-page .bf-purchase-grid>.bf-after-purchase{grid-column:auto;grid-row:auto}}
@media(max-width:760px){.bf-page .bf-offer-heading{margin-bottom:29px}.bf-offer-heading>.bf-audio-badge{margin-bottom:17px}.bf-page .bf-offer-heading>.bf-heading{font-size:clamp(31px,8vw,38px)}.bf-page .bf-offer-heading>p{font-size:16px;margin-top:17px}.bf-page .bf-purchase-grid{display:grid;grid-template-columns:1fr;gap:31px}.bf-page .bf-nutshell{padding:0;margin:0}.bf-nutshell-heading{margin-bottom:22px}.bf-page .bf-nutshell-heading h3{font-size:23px}.bf-nutshell-list{gap:18px}.bf-nutshell-list li{font-size:15px;line-height:1.6;gap:11px}.bf-nutshell-check{width:22px;height:22px;margin-top:2px}.bf-nutshell .bf-offer-help{margin-top:23px!important}.bf-ads-credential{margin-top:19px}}
`;

function MetaExperienceBadge() {
  return (
    <span className="bf-occasion-badge bf-meta-badge">
      Previously at <strong>Meta</strong>
    </span>
  );
}

function KickstartInclusions() {
  const items = [
    { title: "One-to-one meetings", detail: "with your account manager every two weeks" },
    { title: "Content strategy", detail: "built around you and your music" },
    { title: "Content plans", detail: "so you know what to make next" },
    { title: "A comprehensive plan for your next release", detail: "which we put into action together." },
    { title: "24/7 Slack access", detail: "to your team’s channel throughout your kickstart" },
    { title: "Strategy from Damian", detail: "for your artist project" },
    {
      title: "A release strategy for the next 12 months",
      detail: "so each release builds towards a sustainable fanbase.",
    },
    { title: "Content shoot support", detail: "with planning and setting up your shoots" },
    { title: "Advertising management", detail: "from our ads manager", meta: true },
    { title: "Accountability and support", detail: "as you put the work into practice" },
  ];
  return (
    <div className="bf-nutshell" aria-labelledby="bf-inclusions-title">
      <div className="bf-nutshell-heading">
        <h3 id="bf-inclusions-title">Everything included.</h3>
        <p>Yours with any duration you choose.</p>
      </div>
      <ul className="bf-nutshell-list">
        {items.map((item) => (
          <li key={item.title}>
            <span className="bf-nutshell-check" aria-hidden="true">
              <Check />
            </span>
            <div>
              <strong>{item.title}</strong> {item.detail}
              {item.meta && (
                <>
                  <br />
                  <MetaExperienceBadge />
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function getCampaignStorage(): Storage | undefined {
  try {
    return typeof window !== "undefined" ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

function PurchasePanel() {
  const [selected, setSelected] = useState<DurationId>(() => readStoredSelection(getCampaignStorage()));
  const option = getDuration(selected);
  const checkoutUrl = getCheckoutUrl(selected);
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const choose = (id: DurationId, focus = false) => {
    setSelected(id);
    storeSelection(getCampaignStorage(), id);
    if (focus) refs.current[id]?.focus();
  };
  const onKey = (e: React.KeyboardEvent, idx: number) => {
    let next = -1;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") next = (idx + 1) % DURATIONS.length;
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = (idx - 1 + DURATIONS.length) % DURATIONS.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = DURATIONS.length - 1;
    if (next >= 0) {
      e.preventDefault();
      choose(DURATIONS[next].id, true);
    }
  };
  return (
    <section id="bf-offer" className="bf-section bf-purchase" data-energy-tone="dark" aria-labelledby="kickstart-title">
      <style>{nutshellStyles}</style>
      <div className="bf-wrap">
        <div className="bf-offer-heading">
          <div className="bf-audio-badge">
            <div aria-hidden="true">
              <AudioWaveform />
            </div>
            <span className="bf-occasion-badge">BLACK FRIDAY</span>
          </div>
          <h2 id="kickstart-title" className="bf-heading">
            Let’s get to work on your <Highlight variant="pale">first 1,000 fans.</Highlight>
          </h2>
          <p>Give the music you’re already making a focused push, with a plan you can keep building on.</p>
        </div>
      </div>
      <div className="bf-wrap bf-purchase-grid">
        <KickstartInclusions />
        <div id="kickstart" className="bf-purchase-card">
          <h3>Choose your kickstart.</h3>
          <p>Choose how long we work on your music together.</p>
          <p id="duration-label" className="sr-only">
            Choose your kickstart length
          </p>
          <div role="radiogroup" aria-labelledby="duration-label" className="bf-duration-options">
            {DURATIONS.map((duration, idx) => (
              <button
                key={duration.id}
                ref={(el) => (refs.current[duration.id] = el)}
                type="button"
                role="radio"
                aria-checked={duration.id === selected}
                tabIndex={duration.id === selected ? 0 : -1}
                onClick={() => choose(duration.id)}
                onKeyDown={(e) => onKey(e, idx)}
                className="bf-duration"
              >
                <span className="bf-duration-label">{duration.days} days</span>
                <span className="bf-duration-price">{formatUsd(duration.priceUsd)}</span>
                <span className="bf-duration-description">{duration.descriptor}</span>
              </button>
            ))}
          </div>
          <p className="bf-duration-guidance" aria-live="polite">
            {option.guidance}
          </p>
          <a className="bf-duration-guide-link" href="#bf-duration-guide">
            Which length should I choose?
          </a>
          <div className="bf-price-block" aria-live="polite" aria-atomic="true">
            <p className="bf-price-label">Your {option.days}-day Artist Kickstart</p>
            <div className="bf-total-row">
              <del className="bf-was">
                <span className="sr-only">Original price </span>
                {formatUsd(option.originalUsd)}
              </del>
              <span className="bf-now">
                <span className="sr-only">Black Friday price </span>
                {formatUsd(option.priceUsd)} <span className="bf-currency">USD</span>
              </span>
            </div>
            <p className="bf-save">
              Save {formatUsd(getSavings(option))} <span>{getSavingsPercent(option)}% off</span>
            </p>
            <p className="bf-payment-note">One payment upfront. No automatic renewal.</p>
            <a className="bf-price-bonus-link" href="#bf-bonuses">
              Plus your Black Friday bonuses below <ArrowRight aria-hidden />
            </a>
          </div>
          {checkoutUrl ? (
            <a href={checkoutUrl} className="bf-checkout">
              Continue with {option.days} days <ArrowRight aria-hidden />
            </a>
          ) : (
            <>
              <button type="button" className="bf-checkout" disabled aria-describedby="checkout-preview-note">
                Continue with {option.days} days <ArrowRight aria-hidden />
              </button>
              <p id="checkout-preview-note" className="bf-preview-note">
                Preview: checkout opens once payment links are connected.
              </p>
            </>
          )}
          {checkoutUrl && (
            <p className="bf-stripe-security">
              <LockKeyhole aria-hidden="true" />
              <span>
                Secure checkout with <strong>Stripe</strong>
              </span>
            </p>
          )}
        </div>
        <div className="bf-after-purchase">
          <strong>What happens after you buy?</strong>
          <ol className="bf-onboarding-steps">
            <li>Receive your confirmation and welcome email.</li>
            <li>Tell us your preferred start window.</li>
            <li>Agree your exact date and arrange onboarding with the team.</li>
          </ol>
          <p className="bf-offer-help">
            Questions before you choose?{" "}
            <a href="https://wa.me/17372538939" target="_blank" rel="noopener noreferrer">
              WhatsApp us
            </a>
          </p>
        </div>
      </div>
      <div id="bf-bonuses" className="bf-wrap bf-purchase-bonuses" aria-labelledby="bf-bonuses-title">
        <h3 id="bf-bonuses-title" className="bf-heading">
          Plus, your Black Friday <Highlight variant="pale">bonuses.</Highlight>
        </h3>
        <p className="bf-bonuses-intro">These are on top of your work with the team.</p>
        <div className="bf-bonus-grid">
          <article className="bf-bonus-academy bf-reactive">
            <span className="bf-surface-shine" aria-hidden="true" />
            <div className="bf-membership-art" aria-hidden="true">
              <div className="bf-membership-pass">
                <strong>DK-MBA</strong>
                <span>Academy</span>
                <small>Lifetime membership</small>
              </div>
            </div>
            <p className="bf-bonus-eligibility">Free with every kickstart</p>
            <h4>
              Lifetime DK-MBA
              <br />
              Academy membership.
            </h4>
            <div className="bf-bonus-value">
              <span>
                Usually <s>$499</s>
              </span>
              <strong>Free</strong>
            </div>
            <p>Keep learning long after your kickstart. Your Academy membership is yours for life.</p>
            <div className="bf-bonus-confirmation">
              <Check aria-hidden />
              <span>Included with all three: 30, 60 and 90 days</span>
            </div>
          </article>
          <article className="bf-bonus-damian bf-reactive">
            <span className="bf-surface-shine" aria-hidden="true" />
            <div className="bf-private-call-art" aria-hidden="true">
              <div className="bf-private-call-window">
                <span>Private strategy call</span>
                <strong>You + Damian</strong>
                <svg viewBox="0 0 240 34" fill="none" stroke="#d6ebff" strokeWidth="2.5" strokeLinecap="round">
                  {[5, 8, 4, 13, 19, 9, 23, 31, 16, 9, 15, 27, 20, 11, 6, 10, 19, 28, 16, 11, 6, 9, 15, 8, 4].map(
                    (height, index) => (
                      <line
                        key={index}
                        x1={index * 9 + 10}
                        x2={index * 9 + 10}
                        y1={17 - height / 2}
                        y2={17 + height / 2}
                        opacity={0.4 + (index % 4) * 0.18}
                      />
                    ),
                  )}
                </svg>
              </div>
            </div>
            <p className="bf-bonus-eligibility">An extra for 60 and 90 days</p>
            <h4>
              One private strategy
              <br />
              call with Damian.
            </h4>
            <p className="bf-bonus-call-intro">Your music. His perspective.</p>
            <p>Talk through your project directly with Damian and get his take on where to focus next.</p>
            <div className="bf-bonus-confirmation">
              <Check aria-hidden />
              <span>Included with both 60 and 90 days</span>
            </div>
          </article>
        </div>
      </div>
      <DurationGuide />
    </section>
  );
}

function StickyMobileKickstart() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const elements = ["bf-hero-cta", "kickstart", "bf-close", "bf-footer"].map((id) => document.getElementById(id));
    const visible = new Map<Element, boolean>();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => visible.set(entry.target, entry.isIntersecting));
      setShow(!elements.some((el) => el && visible.get(el)));
    });
    elements.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return (
    <div className={`bf-mobile-sticky ${show ? "bf-visible" : ""}`} aria-hidden={!show}>
      <a href="#kickstart" onClick={scrollToKickstart} className="bf-cta" tabIndex={show ? 0 : -1}>
        Choose your kickstart <ArrowRight aria-hidden />
      </a>
    </div>
  );
}

const directContactStyles = `
.bf-direct-contact{position:fixed;right:20px;bottom:20px;z-index:45;display:flex;flex-direction:column;align-items:flex-end;gap:7px;font-family:Geist,Inter,system-ui,sans-serif}.bf-direct-contact-links{display:flex;gap:7px}.bf-direct-contact a{display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:12px 16px;min-height:46px;border-radius:24px;background:#0071e3;color:white;box-shadow:0 5px 20px #00376e26;font-size:13px;font-weight:550;text-decoration:none;border:1px solid #ffffff30;transition:background .18s}.bf-direct-contact a:hover{background:#0062c4}.bf-direct-contact svg{width:19px;height:19px;flex-shrink:0}.bf-direct-contact-note{font-size:11px;color:#41516a;background:#fff;padding:3px 8px;border-radius:12px;box-shadow:0 1px 5px #001f4910}.bf-direct-contact a:focus-visible{outline:3px solid #0071e3;outline-offset:4px}@media(max-width:760px){.bf-direct-contact{right:12px;bottom:calc(var(--bf-contact-lift,0px) + 12px + env(safe-area-inset-bottom,0px));gap:5px}.bf-direct-contact a{padding:10px 13px;min-height:43px;font-size:12px}.bf-direct-contact-note{font-size:10px}}@media(prefers-reduced-motion:reduce){.bf-direct-contact a{transition:none}}
.bf-direct-contact[hidden]{display:none}
@media(max-width:760px){.bf-direct-contact a{width:48px;height:48px;min-height:48px;padding:0;border-radius:50%;gap:0}.bf-direct-contact a>span,.bf-direct-contact-note{display:none}.bf-direct-contact svg{width:22px;height:22px}}
`;

function DirectContact() {
  const [lift, setLift] = useState(0);
  const [hiddenForCheckout, setHiddenForCheckout] = useState(false);
  useEffect(() => {
    const sticky = document.querySelector<HTMLElement>(".bf-mobile-sticky");
    const checkout = document.querySelector<HTMLElement>("#kickstart .bf-checkout");
    let checkoutVisible = false;
    const measure = () => {
      const mobile = window.innerWidth <= 760;
      setLift(
        mobile && sticky?.classList.contains("bf-visible") ? Math.ceil(sticky.getBoundingClientRect().height) : 0,
      );
      setHiddenForCheckout(mobile && checkoutVisible);
    };
    const resize = new ResizeObserver(measure);
    const mutation = new MutationObserver(measure);
    const intersection = new IntersectionObserver(([entry]) => {
      checkoutVisible = entry.isIntersecting;
      measure();
    });
    if (sticky) {
      resize.observe(sticky);
      mutation.observe(sticky, { attributes: true, attributeFilter: ["class"] });
    }
    if (checkout) intersection.observe(checkout);
    window.addEventListener("resize", measure, { passive: true });
    measure();
    return () => {
      resize.disconnect();
      mutation.disconnect();
      intersection.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  const icon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5Z" />
    </svg>
  );
  return (
    <>
      <style>{directContactStyles}</style>
      <aside
        className="bf-direct-contact"
        hidden={hiddenForCheckout}
        style={{ "--bf-contact-lift": `${lift}px` } as React.CSSProperties}
        aria-label="Ask the DK Artists team a question"
      >
        <div className="bf-direct-contact-links">
          <a
            href="https://wa.me/17372538939"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Ask us on WhatsApp, opens in a new tab"
          >
            {icon}
            <span>WhatsApp us</span>
          </a>
        </div>
        <span className="bf-direct-contact-note">Questions? Real people, no AI replies.</span>
      </aside>
    </>
  );
}

const surfaceRefinementStyles = `
.bf-duration-options{padding:6px;gap:6px;border-radius:11px;background:#e9edf3;box-shadow:inset 0 2px 5px #1d365c17,inset 0 -1px 0 #fff;margin-top:27px;overflow:visible}
.bf-duration{border:1px solid transparent;background:transparent;border-radius:8px;transition:background .35s,box-shadow .35s,transform .35s;min-height:90px}
.bf-duration[aria-checked=true]{background:linear-gradient(145deg,#fff,#f3f8ff);border:1px solid #0071e3;padding:16px 5px 10px;box-shadow:0 2px 3px #17386214,0 7px 14px #1e4c7e1f,inset 0 1px 0 #fff;transform:translateY(-1px)}
.bf-duration:active{transform:translateY(1px)}
.bf-recommended{top:-13px;border-radius:5px;padding:1px 7px;box-shadow:0 2px 5px #00478020}
.bf-project-paper{box-shadow:0 1px 2px #1e38531a,0 8px 17px #26496b0c,0 28px 65px #23436c1d}
.bf-project-slack{box-shadow:0 2px 3px #102e4e1f,0 15px 26px #193d6f2b,inset 0 1px 0 #dcecff30}
@media(max-width:760px){.bf-duration-options{padding:5px;gap:4px}.bf-duration{min-height:86px}.bf-duration-price{font-size:14px}}

.dk-workshop{display:flex;flex-direction:column}.dk-workshop-header{order:0}.dk-workshop-sheet-stack{order:1}.dk-workshop-tabs{order:2}.dk-workshop-results{order:3;display:grid;grid-template-columns:1fr 1fr auto;align-items:center;gap:15px;margin:19px 2px 0;padding:0 0 2px}.dk-workshop-results>div{display:flex;flex-direction:column;gap:5px}.dk-workshop-results strong{font-size:25px;letter-spacing:-.04em;line-height:1;font-weight:550;color:#e6f2ff}.dk-workshop-results span{font-size:12px;color:#8da6c5;line-height:1.4}.dk-workshop-results button{display:flex;align-items:center;gap:5px;font-size:12px;color:#a8caef;padding:8px 0;cursor:pointer}.dk-workshop-results button svg{width:15px;height:15px}.dk-workshop-results button span{text-decoration:underline;text-underline-offset:3px;color:#a8caef}
.bf-content-results-dialog{max-width:530px!important;max-height:90dvh;padding:24px;overflow-y:auto;background:#f8fafc;color:#152339}.bf-content-results-dialog h2{font-size:19px;font-weight:600}.bf-content-results-dialog p{font-size:12px;color:#65748a}.bf-content-results-dialog img{display:block;width:100%;height:auto;border-radius:8px}
.bf-reactive{--surface-x:50%;--surface-y:50%;--surface-active:0;position:relative}.bf-surface-shine{position:absolute;inset:0;z-index:4;border-radius:inherit;pointer-events:none;background:radial-gradient(ellipse 380px 260px at var(--surface-x) var(--surface-y),#bcdfff16,transparent 72%);opacity:var(--surface-active);transition:opacity .7s ease;mix-blend-mode:screen}.bf-project-paper .bf-surface-shine,.bf-bonus-academy .bf-surface-shine{background:radial-gradient(ellipse 250px 190px at var(--surface-x) var(--surface-y),#8bb7e518,transparent 75%);mix-blend-mode:multiply}
.bf-recognition-offer{background:radial-gradient(ellipse at 20% 12%,#fff 25%,#f4f5f7 64%,#e9edf3 100%)}.bf-recognition-offer:after,.bf-working-stage:after,.bf-stories:after{content:'';position:absolute;inset:0;pointer-events:none;background-image:url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20width%3D%27256%27%20height%3D%27256%27%20viewBox%3D%270%200%20256%20256%27%3E%3Cfilter%20id%3D%27grain%27%20x%3D%270%27%20y%3D%270%27%20width%3D%27100%25%27%20height%3D%27100%25%27%20color-interpolation-filters%3D%27sRGB%27%3E%3CfeTurbulence%20type%3D%27fractalNoise%27%20baseFrequency%3D%27.82%27%20numOctaves%3D%271%27%20seed%3D%2717%27%20stitchTiles%3D%27stitch%27%2F%3E%3CfeColorMatrix%20type%3D%27saturate%27%20values%3D%270%27%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%27100%25%27%20height%3D%27100%25%27%20filter%3D%27url%28%23grain%29%27%2F%3E%3C%2Fsvg%3E");background-size:128px 128px;background-repeat:repeat;opacity:.014;z-index:0}.bf-stories:after{opacity:.024}.bf-content-stage:after,.bf-creative-atmosphere:after{opacity:.026}.bf-working-stage{background:radial-gradient(ellipse at 9% 80%,#dce2eb 0%,#f0f2f6 32%,#fff 78%)}.bf-compare-table{box-shadow:0 16px 44px #203d610a}.bf-fit{border:0!important}.bf-fit-column{background:linear-gradient(145deg,#f1f5fb,#e9eef6)}.bf-fit-column+.bf-fit-column{background:linear-gradient(145deg,#f8f8f9,#eeeeef)}.bf-faq{background:linear-gradient(135deg,#fff 15%,#f5f6f8)}
.bf-membership-pass,.bf-private-call-window{transition:transform 1.1s cubic-bezier(.2,.7,.2,1),box-shadow 1.1s}.bf-bonus-academy:hover .bf-membership-pass{transform:rotate(-3deg) translateY(-4px);box-shadow:inset 0 1px 0 #dbebff66,0 23px 38px #1e49703b}.bf-bonus-damian:hover .bf-private-call-window{transform:rotate(1deg) translateY(-4px)}.bf-project-paper{transition:transform 1.1s cubic-bezier(.2,.7,.2,1),box-shadow 1.1s}.bf-project-composition:hover .bf-project-paper{transform:translateY(-3px);box-shadow:0 3px 5px #294c6c05,0 27px 70px #23436c21}.bf-project-slack{transition:transform 1.2s cubic-bezier(.2,.7,.2,1)}.bf-project-composition:hover .bf-project-slack{transform:translateY(2px)}.bf-reach-art>svg{animation:bf-reach-breathe 12s ease-in-out infinite alternate;transform-origin:center}.bf-artist-photo img{transition:transform 1.5s cubic-bezier(.2,.7,.2,1)}.bf-artist-feature:hover .bf-artist-photo img{transform:scale(1.025)}
@keyframes bf-reach-breathe{from{transform:translateY(2px);opacity:.9}to{transform:translateY(-3px);opacity:1}}
@media(max-width:1000px) and (min-width:761px){.bf-content-stage .bf-wrap{grid-template-columns:1fr;gap:32px}.bf-content-intro{max-width:700px}.bf-content-intro h2,.bf-content-intro>p{max-width:none}.bf-content-stage .dk-workshop{max-width:650px;margin:0 auto}.bf-offer-v3 .bf-content-stage{padding-top:55px;padding-bottom:55px}.dk-workshop .dk-workshop-sheet{grid-template-columns:138px minmax(0,1fr)}.dk-workshop .dk-workshop-guidance{font-size:13px;min-height:65px}.dk-workshop .dk-workshop-question{font-size:25px}}
@media(max-width:760px){.dk-workshop-results{gap:10px;grid-template-columns:1fr 1fr;position:relative;padding-bottom:25px;margin-top:18px}.dk-workshop-results strong{font-size:25px}.dk-workshop-results button{position:absolute;bottom:-9px;right:0}.dk-workshop-results span{font-size:10px}.bf-content-results-dialog{padding:21px 15px}.bf-project-composition:hover .bf-project-paper{transform:none}.bf-project-composition:hover .bf-project-slack{transform:none}}
@media(hover:none),(pointer:coarse),(prefers-reduced-motion:reduce){.bf-surface-shine{display:none}.bf-membership-pass,.bf-private-call-window,.bf-project-paper,.bf-project-slack,.bf-artist-photo img{transition:none!important}.bf-bonus-academy:hover .bf-membership-pass{transform:rotate(-6deg)}.bf-bonus-damian:hover .bf-private-call-window{transform:rotate(4deg)}.bf-artist-feature:hover .bf-artist-photo img{transform:none}.bf-reach-art>svg{animation:none}}
`;

/** Pointer light follows at a low-pass speed, independent of scroll or frame rate. */
function useSurfaceLight(mainRef: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const root = mainRef.current;
    if (!root) return;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce), (pointer: coarse), (hover: none)");
    let active: HTMLElement | null = null;
    let bounds: DOMRect | null = null;
    let frame = 0,
      last = 0,
      x = 50,
      y = 50,
      tx = 50,
      ty = 50;
    const clear = () => {
      if (active) active.style.setProperty("--surface-active", "0");
      active = null;
      bounds = null;
      cancelAnimationFrame(frame);
      frame = 0;
      last = 0;
    };
    const draw = (now: number) => {
      frame = 0;
      if (!active) return;
      const dt = Math.min((now - (last || now - 16.7)) / 1000, 0.05);
      last = now;
      const ease = 1 - Math.exp(-5.5 * dt);
      x += (tx - x) * ease;
      y += (ty - y) * ease;
      active.style.setProperty("--surface-x", `${x.toFixed(2)}%`);
      active.style.setProperty("--surface-y", `${y.toFixed(2)}%`);
      if (Math.abs(tx - x) + Math.abs(ty - y) > 0.08) frame = requestAnimationFrame(draw);
      else last = 0;
    };
    const move = (event: PointerEvent) => {
      if (preference.matches) return;
      const target = event.target instanceof Element ? event.target.closest<HTMLElement>(".bf-reactive") : null;
      if (!target || !root.contains(target)) {
        clear();
        return;
      }
      if (target !== active) {
        clear();
        active = target;
        bounds = target.getBoundingClientRect();
        x = 50;
        y = 50;
        active.style.setProperty("--surface-x", "50%");
        active.style.setProperty("--surface-y", "50%");
        active.style.setProperty("--surface-active", "1");
      }
      if (!bounds) return;
      tx = Math.max(0, Math.min(100, ((event.clientX - bounds.left) / bounds.width) * 100));
      ty = Math.max(0, Math.min(100, ((event.clientY - bounds.top) / bounds.height) * 100));
      if (!frame) frame = requestAnimationFrame(draw);
    };
    root.addEventListener("pointermove", move, { passive: true });
    root.addEventListener("pointerleave", clear);
    window.addEventListener("scroll", clear, { passive: true });
    window.addEventListener("resize", clear, { passive: true });
    preference.addEventListener("change", clear);
    return () => {
      clear();
      root.removeEventListener("pointermove", move);
      root.removeEventListener("pointerleave", clear);
      window.removeEventListener("scroll", clear);
      window.removeEventListener("resize", clear);
      preference.removeEventListener("change", clear);
    };
  }, [mainRef]);
}

/** One-time entrances enhance visible content without hiding it in the stylesheet. */
function usePageEntrances(mainRef: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const root = mainRef.current;
    if (!root || !("IntersectionObserver" in window) || !("animate" in Element.prototype)) return;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (preference.matches) return;

    const selectors = [
      ".bf-recognition-offer .bf-heading",
      ".bf-recognition-lead",
      ".bf-recognition-offer .bf-spotify-phone",
      ".bf-content-intro",
      ".dk-workshop",
      ".bf-working-copy",
      ".bf-project-composition",
      ".bf-reach-social",
      ".bf-amplify-stage .bf-wrap > div:last-child",
      ".bf-comparison .bf-center-heading",
      ".bf-cv4-workbench",
      ".bf-fit-column",
      ".bf-stories .bf-center-heading",
      ".bf-artist-features",
      ".bf-artist-secondary",
      ".bf-message-wall-heading",
      ".bf-why-now-heading",
      ".bf-why-now-thread > figcaption",
      ".bf-why-now-message",
      ".bf-why-now-offer",
      ".bf-offer-heading",
      ".bf-nutshell",
      ".bf-purchase-bonuses > .bf-heading",
      ".bf-bonus-grid",
      ".bf-duration-guide-intro",
      ".bf-duration-guide-list",
      ".bf-faq-intro",
      ".bf-faq-list",
      ".bf-close .bf-wrap > div",
    ];
    const targets = [...root.querySelectorAll<HTMLElement>(selectors.join(","))];
    const seen = new Set<HTMLElement>();
    const active = new Map<HTMLElement, Animation>();
    let suppressUntil = window.location.hash ? performance.now() + 1000 : 0;
    const cancelAll = () => {
      active.forEach((animation) => animation.cancel());
      active.clear();
    };
    const observer = new IntersectionObserver(
      (entries) => {
        let stagger = 0;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const element = entry.target as HTMLElement;
          observer.unobserve(element);
          if (seen.has(element)) return;
          seen.add(element);
          if (preference.matches || performance.now() < suppressUntil || element.contains(document.activeElement))
            return;
          const animation = element.animate(
            [
              { opacity: 0.12, translate: "0 12px" },
              { opacity: 1, translate: "0 0" },
            ],
            {
              duration: 560,
              delay: Math.min(stagger++ * 60, 120),
              easing: "cubic-bezier(.2,.7,.2,1)",
              fill: "backwards",
            },
          );
          active.set(element, animation);
          animation.onfinish = () => {
            active.delete(element);
            animation.cancel();
          };
          animation.oncancel = () => {
            active.delete(element);
          };
        });
      },
      { threshold: 0, rootMargin: "0px 0px 40px 0px" },
    );

    targets.forEach((element) => {
      const bounds = element.getBoundingClientRect();
      if (bounds.top < window.innerHeight && bounds.bottom > 0) seen.add(element);
      else observer.observe(element);
    });
    const revealInteracted = (event: Event) => {
      if (!(event.target instanceof Node)) return;
      targets.forEach((element) => {
        if (!element.contains(event.target as Node)) return;
        seen.add(element);
        observer.unobserve(element);
        active.get(element)?.cancel();
      });
    };
    const skipAnchorEntrance = () => {
      suppressUntil = performance.now() + 1200;
      cancelAll();
    };
    const onAnchorClick = (event: MouseEvent) => {
      const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href^="#"]') : null;
      if (link) skipAnchorEntrance();
    };
    const onPreferenceChange = () => {
      if (!preference.matches) return;
      observer.disconnect();
      cancelAll();
    };
    root.addEventListener("focusin", revealInteracted);
    root.addEventListener("pointerdown", revealInteracted, { passive: true });
    document.addEventListener("click", onAnchorClick, true);
    window.addEventListener("hashchange", skipAnchorEntrance);
    preference.addEventListener("change", onPreferenceChange);
    return () => {
      observer.disconnect();
      cancelAll();
      root.removeEventListener("focusin", revealInteracted);
      root.removeEventListener("pointerdown", revealInteracted);
      document.removeEventListener("click", onAnchorClick, true);
      window.removeEventListener("hashchange", skipAnchorEntrance);
      preference.removeEventListener("change", onPreferenceChange);
    };
  }, [mainRef]);
}

const typographyRhythmStyles = `
/*
 * Campaign-only type and spacing pass.
 * Append after the campaign/component styles, or emit as the final style in .bf-page.
 * Selectors name reading copy explicitly: workshop, SVG, phone, artist-image labels,
 * decorative membership/call artwork and large prices keep their own typography.
 */
.bf-page {
  --bf-type-display: clamp(2.75rem, 4.15vw, 3.75rem);
  --bf-type-section: clamp(2.25rem, 3.2vw, 3rem);
  --bf-type-chapter: clamp(1.875rem, 2.7vw, 2.5rem);
  --bf-type-subhead: 1.5rem;
  --bf-type-body: 1.0625rem;
  --bf-type-lead: 1.125rem;
  --bf-type-compact: .9375rem;
  --bf-type-note: .875rem;
  --bf-type-meta: .8125rem;
  --bf-space-paragraph: 16px;
  --bf-space-intro: 24px;
  --bf-space-content: 36px;
  --bf-space-section: 72px;
  font-family: Geist, Inter, system-ui, sans-serif;
}

/* Display: retain the established three-line title, without enlarging the hero. */
.bf-page .bf-hero-title {
  font-size: var(--bf-type-display);
  font-weight: 650;
  line-height: 1.06;
  letter-spacing: -.035em;
}
.bf-page .bf-hero {
  padding-top: 40px;
}
.bf-page .bf-hero .bf-audio-badge {
  margin-bottom: 16px;
}
.bf-page .bf-hero-copy {
  font-size: 17px;
  font-weight: 400;
  line-height: 1.6;
  letter-spacing: 0;
  max-width: 32rem;
  margin-top: 20px !important;
}
.bf-page .bf-hero-action {
  margin-top: 24px;
}
.bf-page .bf-proof-strip {
  margin-top: 32px;
}

/* One section-heading scale. H3s inside product demonstrations are not selected. */
.bf-page .bf-recognition-offer .bf-heading,
.bf-page .bf-offer-v3 .bf-content-intro > h2,
.bf-page .bf-comparison .bf-center-heading > h2,
.bf-page .bf-stories .bf-center-heading > h2,
.bf-page .bf-messages-v4 .bf-messages-heading > .bf-heading,
.bf-page .bf-purchase-copy > .bf-heading,
.bf-page .bf-faq-intro > .bf-heading {
  font-size: var(--bf-type-section);
  font-weight: 600;
  line-height: 1.18;
  letter-spacing: -.03em;
}
.bf-page .bf-offer-v3 .bf-working-copy > h3,
.bf-page .bf-offer-v3 .bf-amplify-stage .bf-wrap > div > h3,
.bf-page .bf-purchase-bonuses > .bf-heading {
  font-size: var(--bf-type-chapter);
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -.025em;
}
.bf-page .bf-working-copy > h4,
.bf-page .bf-fit-column > h3,
.bf-page .bf-purchase-card > h3,
.bf-page .bf-bonus-grid article > h4 {
  font-size: var(--bf-type-subhead);
  font-weight: 550;
  line-height: 1.28;
  letter-spacing: -.02em;
}
.bf-page .bf-start-note > h3 {
  font-size: 20px;
  font-weight: 550;
  line-height: 1.3;
  letter-spacing: -.015em;
}

/* Reading copy: 17px desktop, 16px mobile, with natural Geist spacing. */
.bf-page .bf-recognition-body,
.bf-page .bf-offer-v3 .bf-content-intro > p,
.bf-page .bf-offer-v3 .bf-working-copy > p,
.bf-page .bf-offer-v3 .bf-amplify-stage .bf-wrap > div > p,
.bf-page .bf-center-heading > p,
.bf-page .bf-messages-v4 .bf-messages-heading > p,
.bf-page .bf-purchase-copy > p:not(.bf-offer-help),
.bf-page .bf-faq-intro > p,
.bf-page .bf-bonus-grid article > p:not(.bf-bonus-eligibility):not(.bf-bonus-call-intro) {
  font-size: var(--bf-type-body);
  font-weight: 400;
  line-height: 1.7;
  letter-spacing: 0;
}
.bf-page .bf-recognition-lead {
  font-size: var(--bf-type-lead);
  font-weight: 400;
  line-height: 1.65;
  letter-spacing: -.01em;
  max-width: 58ch;
  margin-top: var(--bf-space-intro) !important;
}
.bf-page .bf-recognition-body {
  max-width: 58ch;
  margin-top: var(--bf-space-paragraph) !important;
}
.bf-page .bf-offer-v3 .bf-content-intro > p,
.bf-page .bf-offer-v3 .bf-working-copy > p,
.bf-page .bf-offer-v3 .bf-amplify-stage .bf-wrap > div > p,
.bf-page .bf-purchase-copy > p:not(.bf-offer-help) {
  max-width: 58ch;
}
.bf-page .bf-center-heading > p,
.bf-page .bf-faq-intro > p {
  max-width: 36rem;
  margin: var(--bf-space-intro) auto 0;
}
.bf-page .bf-messages-v4 .bf-messages-heading > p {
  max-width: 32rem;
  margin-top: var(--bf-space-intro);
}
.bf-page .bf-content-intro > h2 + p,
.bf-page .bf-working-copy > h3 + p,
.bf-page .bf-amplify-stage .bf-wrap > div > h3 + p,
.bf-page .bf-purchase-copy > .bf-heading + p {
  margin-top: var(--bf-space-intro);
}
.bf-page .bf-content-intro > p + p {
  margin-top: var(--bf-space-paragraph);
}
.bf-page .bf-working-copy > h4 {
  margin-top: 28px;
  margin-bottom: 12px;
}
.bf-page .bf-working-copy > h4 + p {
  margin-top: 0;
}
.bf-page .bf-bonuses-intro {
  font-size: var(--bf-type-body);
  line-height: 1.7;
  margin-top: var(--bf-space-paragraph) !important;
}
.bf-page .bf-bonus-grid .bf-bonus-call-intro {
  font-size: var(--bf-type-lead) !important;
  line-height: 1.5;
  margin: 24px 0 16px !important;
}
.bf-page .bf-included li,
.bf-page .bf-fit li,
.bf-page .bf-start-note p,
.bf-page .bf-after-purchase {
  font-size: var(--bf-type-compact);
  line-height: 1.65;
  letter-spacing: 0;
}

/* Notes that affect the buying decision should not shrink to 10–12px. */
.bf-page .bf-hero-price,
.bf-page .bf-hero-bonus,
.bf-page .bf-bonus-grid .bf-bonus-eligibility,
.bf-page .bf-bonus-grid .bf-bonus-confirmation,
.bf-page .bf-price-label,
.bf-page .bf-payment-note,
.bf-page .bf-purchase .bf-adspend {
  font-size: var(--bf-type-note);
  line-height: 1.6;
  letter-spacing: 0;
}
.bf-page .bf-purchase-card .bf-recommendation,
.bf-page .bf-offer-help {
  font-size: var(--bf-type-note) !important;
  line-height: 1.65;
}
.bf-page .bf-preview-note,
.bf-page .bf-artist-results-note,
.bf-page .bf-fit-note {
  font-size: var(--bf-type-meta);
  line-height: 1.6;
}
.bf-page .bf-bonus-grid .bf-bonus-eligibility {
  margin-bottom: 16px !important;
}
.bf-page .bf-messages-v4 .bf-more-messages,
.bf-page .bf-stories-more .bf-text-link {
  font-size: var(--bf-type-compact);
  line-height: 1.5;
}
.bf-page .bf-cta,
.bf-page .bf-checkout {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 0;
}

/* FAQ and closing copy use the same reading scale, rather than a miniature scale. */
.bf-page .bf-faq-trigger {
  font-size: var(--bf-type-body) !important;
  font-weight: 500 !important;
  line-height: 1.45 !important;
  padding-top: 22px !important;
  padding-bottom: 22px !important;
}
.bf-page .bf-faq-answer {
  font-size: var(--bf-type-body) !important;
  line-height: 1.7 !important;
  padding-bottom: 24px !important;
}
.bf-page .bf-close h2 {
  font-size: 32px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -.025em;
}
.bf-page .bf-close p {
  font-size: 16px;
  line-height: 1.65;
  max-width: 38rem;
  margin-top: 16px;
}
.bf-page .bf-footer {
  font-size: var(--bf-type-meta);
  line-height: 1.6;
}

/* Each content chapter owns its breathing room. Empty old ribbon spacers do not. */
.bf-page .bf-ribbon-turn {
  height: 0;
  min-height: 0;
  padding: 0;
}
.bf-page .bf-recognition.bf-recognition-offer,
.bf-page .bf-offer-v3 .bf-content-stage,
.bf-page .bf-offer-v3 .bf-working-stage,
.bf-page .bf-offer-v3 .bf-amplify-stage,
.bf-page .bf-comparison,
.bf-page .bf-stories,
.bf-page .bf-messages.bf-messages-v4,
.bf-page .bf-purchase,
.bf-page .bf-faq {
  padding-top: var(--bf-space-section);
  padding-bottom: var(--bf-space-section);
}
.bf-page .bf-comparison .bf-compare-table,
.bf-page .bf-stories .bf-artist-features,
.bf-page .bf-faq .bf-faq-list,
.bf-page .bf-purchase-bonuses .bf-bonus-grid {
  margin-top: var(--bf-space-content);
}
.bf-page .bf-messages-v4 .bf-messages-heading {
  margin-bottom: 28px;
}
.bf-page .bf-fit {
  margin-top: var(--bf-space-content);
  padding-top: 0 !important;
}
.bf-page .bf-purchase-bonuses {
  padding-top: 56px;
}
.bf-page .bf-included {
  margin-top: 24px;
}
.bf-page .bf-start-note {
  margin-top: 28px;
  padding-top: 24px;
}
.bf-page .bf-recognition-offer .bf-recognition-promise,
.bf-page .bf-recognition-offer .bf-kickstart-journey + .bf-recognition-promise {
  font-size: var(--bf-type-body);
  line-height: 1.65;
  max-width: 58ch;
  margin-top: 24px !important;
  padding-top: 24px;
}

/* The tablet messages introduction sits beside its heading, so it needs no top gap. */
@media (min-width: 761px) and (max-width: 1099px) {
  .bf-page .bf-messages-v4 .bf-messages-heading > p {
    margin-top: 0;
  }
  .bf-page .bf-messages-v4 .bf-messages-heading {
    margin-bottom: 0;
  }
}

@media (max-width: 760px) {
  .bf-page {
    --bf-type-display: clamp(2.25rem, 10vw, 3rem);
    --bf-type-section: clamp(2rem, 8.5vw, 2.25rem);
    --bf-type-chapter: clamp(1.875rem, 8vw, 2.125rem);
    --bf-type-subhead: 1.375rem;
    --bf-type-body: 1rem;
    --bf-type-lead: 1.0625rem;
    --bf-space-intro: 20px;
    --bf-space-content: 28px;
    --bf-space-section: 48px;
  }
  .bf-page .bf-hero {
    padding-top: 28px;
  }
  .bf-page .bf-hero-title {
    line-height: 1.08;
  }
  .bf-page .bf-hero-copy {
    font-size: 16px;
    line-height: 1.6;
    max-width: 32rem;
    margin-top: 18px !important;
  }
  .bf-page .bf-hero-action {
    margin-top: 24px;
  }
  .bf-page .bf-proof-strip {
    margin-top: 28px;
  }
  .bf-page .bf-center-heading > p,
  .bf-page .bf-faq-intro > p {
    margin-left: 0;
    margin-right: 0;
  }
  .bf-page .bf-messages-v4 .bf-messages-heading {
    margin-bottom: 24px;
  }
  .bf-page .bf-working-copy > h4 {
    margin-top: 24px;
    margin-bottom: 12px;
  }
  .bf-page .bf-purchase-bonuses {
    padding-top: 40px;
  }
  .bf-page .bf-start-note {
    margin-top: 24px;
    padding-top: 20px;
  }
  .bf-page .bf-close h2 {
    font-size: 30px;
  }
}

`;

const centeredHeroStyles = `
.bf-academy-trust{display:flex;align-items:center;justify-content:center;gap:12px;margin:17px auto 0;color:#9ca7b6}
.bf-trust-portraits{display:flex;flex-shrink:0;padding-left:7px}
.bf-trust-portraits img{display:block;width:28px;height:28px;object-fit:cover;border:1.5px solid #9babc1;border-radius:50%;margin-left:-7px;background:#242a33;box-shadow:0 2px 7px #0003}
.bf-academy-trust p{font-size:12px;line-height:1.45;letter-spacing:0}
.bf-academy-trust strong{font-weight:500;color:#c6cedb}
.bf-industry-proof{max-width:790px;margin:0 auto;padding:11px 0 30px;text-align:center}
.bf-industry-proof>p{font-size:12px;line-height:1.5;color:#8c97a8}
.bf-industry-proof ul{list-style:none;padding:0;margin:16px 0 0;display:grid;grid-template-columns:repeat(5,1fr);gap:30px;align-items:center}
.bf-industry-proof li{height:54px;display:flex;align-items:center;justify-content:center;opacity:.45;color:#e8eef8;transition:opacity .35s}
.bf-industry-proof li:hover{opacity:.75}
.bf-industry-proof img{display:block;max-width:100%;object-fit:contain}
.bf-industry-warner svg{width:58px;height:43px}
.bf-industry-columbia img{width:68px;height:53px}
.bf-industry-cinq img{width:68px;height:53px;filter:brightness(2);mix-blend-mode:lighten}
.bf-industry-tidal img{width:80px;height:54px;filter:url(#bf-tidal-knockout)}
.bf-industry-spotify img{width:43px;height:43px}
@media(max-width:760px){
  .bf-academy-trust{gap:10px;margin-top:15px}
  .bf-academy-trust p{text-align:left;font-size:11px}
  .bf-academy-trust strong{display:block}
  .bf-trust-portraits img{width:26px;height:26px}
  .bf-industry-proof{padding:0 0 26px;max-width:490px}
  .bf-industry-proof>p{max-width:270px;margin:0 auto;font-size:11px;line-height:1.5}
  .bf-industry-proof ul{gap:15px;margin-top:15px}
  .bf-industry-proof li{height:40px}
  .bf-industry-warner svg{width:42px;height:32px}
  .bf-industry-columbia img,.bf-industry-cinq img{width:47px;height:39px}
  .bf-industry-tidal img{width:55px;height:42px}
  .bf-industry-spotify img{width:31px;height:31px}
}

.bf-page .bf-hero-centered{padding:28px 0 0}
.bf-hero-centered .bf-hero-pitch{width:100%;max-width:980px;margin:0 auto;text-align:center}
.bf-page .bf-hero-centered .bf-audio-badge{margin:0 auto 18px}
.bf-page .bf-hero-centered .bf-hero-title{font-size:clamp(48px,4.8vw,68px);line-height:1.12;letter-spacing:-.04em;text-wrap:balance}
.bf-hero-centered .bf-title-first{white-space:nowrap;margin-bottom:6px}
.bf-hero-centered .bf-title-first>span{display:inline-block}
.bf-page .bf-hero-centered .bf-title-result{margin:0 0 0 6px}
.bf-hero-centered .bf-hero-title .bf-highlight{padding:2px 13px 5px;border-radius:9px;box-shadow:0 0 54px #0071e32e}
.bf-page .bf-hero-centered .bf-hero-copy{max-width:650px;margin:20px auto 0!important;font-size:17px;line-height:1.6;color:#bfc5d0;text-wrap:pretty}
.bf-page .bf-hero-centered .bf-hero-action{margin-top:21px}
.bf-hero-centered .bf-hero-action .bf-cta{min-width:250px;gap:25px;padding:14px 21px;box-shadow:0 1px 0 #70b8ff6b inset,0 8px 28px #0068db24}
.bf-page .bf-hero-centered .bf-hero-price{margin-top:11px!important;font-size:13px}
.bf-page .bf-hero-centered .bf-proof-strip{max-width:650px;margin:24px auto 0;padding:20px 0 24px;grid-template-columns:1fr 1fr;gap:30px;border-color:#b7c9e326}
.bf-hero-centered .bf-proof-strip>p{font-size:12px;max-width:70px;line-height:1.5;color:#919dad}
.bf-hero-centered .bf-proof-tile{gap:12px;align-items:center;border-radius:5px;transition:background .25s}
.bf-hero-centered .bf-proof-tile+.bf-proof-tile{padding-left:23px;border-color:#b7c9e326}
.bf-hero-centered .bf-proof-face{width:49px;height:57px;border-radius:6px;border:1px solid #d6e5fa20}
.bf-page .bf-hero-centered .bf-proof-name{font-size:12px}
.bf-page .bf-hero-centered .bf-proof-result{font-size:16px;line-height:1.35}
.bf-hero-centered .bf-proof-watch{font-size:11px;color:#9bb9dc;margin-top:4px}
.bf-hero-centered .bf-proof-tile:hover .bf-proof-watch{color:#d9ebff}
@media(min-width:1100px) and (max-height:800px){
  .bf-page .bf-campaign-banner .bf-banner-inner{min-height:50px;padding:7px 0}
  .bf-page .bf-nav{height:64px}
  .bf-page .bf-hero-centered{padding-top:22px}
  .bf-page .bf-hero-centered .bf-audio-badge{margin-bottom:13px}
  .bf-page .bf-hero-centered .bf-hero-title{font-size:58px}
  .bf-page .bf-hero-centered .bf-hero-copy{margin-top:15px!important;font-size:16px;line-height:1.55;max-width:680px}
  .bf-page .bf-hero-centered .bf-hero-action{margin-top:17px}
  .bf-page .bf-hero-centered .bf-proof-strip{margin-top:22px;padding-top:16px;padding-bottom:19px}
}
@media(min-width:761px) and (max-width:1050px){
  .bf-page .bf-hero-centered .bf-hero-title{font-size:clamp(44px,5.6vw,58px)}
  .bf-page .bf-hero-centered .bf-proof-strip{gap:18px}
  .bf-hero-centered .bf-proof-tile+.bf-proof-tile{padding-left:18px}
  .bf-page .bf-hero-centered .bf-proof-result{font-size:14px}
}
@media(max-width:760px){
  .bf-page .bf-hero-centered{padding-top:25px}
  .bf-hero-centered .bf-hero-pitch{max-width:490px}
  .bf-page .bf-hero-centered .bf-audio-badge{margin-bottom:20px}
  .bf-page .bf-hero-centered .bf-hero-title{font-size:clamp(35px,10vw,56px);line-height:1.13;letter-spacing:-.04em}
  .bf-hero-centered .bf-title-first{white-space:normal;margin-bottom:5px}
  .bf-hero-centered .bf-title-first>span{display:block}
  .bf-page .bf-hero-centered .bf-title-result{margin:7px 0 10px}
  .bf-hero-centered .bf-hero-title .bf-highlight{padding:2px 11px 5px;border-radius:7px}
  .bf-page .bf-hero-centered .bf-hero-copy{max-width:420px;margin-top:20px!important;font-size:15px;line-height:1.65}
  .bf-page .bf-hero-centered .bf-hero-action{margin-top:22px}
  .bf-hero-centered .bf-hero-action .bf-cta{width:100%;min-width:0;justify-content:center;padding:15px 18px}
  .bf-page .bf-hero-centered .bf-hero-price{font-size:12px;line-height:1.6}
  .bf-page .bf-hero-centered .bf-proof-strip{grid-template-columns:1fr 1fr;align-items:start;gap:14px 12px;max-width:490px;margin-top:26px;padding:16px 0 24px}
  .bf-hero-centered .bf-proof-strip>p{grid-column:1/-1;text-align:center;max-width:none}
  .bf-hero-centered .bf-proof-tile{gap:9px;align-items:flex-start}
  .bf-hero-centered .bf-proof-tile+.bf-proof-tile{padding-left:12px}
  .bf-hero-centered .bf-proof-face{width:35px;height:43px;border-radius:5px}
  .bf-page .bf-hero-centered .bf-proof-name{font-size:11px}
  .bf-page .bf-hero-centered .bf-proof-result{font-size:13px;line-height:1.4}
  .bf-hero-centered .bf-proof-watch{font-size:10px;gap:5px;margin-top:5px}
}
@media(max-width:360px){
  .bf-hero-centered .bf-proof-face{width:28px;height:37px}
  .bf-hero-centered .bf-proof-tile{gap:7px}
  .bf-page .bf-hero-centered .bf-proof-result{font-size:12px}
}
`;

export default function BlackFriday() {
  const mainRef = useRef<HTMLElement | null>(null);
  useSurfaceLight(mainRef);
  usePageEntrances(mainRef);
  const [playing, setPlaying] = useState<string | null>(null);
  const lastVideoTrigger = useRef<HTMLButtonElement | null>(null);
  const current = videoTestimonials.find((artist) => artist.id === playing);
  const videoId = current?.videoUrl.match(/vimeo\.com\/(\d+)/)?.[1];
  const openStory: OpenStory = (id, trigger) => {
    lastVideoTrigger.current = trigger;
    setPlaying(id);
  };
  return (
    <>
      <div className="bf-page">
        <style>{campaignStyles}</style>
        <Helmet>
          <title>{CAMPAIGN_NAME} | Black Friday | DK Artists</title>
          <meta
            name="description"
            content="Build an audience around your music with personal content direction and advertising managed by DK Artists. Black Friday Artist Kickstart: 30, 60 or 90 days."
          />
          {BLACK_FRIDAY_NOINDEX && <meta name="robots" content="noindex, nofollow" />}
        </Helmet>
        <aside className="bf-occasion bf-campaign-banner" aria-label="Black Friday offer">
          <div className="bf-banner-surface" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div className="bf-wrap bf-banner-inner">
            <div className="bf-banner-lockup">
              Black Friday
              <span className="bf-banner-cut" aria-hidden="true" />
            </div>
            <p className="bf-banner-description">
              The Artist Kickstart<span>30, 60 or 90 days with your team.</span>
            </p>
            <a href="#kickstart" className="bf-banner-link">
              <span>View offer</span>
              <span className="bf-banner-arrow">
                <ArrowUpRight aria-hidden="true" />
              </span>
            </a>
          </div>
        </aside>
        <header className="bf-header">
          <div className="bf-wrap bf-nav">
            <Link to="/" aria-label="DK Artists home">
              <img src={logo} alt="DK Artists" width={1000} height={200} className="bf-logo" />
            </Link>
            <nav aria-label="Campaign" className="bf-nav-links">
              <a href="#bf-support">How it works</a>
              <a href="#bf-artists">Artist stories</a>
              <a href="#kickstart" onClick={scrollToKickstart} className="bf-nav-buy">
                Choose your kickstart
              </a>
            </nav>
          </div>
        </header>
        <main ref={mainRef} className="bf-main">
          <EnergyRibbon mainRef={mainRef} />
          <section
            className="bf-section bf-dark bf-hero bf-hero-centered"
            data-energy-tone="dark"
            aria-labelledby="bf-title"
          >
            <div className="bf-wrap">
              <div className="bf-hero-pitch">
                <div className="bf-audio-badge">
                  <div aria-hidden="true">
                    <AudioWaveform />
                  </div>
                  <div className="bf-occasion-badge">BLACK FRIDAY</div>
                </div>
                <h1 id="bf-title" className="bf-hero-title" aria-label={CAMPAIGN_NAME}>
                  <span className="bf-title-first">
                    <span>The Zero To</span>{" "}
                    <span className="bf-title-result">
                      <Highlight>1,000 Fans</Highlight>
                    </span>
                  </span>{" "}
                  <span>Artist Kickstart</span>
                </h1>
                <p className="bf-hero-copy">
                  Give your music a proper push, with us in your corner for 30, 60 or 90 days. We help you find what
                  makes potential fans care and turn it into content, with our team running the advertising to reach
                  them.
                </p>
                <div className="bf-hero-action">
                  <KickstartCTA id="bf-hero-cta" />
                  <p className="bf-hero-price">
                    Black Friday prices from <strong>{formatUsd(LOWEST_PRICE)} USD</strong>. Pay once.
                  </p>
                  <AcademyTrust />
                </div>
              </div>
              <ArtistProofStrip onPlay={openStory} />
              <IndustryProof />
            </div>
          </section>
          <Recognition />
          <RibbonTurn />
          <Journey />
          <ComparisonAndFit />
          <RibbonTurn />
          <ArtistStories onPlay={openStory} />
          <Messages />
          <WhyNow />
          <RibbonTurn />
          <PurchasePanel />
          <section className="bf-section bf-faq" data-energy-tone="light" aria-labelledby="faq-title">
            <div className="bf-wrap bf-faq-layout">
              <div className="bf-faq-intro">
                <h2 id="faq-title" className="bf-heading">
                  A few <Highlight>questions</Highlight> before you choose.
                </h2>
                <p>What to expect from the support, your part in the work and getting started.</p>
              </div>
              <Accordion type="single" collapsible className="bf-faq-list">
                {FAQS.map((faq, index) => (
                  <AccordionItem key={faq.q} value={`faq-${index}`} className="bf-faq-item">
                    <AccordionTrigger className="bf-faq-trigger">{faq.q}</AccordionTrigger>
                    <AccordionContent className="bf-faq-answer">{faq.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
          <section id="bf-close" className="bf-section bf-close" data-energy-tone="light">
            <div className="bf-wrap">
              <div>
                <h2>
                  Let’s get your <Highlight>music moving.</Highlight>
                </h2>
                <p>
                  Let’s get to work on the music you have now. Bring your ideas to the team as you make the content, and
                  build the momentum you want to carry into your next release.
                </p>
              </div>
              <KickstartCTA />
            </div>
          </section>
        </main>
        <Dialog open={!!playing} onOpenChange={(open) => !open && setPlaying(null)}>
          <DialogContent
            aria-describedby={undefined}
            className="bf-media-dialog"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              lastVideoTrigger.current?.focus({ preventScroll: true });
            }}
          >
            <DialogTitle className="sr-only">{current?.artistName}'s story</DialogTitle>
            {videoId && (
              <div className="bf-video-frame">
                <iframe
                  src={`https://player.vimeo.com/video/${videoId}?autoplay=1&badge=0&autopause=0`}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={`${current?.artistName}'s story`}
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
        <StickyMobileKickstart />
        <DirectContact />
        <style>{surfaceRefinementStyles}</style>
        <style>{typographyRhythmStyles}</style>
        <style>{centeredHeroStyles}</style>
      </div>
      <div id="bf-footer">
        <Footer />
      </div>
    </>
  );
}
